import * as THREE from "three";
import { applyLessonFraming } from "./LessonFraming.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { FBXLoader } from "three/addons/loaders/FBXLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { GUI } from "three/addons/libs/lil-gui.module.min.js";
// The custom sky dome provides the visual backdrop (no HDRI background).
// HDRLoader is used ONLY by the opt-in "Studio" lighting mode (Task #552),
// which lazy-loads the Tripo studio HDRI into scene.environment. The .hdr
// file itself is never fetched until Studio is first activated.
// import { EXRLoader } from "three/addons/loaders/EXRLoader.js";
import { HDRLoader } from "three/addons/loaders/HDRLoader.js";
import { Reflector } from "three/addons/objects/Reflector.js";

import { RangeBasedFBXLoader } from "./RangeBasedFBXLoader.js";
import { RangeBasedGLTFLoader } from "./RangeBasedGLTFLoader.js";
import { setRuntimeAnimationOverride } from "./AnimationConfig.js";
import {
    AdaptiveResolutionGovernor,
    createFpsOverlay,
    isFpsOverlayRequested,
} from "./AdaptiveResolution.js";
import { HairShaderMaterial } from "../HairShaderMaterial.js";
import { PBRMaterialManager } from "./PBRMaterialManager.js";
import { AnimationController } from "./AnimationController.js";
import { LipSyncController } from "./LipSyncController.js";
import { BlinkController } from "./BlinkController.js";
import { EyeTrackingController } from "./EyeTrackingController.js";
import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";
import { IdleSmileController } from "./IdleSmileController.js";
import { characterData } from "./characterData.js";
import { GreetingService } from "./GreetingService.js";
import { ParticleSystem } from "./ParticleSystem.js";
import { ModelManager } from "./ModelManager.js";
import { CameraAnimator } from "./CameraAnimator.js";
import { ImagePanel3D } from "./ImagePanel3D.js";
import { SimpleImagePanel } from "./SimpleImagePanel.js";
import { computeArtworkAnchor } from "./ArtworkPanelPlacement.js";
import { LessonReactionController } from "./LessonReactionController.js";
import {
    calculateOptimalCameraPosition,
} from "./ModelPlatformConfig.js";
import { SCENE_FRAMING } from "./SceneFraming.js";
import {
    detectEyeAnchors,
    detectExplicitEyeAnchor,
    fallbackAnchorsFromHead,
    computeFramingFromAnchors,
} from "./EyeFraming.js";
import {
    computePose,
    detectEyeHeight,
    getPoseConfig,
    isManuallyLocked,
    saveOverride,
    CHARACTER_POSES,
    DEFAULT_EYE_HEIGHT,
    ANTHROPOMETRIC_EYE_RATIO,
} from "./CharacterPoseConfig.js";
import { PlatformHeightController } from "./PlatformHeightController.js";
import { PlatformTestInterface } from "./PlatformTestInterface.js";
import { TeethMaterialManager } from "./TeethMaterialManager.js";
import { MouthMaterialManager } from "./MouthMaterialManager.js";
import { enforceTeethDuctBlack } from "./DuctMaterialGuards.js";
import {
    shouldUseNativeMaterials,
    logNativeMaterialInfo,
    downgradePhysicalMaterials,
    physicalMaterialsRisky,
    rememberPhysicalMaterialsBroken,
    physicalProgramFailureDetected,
    isDesktopChromiumUA,
    physicalPreflightFailed,
} from "./NativeMaterialConfig.js";

// ---- Studio lighting mode (Task #552) -------------------------------------
// Second lighting look reproducing the Tripo3D viewer: pure HDRI image-based
// lighting (zero contributing analytic lights), ACES filmic tone mapping
// @ exposure 1.0. The module itself always CONSTRUCTS the classic 6-light rig
// and boots in classic; /lab's inline settings script applies Studio as the
// page default (founder ruling 2026-09-01) unless the user saved "classic" —
// every other ModelViewer surface (learn stage, /testcharacter, embeds,
// share-card) stays classic. The rig is only HIDDEN while Studio is active —
// never removed. Spec (measured off the live Tripo viewer):
// attached_assets/Pasted--Tripo3D-Viewer-Lighting-
// Implementation-Spec-for-a-thre_1788263604539.txt
const STUDIO_HDRI_URL = "/static/hdri/studio.hdr";

// Shared platform prop cache (Task #552 follow-on, post-100% gap): the
// marble platform FBX is identical for every character, so it is fetched,
// parsed, and material-prepared exactly once per page. applyPose commits
// the same prepared object on every pose; a failed load is never cached.
let sharedPlatformObject = null;
let sharedPlatformPromise = null;
// Tripo's HDRI is authored for a model facing +X (key softbox at azimuth
// +36°, elevation +24°). Our characters face +Z (the boot camera sits on +Z
// at (0,180,95) looking back at the origin), so swing the environment by
// -90° around Y — exactly the spec's orientation-note example — to land the
// key ~36° off the face, from above, with the warm rim on the opposite
// shoulder. Tune THIS constant only; never rotate the model.
const STUDIO_ENV_ROTATION_Y_DEG = -90;

export class ModelViewer {
    constructor(canvas) {
        if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
            throw new Error("Valid canvas element is required");
        }

        // Initialize ModelManager first to ensure it's available throughout
        this.modelManager = new ModelManager();
        this.canvas = canvas;
        this.scene = new THREE.Scene();

        // Detect mobile devices early
        this.isMobile = window.innerWidth <= 768 || "ontouchstart" in window;

        // Camera: shorter far plane on mobile to reduce depth + GPU load
        this.camera = new THREE.PerspectiveCamera(
            50,
            window.innerWidth / window.innerHeight,
            0.1,
            this.isMobile ? 1600 : 2000,
        );

        // Initialize clock early - needed for animation loop
        this.clock = new THREE.Clock();
        this.webGLAvailable = true; // Flag to track WebGL availability

        // Opt-in (Task #251): size the renderer + camera to the CANVAS element's
        // own box instead of the full window. Only the /learn resident corner
        // sets this (ResidentLegend); /lab never does, so the Lab is untouched.
        this.useCanvasSize = false;

        // Opt-in (Task #284): frame the WHOLE body (head to feet) instead of
        // the eye-anchored portrait crop. Only the /learn resident corner sets
        // this (ResidentLegend); /lab never does, so the Lab is untouched.
        this.fullBodyFraming = false;

        // Frame throttling variables
        this.lastFrameTime = 0;
        // Slightly lower FPS target for mobile for stability
        this.targetMobileDelta = 1000 / 24; // ~24 FPS on mobile
        // Desktop cap at 60 FPS: high-refresh (120/144 Hz) monitors otherwise
        // push 2-2.4x the frames for no visual gain. 60 keeps A2F mouth/face
        // blendshape updates (anchored to audio time) perfectly smooth.
        this.targetDesktopDelta = 1000 / 60;

        // Task #274 (user-approved): MOBILE-ONLY dynamic resolution.
        // Every character starts at FULL quality; the governor watches the
        // real interval between rendered frames and only steps the pixel
        // ratio down while the phone demonstrably can't keep up (median
        // over a rolling window — spikes are ignored), then climbs back.
        // Desktop NEVER instantiates this — always full quality.
        this.adaptiveResGovernor = this.isMobile
            ? new AdaptiveResolutionGovernor()
            : null;
        // On-device debug readout, only with ?fps=1 in the URL.
        this._fpsOverlayEl = isFpsOverlayRequested() ? createFpsOverlay() : null;
        this._fpsFrameCount = 0;
        this._fpsWindowStart = 0;

        try {
            this.renderer = new THREE.WebGLRenderer({
                canvas: this.canvas,
                alpha: true,
                antialias: true, // disable MSAA on mobile: big GPU win
                powerPreference: this.isMobile
                    ? "low-power"
                    : "high-performance",
            });

            // Centralized resolution / pixel ratio setup
            this.configureRendererSize();
        } catch (rendererError) {
            console.warn(
                "WebGL renderer creation failed - running in limited mode:",
                rendererError,
            );
            // Create a fallback message on the canvas
            const ctx = this.canvas.getContext("2d");
            if (ctx) {
                ctx.fillStyle = "#1a1a1a";
                ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
                ctx.fillStyle = "#666";
                ctx.font = "18px Arial";
                ctx.textAlign = "center";
                ctx.fillText(
                    "3D rendering unavailable in this environment",
                    this.canvas.width / 2,
                    this.canvas.height / 2,
                );
            }
            this.webGLAvailable = false; // Mark WebGL as unavailable
            return; // Exit initialization early but don't throw error
        }

        // Handle WebGL context loss on mobile (prevents infinite reload loops)
        this.canvas.addEventListener(
            "webglcontextlost",
            (event) => {
                event.preventDefault();
                console.warn(
                    "WebGL context lost - disabling 3D on this session.",
                );
                this.webGLAvailable = false;
                if (this.animationFrameId) {
                    cancelAnimationFrame(this.animationFrameId);
                }
            },
            false,
        );

        this.canvas.addEventListener(
            "webglcontextrestored",
            () => {
                console.warn(
                    "WebGL context restored - NOT reloading model automatically on mobile.",
                );
                // You *can* implement a safe manual reload here later if needed.
            },
            false,
        );

        // Skybox configuration.
        // The warm CSS atmosphere (templates/index.html .scene/.haze/.vignette)
        // is the backdrop on EVERY device now, shown through the transparent
        // (alpha:true) WebGL canvas — the 3D sky dome is never added to the
        // scene. This flag must stay TRUE everywhere so setBackgroundColors()
        // early-returns and never paints a gray gradient onto scene.background.
        // Previously `!this.isMobile` left it false on mobile, so the color
        // animation painted an opaque gray gradient ~3s after load that covered
        // the CSS backdrop (the "gray background on mobile" bug). Desktop was
        // unaffected because the flag was already true there.
        this.usingSkybox = true;

        // Background colors and animation (still keep for UI and fallback)
        this.backgroundColors = {
            top: new THREE.Color(0x2a2a2a),
            bottom: new THREE.Color(0x383838),
        };

        this.colorPresets = [
            { top: 0x2a2a2a, bottom: 0x1a1a1a }, // Deep graphite to black
            { top: 0x3c3c3c, bottom: 0x2a2a2a }, // Iron gray to graphite
            { top: 0x4a4a4a, bottom: 0x333333 }, // Dark steel to deep gray
            { top: 0x5a5a5a, bottom: 0x2f2f2f }, // Metallic dark gray to charcoal
            { top: 0x6d6d6d, bottom: 0x383838 }, // Brushed metal gray to dark stone
        ];
        this.currentPresetIndex = 0;
        this.isTransitioning = false;
        this.transitionDuration = 5.0; // Duration for each color transition
        this.pauseDuration = 3.0; // Time to pause between transitions

        this.mixer = null;
        this.model = null;
        this.platform = null;
        this.groundPlane = null;
        this.skyDome = null;
        this.animationController = null;
        this.lipSyncController = null;
        this.blinkController = null;
        this.eyeTrackingController = null;
        this.idleSmileController = null;
        this.greetingService = new GreetingService();

        // Set the ModelViewer reference in the greeting service
        if (this.greetingService && this.greetingService.setModelViewer) {
            this.greetingService.setModelViewer(this);
            console.log(
                "Connected ModelViewer to greeting service for model ID access",
            );
        }

        this.materialManager = new PBRMaterialManager();
        this.blendShapeControls = {};
        this.currentModelKey = null;
        this.loadingTimeout = null;
        this.isLoading = false;
        this._materializeLoadToken = null;
        this._selectedModelLoadTail = null;
        this.particleSystem = null;
        this.cameraAnimator = null;
        this.imagePanel = null;
        this.imagePanel3D = null;
        this.platformHeightController = null;
        this.platformTestInterface = null;
        this.teethMaterialManager = null;
        this.mouthMaterialManager = null;

        this.defaultCameraConfig = {
            position: { ...SCENE_FRAMING.cameraPosition },
            target: { ...SCENE_FRAMING.cameraTarget },
            rotation: { x: 0, y: 0, z: 0 },
        };

        // Monotonically increasing token used by applyPose() so that a stale
        // platform FBX callback from a previous character cannot stomp the
        // pose committed for the current one.
        this._poseRequestToken = 0;

        // Lighting mode (Task #552): "classic" = the analytic 6-light rig +
        // per-device tone mapping (default, unchanged); "studio" = Tripo-style
        // HDRI IBL. The saved preference is auto-applied by the /lab page
        // only — this class never reads localStorage for it. Other surfaces
        // keep this baseline unless explicitly opted in by their owner
        // (the /learn lesson player requests Studio through ResidentLegend).
        this._lightingMode = "classic";
        this._studioEnvTexture = null; // decoded HDR (fetched lazily, once)
        this._studioEnvLoading = false; // single-flight guard for the fetch
        this._classicRenderDefaults = null; // captured in init()

        this.loadCameraConfig();

        // Core setup
        this.init();
        this.setupBackground();
        this.setupLighting();
        this.setupPlatform();
        this.setupEvents();
        this.setupCameraControls();
        this.setupModelControls();
        this.setupPlatformControls();
        this.startColorAnimation();
        this.animate();
    }

    /**
     * Centralized renderer sizing + pixel ratio config
     * Keeps mobile sharp but MUCH cheaper than full DPR
     */
    configureRendererSize() {
        const devicePR = window.devicePixelRatio || 1;

        // Clamp the effective pixel ratio. On Retina/4K displays devicePR is
        // 2-4, which means rendering 4-16x the pixels of a 1x screen — the main
        // cause of the stutter. A ceiling of 2 keeps the character crisp while
        // cutting fill cost dramatically on high-DPI screens. (1x screens are
        // unaffected since min() keeps their ratio at 1.)
        // NOTE: the BASE stays at 2 on mobile — USER DIRECTIVE: never trade
        // visual quality statically. What IS allowed (user-approved, Task
        // #274 follow-on) is the adaptive governor below: mobile-only, it
        // eases the effective ratio down ONLY while measured frame times
        // show a sustained slideshow, and restores full quality after.
        const MAX_PIXEL_RATIO = 2;
        const basePixelRatio = Math.min(devicePR, MAX_PIXEL_RATIO);
        const adaptiveScale = this.adaptiveResGovernor
            ? this.adaptiveResGovernor.scale
            : 1;
        // Floor at 1: never render below 1 physical px per CSS px.
        const targetPixelRatio = Math.max(1, basePixelRatio * adaptiveScale);

        this.renderer.setPixelRatio(targetPixelRatio);

        let w = window.innerWidth;
        let h = window.innerHeight;

        if (this.useCanvasSize && this.canvas) {
            // Corner-portrait mode: the canvas is a small CSS box, so render to
            // its own size and match the camera aspect to it (otherwise the
            // full-window aspect would be squashed into the box).
            w = this.canvas.clientWidth || this.canvas.width || w;
            h = this.canvas.clientHeight || this.canvas.height || h;
            if (this.camera) {
                this.camera.aspect = w / h;
                this.camera.updateProjectionMatrix();
            }
        } else if (this.isMobile) {
            // Do NOT use full height on mobile to reduce GPU memory usage
            // h = Math.round(window.innerHeight * 0.75); // 75% of screen height
            h = window.innerHeight;
        }

        this.renderer.setSize(w, h, false);
    }

    animate() {
        // Only continue animation loop if WebGL is available
        if (!this.webGLAvailable) {
            return;
        }

        requestAnimationFrame(() => this.animate());

        // Task #331 runtime safety net: on backends where we KEPT physical
        // materials (Safari/iPad etc.), watch the first frames after a model
        // load for failed WebGL programs. If any program failed, downgrade
        // this model live, and remember on this device so future loads
        // downgrade preemptively. No-op (a few cheap array scans) when
        // healthy. Early checkpoints keep the visible glitch sub-second on
        // machines that fail despite passing (or skipping) the preflight.
        if (this._physicalHealthWatch != null) {
            this._physicalHealthWatch++;
            if (
                this._physicalHealthWatch === 5 ||
                this._physicalHealthWatch === 15 ||
                this._physicalHealthWatch === 30 ||
                this._physicalHealthWatch === 180
            ) {
                if (physicalProgramFailureDetected(this.renderer)) {
                    console.error(
                        "[MATERIAL-FALLBACK] WebGL program failure with physical materials — downgrading this model to MeshStandardMaterial and flagging this device",
                    );
                    rememberPhysicalMaterialsBroken();
                    if (this.model) {
                        this.model.traverse((c) => {
                            if (c.isMesh) downgradePhysicalMaterials(c);
                        });
                        // Task #340: downgrade clones are born at their
                        // authored blend state (never invisible); settle
                        // once more as pure enforcement of that guarantee.
                        this._settleAuthoredMaterials(this.model);
                    }
                    this._physicalHealthWatch = null;
                } else if (this._physicalHealthWatch === 180) {
                    this._physicalHealthWatch = null; // healthy — stop watching
                }
            }
        }

        const now = performance.now();

        // Frame-rate cap. Mobile stays at its aggressive ~24 FPS target;
        // desktop is capped at 60 FPS so high-refresh (120/144 Hz) monitors
        // don't waste GPU on frames the eye can't use. The modulo-reset on
        // desktop keeps the cadence locked to a steady 60 FPS grid (instead of
        // dropping to ~40) regardless of the monitor's native refresh rate, so
        // pacing stays smooth — including A2F mouth/face updates during speech.
        if (this.isMobile) {
            const elapsed = now - this.lastFrameTime;
            if (elapsed < this.targetMobileDelta) {
                return; // skip this frame
            }
            this.lastFrameTime = now;
        } else {
            const elapsed = now - this.lastFrameTime;
            if (elapsed < this.targetDesktopDelta) {
                return; // skip this frame
            }
            // Anchor to the 60 FPS grid so we don't accumulate drift and
            // accidentally halve the effective frame rate on 120/144 Hz panels.
            this.lastFrameTime = now - (elapsed % this.targetDesktopDelta);
        }

        const deltaTime = this.clock.getDelta();
        const currentTime = now;

        // Update animated sky dome
        // if (this.skyDome) {
        //this.updateSkyGradient(deltaTime);
        //}

        // Undo only the reaction's previous offsets BEFORE the mixer, including
        // channels an idle take does not key. The current idle remains the base.
        this.lessonReactionController?.beforeMixer?.();
        // Update mixers for animations
        if (this.mixer) {
            this.mixer.update(deltaTime);
        }

        // Update facial animation controllers — but stand down while A2F
        // is animating the eyes so the local idle blink/track loops don't
        // overwrite A2F's eye blendshape values frame-by-frame. The driver
        // exposes a class-level "any frame applied within ~300 ms" probe
        // with a small grace window so the handoff stays smooth across
        // the brief decay-to-zero tail at the end of each turn.
        const _a2fOwningEyes = A2FLipSyncDriver.isAnyActive();
        if (this.blinkController && !_a2fOwningEyes) {
            this.blinkController.update(currentTime);
        }

        if (this.eyeTrackingController && !_a2fOwningEyes) {
            this.eyeTrackingController.update(currentTime);
        }

        // Idle facial life (resting tone + smile presets + micro-expression
        // tics on brows/eyes/nose). Folded onto this single capped render loop
        // so the idle face is written on the same 60 FPS grid as everything
        // else instead of its old private (uncapped) requestAnimationFrame —
        // that off-beat loop was the source of the idle eyebrow/forehead
        // jitter on high-refresh / Safari displays. Called unconditionally:
        // the controller self-gates during speech and the post-speech cooldown
        // (and zeroes its footprint on hand-off), and it runs BEFORE the
        // emotion overlay so emotion remains the final facial write per turn.
        if (this.idleSmileController) {
            this.idleSmileController.update(currentTime);
        }

        // Drive the A2F emotion overlay from this single render loop, AFTER
        // the blink / eye-tracking controllers and right before render, so
        // emotion's eye/brow/nose shapes are always the final facial write
        // on the rendered cadence. This collapses the renderer's previously
        // independent (uncapped) rAF onto the 60 FPS frame-cap grid and
        // removes the cross-loop contention that flickered the
        // micro-expressions. The renderer is created lazily per TTS session
        // and exposed on the lip-sync controller.
        const _emotionRenderer =
            this.lipSyncController && this.lipSyncController.a2fEmotionRenderer;
        if (_emotionRenderer && typeof _emotionRenderer.renderTick === "function") {
            _emotionRenderer.renderTick(now);
        }

        // Task #238: course-lesson character reactions (nod / "not quite" /
        // considering / gaze-to-problem). Only attached on the course lesson
        // page — /lab never sets `lessonReactionController`, so this is a no-op
        // everywhere else. The controller SELF-GATES on
        // A2FLipSyncDriver.isAnyActive(): during speech it yields the FACE
        // entirely to A2F (A2F is KING) but may let a head-BONE nod/shake briefly
        // ride the start of speech (Task #258 — the voice never drives the head
        // bone). Stepped here on the shared loop, AFTER mixer.update so its small
        // additive head-pose overlay rides on top of the idle clip.
        if (this.lessonReactionController &&
            typeof this.lessonReactionController.update === "function") {
            this.lessonReactionController.update(now);
        }

        // Other updates like particle systems, camera animation, etc.
        if (this.particleSystem) {
            this.particleSystem.update(deltaTime);
        }

        if (this.imagePanel && this.imagePanel.isVisible) {
            this.imagePanel.update();
        }

        // Drain OrbitControls damping every frame. OrbitControls only applies
        // `dampingFactor` (a fraction) of the accumulated drag per update() and
        // keeps the rest in a reservoir; that reservoir is meant to be drained
        // by a per-frame update() in the render loop. Without this call the
        // reservoir was only drained on pointer-move events, so a drag applied
        // only a fraction of your motion (felt "weak / slipping") and trailed
        // the cursor more the longer you dragged (felt "laggy"). With it, the
        // drag tracks the cursor tightly and releases with a smooth glide whose
        // length is set by the chosen feel (see setCameraFeel). Gated on
        // enableDamping so mobile (damping off, direct 1:1 tracking) skips it.
        if (this.controls && this.controls.enableDamping) {
            this.controls.update();
        }

        // Ceremony framing is strictly render-local, after controls. Never feed
        // the temporary camera displacement back into OrbitControls.
        const renderFrame = () => {
            this.lessonReactionController?.beforeRender?.();
            try {
                this.renderer.render(this.scene, this.camera);
            } finally {
                this.lessonReactionController?.afterRender?.();
            }
        };
        renderFrame();

        // Task #274: feed the adaptive-resolution governor with the real
        // rendered-frame cadence (mobile only — null on desktop). When it
        // decides to step the quality ladder, re-apply the renderer size.
        if (this.adaptiveResGovernor) {
            const changedScale = this.adaptiveResGovernor.onFrameRendered(now);
            if (changedScale !== null) {
                this.configureRendererSize();
                // Task #274 (flicker fix): resizing the drawing buffer CLEARS
                // it, and this runs AFTER renderer.render — so without an
                // immediate re-render the compositor shows a blank canvas for
                // one full frame (~42ms on the 24 FPS mobile loop). That was
                // visible as a quick full-page "flicker" every time the
                // governor stepped. Re-render synchronously so the resized
                // buffer is refilled before the browser ever composites it.
                renderFrame();
                console.log(
                    `[ADAPTIVE-RES] scale=${changedScale} level=${this.adaptiveResGovernor.level} medianFrameMs=${Math.round(this.adaptiveResGovernor.medianIntervalMs || 0)}`,
                );
            }
        }

        // ?fps=1 debug readout (updates ~2x/s; near-zero cost otherwise).
        if (this._fpsOverlayEl) {
            this._fpsFrameCount++;
            if (!this._fpsWindowStart) this._fpsWindowStart = now;
            const winMs = now - this._fpsWindowStart;
            if (winMs >= 500) {
                const fps = Math.round((this._fpsFrameCount * 1000) / winMs);
                const pr = this.renderer.getPixelRatio
                    ? this.renderer.getPixelRatio().toFixed(2)
                    : "?";
                const lvl = this.adaptiveResGovernor
                    ? this.adaptiveResGovernor.level
                    : "-";
                this._fpsOverlayEl.textContent = `${fps} fps · px ${pr} · L${lvl}`;
                this._fpsFrameCount = 0;
                this._fpsWindowStart = now;
            }
        }
    }

    setupBackground() {
        // Create custom sky dome with gradient and radial glow
        this.setupSkyDome();

        // Keep the sky dome purely visual - don't use for environment lighting
        this.scene.background = null; // Keep background clear to show skydome
        this.scene.environment = null; // Disable environment lighting from sky dome

        // Disable fog completely
        this.scene.fog = null;

        console.log(
            "Background setup with isolated sky dome (no environment lighting)",
        );
    }

    /*animate() {
        requestAnimationFrame(() => this.animate());

        const deltaTime = this.clock.getDelta();

        // Update animated sky dome
        if (this.skyDome) {
            this.updateSkyGradient(deltaTime);
        }

        // Update mixers for animations
        if (this.mixer) {
            this.mixer.update(deltaTime);
        }

        // Other updates like particle systems, camera animation, etc.
        if (this.particleSystem) {
            this.particleSystem.update(deltaTime);
        }

        // Render scene
        this.renderer.render(this.scene, this.camera);
    } */

    setupSkyDome() {
        console.log("Setting up sky dome with Light/Dark mode support");

        // Sky mode properties - default to Light Mode
        this._skyMode = "light";
        this._skyColors = {
            light: { r: 249, g: 246, b: 240 }, // #f9f6f0
            dark: { r: 0, g: 0, b: 0 }, // black
        };
        this._currentSkyColor = { ...this._skyColors.light };
        this._targetSkyColor = { ...this._skyColors.light };
        this._skyTransitionProgress = 1;
        this._skyTransitionDuration = 2.5; // seconds
        this._skyTransitionId = null; // for animation cancellation

        // Mobile performance: use smaller sky texture and fewer segments
        const isMobile = this.isMobile;
        const skySize = isMobile ? 1024 : 1024;
        const skySegments = isMobile ? 16 : 16;

        // Create canvas for gradient
        this.skyCanvas = document.createElement("canvas");
        this.skyCanvas.width = skySize;
        this.skyCanvas.height = skySize;
        this.skyContext = this.skyCanvas.getContext("2d");

        // Create texture from canvas
        this.skyTexture = new THREE.CanvasTexture(this.skyCanvas);
        this.skyTexture.mapping = THREE.EquirectangularReflectionMapping;
        this.skyTexture.wrapS = THREE.RepeatWrapping;
        this.skyTexture.wrapT = THREE.RepeatWrapping;
        this.skyTexture.anisotropy = isMobile ? 4 : 4;

        // Sky sphere
        const skyGeometry = new THREE.SphereGeometry(
            1000,
            skySegments,
            skySegments,
        );
        const skyMaterial = new THREE.MeshBasicMaterial({
            map: this.skyTexture,
            side: THREE.BackSide,
            fog: false,
            toneMapped: false,
            transparent: false,
            depthWrite: false,
            // depthTest:false + renderOrder:-1 → true draw-first skybox.
            // Any opaque mesh added afterwards still covers it on its
            // own pixels (so the strip pass below is the real fix for
            // embedded FBX backdrops), but this keeps the sky immune to
            // depth-fight glitches against transparent / oddly-ordered
            // geometry.
            depthTest: false,
        });

        this.skyDome = new THREE.Mesh(skyGeometry, skyMaterial);
        this.skyDome.name = "SkyDome";
        this.skyDome.renderOrder = -1;
        this.skyDome.frustumCulled = false;
        this.skyDome.matrixAutoUpdate = false;
        this.skyDome.castShadow = false;
        this.skyDome.receiveShadow = false;
        // Lab UI merge: the warm CSS atmosphere (templates/index.html .scene/.haze)
        // is the backdrop now, so the 3D sky ball is NOT added to the scene. The
        // mesh + setSkyMode/updateSkyGradient refs stay intact (harmless no-ops
        // since the dome never renders) to avoid null guards elsewhere. Platform,
        // ground plane and lighting are unaffected.
        // this.scene.add(this.skyDome);

        // Animation variables
        this.animTime = 0;
        this.waterfalls = [];

        // Initialize waterfall streams
        /* for (let i = 0; i < 8; i++) {
            this.waterfalls.push({
                x: Math.random(),
                speed: 0.3 + Math.random() * 0.7,
                width: 0.1 + Math.random() * 0.2,
                color: this.getRandomColor(),
                phase: Math.random() * Math.PI * 2,
                offset: Math.random() * 1000,
            });
        }*/

        // First draw
        this.updateSkyGradient();
        console.log("Waterfall sky dome created successfully");
    }

    /*getRandomColor() {
        const colors = [
            "#050608", // almost true black
            "#0B0D10", // near-black charcoal
            "#13151A", // deep graphite
            "#191C22", // soft dark gray
            "#20242B", // subtle slate gray glow
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    }*/

    updateSkyGradient() {
        const ctx = this.skyContext;
        const w = this.skyCanvas.width;
        const h = this.skyCanvas.height;

        // Reset transform
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // Fill entire canvas with current sky color
        ctx.clearRect(0, 0, w, h);
        const { r, g, b } = this._currentSkyColor;
        ctx.fillStyle = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
        ctx.fillRect(0, 0, w, h);

        // Update Three.js texture
        this.skyTexture.needsUpdate = true;
    }

    setSkyMode(mode, animated = true) {
        if (mode !== "light" && mode !== "dark") {
            console.warn("[ModelViewer] Invalid sky mode:", mode);
            return;
        }

        if (this._skyMode === mode && this._skyTransitionProgress >= 1) {
            console.log("[ModelViewer] Already in", mode, "mode");
            return;
        }

        console.log(
            "[ModelViewer] Switching to",
            mode,
            "mode",
            animated ? "with animation" : "instantly",
        );

        // Cancel any existing transition
        if (this._skyTransitionId) {
            cancelAnimationFrame(this._skyTransitionId);
            this._skyTransitionId = null;
        }

        this._skyMode = mode;
        this._targetSkyColor = { ...this._skyColors[mode] };

        if (animated) {
            this._skyTransitionProgress = 0;
            this._startSkyTransition();
        } else {
            this._currentSkyColor = { ...this._targetSkyColor };
            this._skyTransitionProgress = 1;
            this.updateSkyGradient();
        }
    }

    _startSkyTransition() {
        const startColor = { ...this._currentSkyColor };
        const targetColor = { ...this._targetSkyColor };
        const duration = this._skyTransitionDuration * 1000;
        const startTime = performance.now();

        const easeInOutCubic = (t) => {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            this._currentSkyColor.r =
                startColor.r + (targetColor.r - startColor.r) * easedProgress;
            this._currentSkyColor.g =
                startColor.g + (targetColor.g - startColor.g) * easedProgress;
            this._currentSkyColor.b =
                startColor.b + (targetColor.b - startColor.b) * easedProgress;

            this._skyTransitionProgress = progress;
            this.updateSkyGradient();

            if (progress < 1) {
                this._skyTransitionId = requestAnimationFrame(animate);
            } else {
                // Ensure final color is exactly the target
                this._currentSkyColor = { ...targetColor };
                this.updateSkyGradient();
                this._skyTransitionId = null;
                console.log("[ModelViewer] Sky transition complete");
            }
        };

        this._skyTransitionId = requestAnimationFrame(animate);
    }

    getSkyMode() {
        return this._skyMode;
    }

    // HDRI methods have been replaced by custom sky dome
    // Removed: setupHDRISkybox(), loadHDRFallback(), setupCubemapSkybox(), setupGradientBackground()

    setupLighting() {
        // SAME lighting on mobile + desktop
        const AMBIENT_INTENSITY = 0.8;
        const MAIN_INTENSITY = 1.8;
        const FILL_INTENSITY = 1.5;
        const RIM_INTENSITY = 2.0;
        // Bottom-bounce directional removed (perf): each extra light adds a
        // per-pixel lighting calc that hurts most on high-DPI screens. Its weak
        // (0.8) under-fill is folded into the hemisphere ground color below so
        // the look stays essentially the same.
        const HEMI_INTENSITY = 1.3;
        const POINT_INTENSITY = 1.8;

        // Ambient
        const ambientLight = new THREE.AmbientLight(
            0xfff8f5,
            AMBIENT_INTENSITY,
        );
        ambientLight.layers.disableAll();
        ambientLight.layers.enable(0);
        this.scene.add(ambientLight);
        this.ambientLight = ambientLight;

        // Main directional
        const mainLight = new THREE.DirectionalLight(0xfff4d6, MAIN_INTENSITY);
        mainLight.position.set(-20, 80, 60);
        mainLight.castShadow = false; // keep off for performance + consistency
        mainLight.layers.disableAll();
        mainLight.layers.enable(0);
        this.scene.add(mainLight);
        this.mainLight = mainLight;

        // Fill directional
        const fillLight = new THREE.DirectionalLight(0xb8d4ff, FILL_INTENSITY);
        fillLight.position.set(40, 60, -30);
        fillLight.castShadow = false;
        fillLight.layers.disableAll();
        fillLight.layers.enable(0);
        this.scene.add(fillLight);
        this.fillLight = fillLight;

        // Rim directional
        const rimLight = new THREE.DirectionalLight(0xffffff, RIM_INTENSITY);
        rimLight.position.set(0, 40, -80);
        rimLight.castShadow = false;
        rimLight.layers.disableAll();
        rimLight.layers.enable(0);
        this.scene.add(rimLight);
        this.rimLight = rimLight;

        // Bottom-bounce directional removed (perf). The hemisphere ground
        // color below is slightly brightened (0x444444 -> 0x555555) plus its
        // intensity nudged up to preserve the soft under-fill it provided.

        // Hemisphere
        const hemisphereLight = new THREE.HemisphereLight(
            0xffffff,
            0x555555,
            HEMI_INTENSITY,
        );
        hemisphereLight.layers.disableAll();
        hemisphereLight.layers.enable(0);
        this.scene.add(hemisphereLight);
        this.hemisphereLight = hemisphereLight;

        // Point light
        const pointLight = new THREE.PointLight(0xffffff, POINT_INTENSITY, 300);
        pointLight.position.set(50, 100, 50);
        pointLight.castShadow = false;
        pointLight.layers.disableAll();
        pointLight.layers.enable(0);
        this.scene.add(pointLight);
        this.pointLight = pointLight;

        // Every analytic rig light, so Studio mode (setLightingMode) can
        // hide and restore the WHOLE rig without ever deleting it. Keep in
        // sync if a light is ever added to / removed from this method.
        this._analyticLights = [
            ambientLight,
            mainLight,
            fillLight,
            rimLight,
            hemisphereLight,
            pointLight,
        ];

        console.log(
            "Lighting setup completed (6 lights, same on mobile + desktop)",
        );
    }

    /**
     * Set up the platform for the given character (or a generic boot
     * platform if modelId is null). Delegates to applyPose() which is
     * the single atomic platform+model commit path.
     */
    setupPlatform(modelId = null) {
        if (this.groundPlane) {
            this.scene.remove(this.groundPlane);
        }
        return this.applyPose(modelId);
    }

    /**
     * Atomic pose commit. Loads the platform FBX off-scene, then in the
     * loader's onLoad callback synchronously:
     *   - removes the previous platform,
     *   - adds the new platform at the pose-derived (Y, Z) and scale,
     *   - sets model.position.y and flips model.visible = true.
     *
     * Guarded by a monotonically increasing token so a stale callback
     * from a previous character cannot stomp the current one.
     */
    applyPose(modelId = null, opts = null) {
        const token = ++this._poseRequestToken;
        const pose = computePose(modelId, opts);
        console.log(
            `[applyPose] modelId=${modelId} name=${pose.name} ` +
            `eyeHeight=${pose.eyeHeight.toFixed(2)} (source=${pose.source}) ` +
            `modelY=${pose.modelY.toFixed(2)} platformY=${pose.platformY.toFixed(2)}`,
        );

        const commit = (platformObj) => {
            if (token !== this._poseRequestToken) {
                console.log(
                    `[applyPose] discarding stale pose token=${token} ` +
                    `current=${this._poseRequestToken}`,
                );
                return;
            }
            // Remove previous platform synchronously with the new one's add
            if (this.platform) {
                this.scene.remove(this.platform);
            }
            platformObj.position.set(0, pose.platformY, pose.platformZ);
            platformObj.scale.set(
                pose.platformScale,
                pose.platformScale,
                pose.platformScale,
            );
            platformObj.userData.modelId = modelId;
            platformObj.userData.pose = pose;
            this.platform = platformObj;
            this.scene.add(platformObj);

            if (this.model) {
                const floorOffset = Number.isFinite(this._glbFloorOffsetY)
                    ? this._glbFloorOffsetY
                    : 0;
                this.model.position.set(0, pose.modelY + floorOffset, 0);
                // Face the camera by default. Per-character override via
                // characterData[id].modelRotationY (radians) for assets
                // whose authored forward axis is not -Z.
                const charEntry =
                    (characterData && characterData[modelId]) || null;
                const rotY =
                    charEntry &&
                    Number.isFinite(charEntry.modelRotationY)
                        ? charEntry.modelRotationY
                        : 0;
                this.model.rotation.y = rotY;

                // One-shot loader handoff. The first
                // time the freshly-loaded model becomes visible it shows
                // FULLY SOLID (founder ruling 2026-08-11: no character ever
                // starts at opacity 0 — the old dissolve-in is gone) and the
                // editorial loading surface is told to exit. Guarded by the
                // _pendingReveal flag so later applyPose() calls (pose
                // tuning, re-framing) don't re-trigger it.
                if (this._pendingReveal) {
                    this._pendingReveal = false;
                    // Task #199: never show the character behind the
                    // Hall/selector picker cards. While a picker overlay is
                    // open, keep the model hidden and defer the reveal until
                    // the picker settles, so a cold visitor sees a clean
                    // sequence (loading -> character -> picker).
                    this._revealModelWhenStageClear();
                } else {
                    this.model.visible = true;
                    // Model-ready safety net (Task #331, kept by #340 as
                    // pure enforcement): whenever a pose commit re-shows an
                    // already-revealed model, force its materials back to
                    // authored blend state — heals any stray transparency;
                    // there is no fade left to wait out.
                    this._settleAuthoredMaterials(this.model);
                }
                // Re-frame the camera against the actual eye world
                // positions now that the model is at its committed pose.
                // Static SCENE_FRAMING.faceAnchorY assumed every rig's
                // eyes landed at Y=180 after pose committal — true for
                // ARKit GLBs but false for any rig whose floor offset
                // shifts them upward. The framing pass reads real eye
                // world positions and is self-correcting; runs for
                // both GLB and FBX since Task #75.
                try {
                    this._applyEyeBasedFraming(modelId);
                } catch (e) {
                    console.warn(
                        "[applyPose] eye-based framing failed:",
                        e,
                    );
                }
            }
        };

        // Task #552 follow-on (post-100% gap): the platform is one static
        // FBX prop shared by every character, but each applyPose used to
        // re-fetch + re-parse it — and the reveal hard-cut waited on that.
        // Load and prepare it once per page (kicked off in parallel with
        // the character download in loadModel), then commit the same
        // prepared object on every later pose. Load failures keep the old
        // fallback slab and are not cached, so a later pose retries.
        this._loadSharedPlatform().then(
            (platformObj) => commit(platformObj),
            () => {
                const fallbackGeo = new THREE.BoxGeometry(500, 2, 500);
                const fallbackMat = new THREE.MeshBasicMaterial({
                    color: 0xffffff,
                    side: THREE.DoubleSide,
                });
                commit(new THREE.Mesh(fallbackGeo, fallbackMat));
            },
        );
    }

    /**
     * Fetch + prepare the shared platform FBX exactly once per page.
     * Resolves with the SAME prepared object every time — the platform is
     * a singleton prop; applyPose re-positions/re-scales it per character
     * and re-adding an already-added object to the scene is a no-op.
     * Material setup mirrors the old per-load path (async per mesh; the
     * commit never waited on it). Rejection is not cached: the next
     * applyPose retries the load and falls back to the plain slab.
     */
    _loadSharedPlatform() {
        if (sharedPlatformObject) {
            return Promise.resolve(sharedPlatformObject);
        }
        if (!sharedPlatformPromise) {
            const customTextures = {
                albedo: "/static/textures/platform.png",
                normal: "/static/textures/platform_normal.png",
                roughness: "/static/textures/platform_roughness.png",
            };
            sharedPlatformPromise = new Promise((resolve, reject) => {
                new FBXLoader().load(
                    "/static/models/platform.fbx",
                    (platformModel) => {
                        platformModel.receiveShadow = false;
                        platformModel.castShadow = false;
                        platformModel.traverse(async (child) => {
                            if (child.isMesh) {
                                child.receiveShadow = false;
                                child.castShadow = false;
                                try {
                                    await this.materialManager.setupPlatformMaterial(
                                        child,
                                        customTextures,
                                    );
                                } catch (e) {
                                    console.warn(
                                        "[applyPose] platform material setup failed:",
                                        e,
                                    );
                                }
                            }
                        });
                        sharedPlatformObject = platformModel;
                        resolve(platformModel);
                    },
                    undefined,
                    (error) => {
                        console.error(
                            "[applyPose] Error loading platform FBX, using fallback:",
                            error,
                        );
                        sharedPlatformPromise = null; // not cached — retry
                        reject(error);
                    },
                );
            });
        }
        return sharedPlatformPromise;
    }

    /**
     * Create a 3D reflective ground plane below the platform
     * @param {number} height - Height position for the ground plane
     */
    createGroundPlane(height) {
        // Skip reflective ground on mobile for performance
        if (this.isMobile) {
            console.log("Skipping reflective ground on mobile for performance");
            return;
        }

        // Create a large plane geometry for the ground
        const planeGeometry = new THREE.PlaneGeometry(300, 200);

        // Create reflector for realistic ground reflections
        this.createGroundReflector(planeGeometry, height);
    }

    /**
     * Create a reflective ground surface using THREE.Reflector
     * @param {THREE.PlaneGeometry} geometry - The plane geometry
     * @param {number} height - Height position for the ground plane
     */
    createGroundReflector(geometry, height) {
        try {
            // Create reflector with high-quality settings
            const reflector = new Reflector(geometry, {
                clipBias: 0.003,
                textureWidth: Math.min(
                    window.innerWidth * window.devicePixelRatio,
                    1024,
                ),
                textureHeight: Math.min(
                    window.innerHeight * window.devicePixelRatio,
                    1024,
                ),
                color: 0x889999,
                recursion: 1,
            });

            // Position the reflector horizontally below the platform
            reflector.rotation.x = -Math.PI / 2; // Rotate to be horizontal
            reflector.position.set(0, height, 0);

            this.groundPlane = reflector;
            this.scene.add(reflector);

            console.log(`Reflective ground plane created at height: ${height}`);
        } catch (error) {
            console.warn(
                "Failed to create Reflector, falling back to simulated reflection:",
                error,
            );
            // Fall back to simulated reflection if Reflector fails
            this.createSimulatedReflectiveGround(geometry, height);
        }
    }

    /**
     * Create a simulated reflective ground using render targets and custom material
     * @param {THREE.PlaneGeometry} geometry - The plane geometry
     * @param {number} height - Height position for the ground plane
     */
    createSimulatedReflectiveGround(geometry, height) {
        // Create render target for reflection
        const renderTarget = new THREE.WebGLRenderTarget(
            window.innerWidth * 0.5,
            window.innerHeight * 0.5,
            {
                minFilter: THREE.LinearFilter,
                magFilter: THREE.LinearFilter,
                format: THREE.RGBFormat,
                type: THREE.UnsignedByteType,
            },
        );

        // Create reflection camera
        this.reflectionCamera = new THREE.PerspectiveCamera(
            this.camera.fov,
            this.camera.aspect,
            this.camera.near,
            this.camera.far,
        );

        // Create reflective material with the render target as a texture
        const reflectiveMaterial = new THREE.MeshStandardMaterial({
            color: 0x404040, // Dark base color for realistic reflection
            roughness: 0.1, // Low roughness for mirror-like surface
            metalness: 0.8, // High metalness for reflective properties
            envMapIntensity: 1.0,
            side: THREE.DoubleSide,
        });

        // Add reflection texture mixing
        reflectiveMaterial.onBeforeCompile = (shader) => {
            shader.uniforms.tReflection = { value: renderTarget.texture };
            shader.uniforms.uReflectionStrength = { value: 0.6 };
            shader.uniforms.uReflectionBlur = { value: 0.1 };

            shader.vertexShader = `
                varying vec4 vWorldPosition;
                ${shader.vertexShader}
            `.replace(
                "#include <project_vertex>",
                `
                #include <project_vertex>
                vWorldPosition = modelMatrix * vec4(position, 1.0);
                `,
            );

            shader.fragmentShader = `
                uniform sampler2D tReflection;
                uniform float uReflectionStrength;
                uniform float uReflectionBlur;
                varying vec4 vWorldPosition;
                ${shader.fragmentShader}
            `.replace(
                "#include <output_fragment>",
                `
                // Calculate reflection UV coordinates
                vec2 reflectionUv = gl_FragCoord.xy / vec2(${window.innerWidth * 0.5}, ${window.innerHeight * 0.5});
                reflectionUv.y = 1.0 - reflectionUv.y;

                // Sample reflection texture
                vec3 reflection = texture2D(tReflection, reflectionUv).rgb;

                // Mix reflection with surface color
                gl_FragColor.rgb = mix(gl_FragColor.rgb, reflection, uReflectionStrength);

                #include <output_fragment>
                `,
            );
        };

        // Create the mesh
        this.groundPlane = new THREE.Mesh(geometry, reflectiveMaterial);

        // Position the plane horizontally below the platform
        this.groundPlane.rotation.x = -Math.PI / 2; // Rotate to be horizontal
        this.groundPlane.position.set(0, height, 0);

        // Enable shadow reception for the ground
        this.groundPlane.receiveShadow = true;
        this.groundPlane.castShadow = false;

        // Store render target for cleanup
        this.groundReflectionTarget = renderTarget;

        // Add to scene
        this.scene.add(this.groundPlane);

        console.log(
            `Simulated reflective ground plane created at height: ${height}`,
        );
    }

    /**
     * Update the ground reflection for simulated reflection
     */
    updateGroundReflection() {
        if (
            !this.reflectionCamera ||
            !this.groundReflectionTarget ||
            !this.groundPlane
        ) {
            return;
        }

        // Position reflection camera as a mirror of the main camera
        this.reflectionCamera.position.copy(this.camera.position);
        this.reflectionCamera.position.y =
            -this.camera.position.y + 2 * this.groundPlane.position.y;
        this.reflectionCamera.lookAt(0, this.groundPlane.position.y, 0);

        // Temporarily hide the ground plane to avoid self-reflection
        this.groundPlane.visible = false;

        // Render reflection to the render target
        this.renderer.setRenderTarget(this.groundReflectionTarget);
        this.renderer.render(this.scene, this.reflectionCamera);
        this.renderer.setRenderTarget(null);

        // Show the ground plane again
        this.groundPlane.visible = true;
    }

    /**
     * Update camera position based on model platform configuration
     * @param {Object} platformConfig - Platform configuration object
     */
    updateCameraForModel(platformConfig) {
        if (!this.camera || !this.controls) {
            console.warn("Camera or controls not available for update");
            return;
        }

        const optimalCameraConfig =
            calculateOptimalCameraPosition(platformConfig);

        console.log(
            `Adjusting camera for ${platformConfig.name} (${platformConfig.heightCategory})`,
        );
        console.log("Optimal camera config:", optimalCameraConfig);

        // Smoothly animate camera to new position
        const startPosition = this.camera.position.clone();
        const startTarget = this.controls.target.clone();

        const endPosition = new THREE.Vector3(
            optimalCameraConfig.position.x,
            optimalCameraConfig.position.y,
            optimalCameraConfig.position.z,
        );

        const endTarget = new THREE.Vector3(
            optimalCameraConfig.target.x,
            optimalCameraConfig.target.y,
            optimalCameraConfig.target.z,
        );

        // Animate camera position over 1 second
        const animationDuration = 1000; // 1 second
        const startTime = Date.now();

        const animateCamera = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);

            // Use easing function for smooth animation
            const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out

            // Interpolate camera position
            this.camera.position.lerpVectors(
                startPosition,
                endPosition,
                easeProgress,
            );
            this.controls.target.lerpVectors(
                startTarget,
                endTarget,
                easeProgress,
            );

            this.controls.update();

            if (progress < 1) {
                requestAnimationFrame(animateCamera);
            } else {
                console.log(
                    "Camera animation complete for model-specific positioning",
                );
                // Reset zoom state baseline after camera repositioning
                this._resetZoomStateBaseline();
            }
        };

        animateCamera();
    }

    /**
     * Reset the zoom state baseline after camera config changes (desktop only)
     * This ensures Y position targets are correct for the new camera position
     * Note: This is called AFTER camera animations complete, so camera is already
     * at the intended offset-free position - we just capture it as the new baseline
     */
    _resetZoomStateBaseline() {
        if (!this._zoomState) return;

        // Cancel any running zoom animation
        if (this._zoomAnimationId) {
            cancelAnimationFrame(this._zoomAnimationId);
            this._zoomAnimationId = null;
        }

        // Capture current camera position as the new baseline
        // Camera is already at the intended position from the camera animation
        // so we don't need to adjust it - just record it as the new baseline
        const newCameraBaseline = this.camera.position.y;
        const newTargetBaseline = this.controls.target.y;
        this._zoomState.baselineCameraY = newCameraBaseline;
        this._zoomState.baselineTargetY = newTargetBaseline;
        this._zoomState.currentLerpT = 0;
        this._zoomState.targetLerpT = 0;
        this._zoomState.isAnimating = false;
        this._zoomState.isExtremeZoom = false;

        console.log(
            `Zoom state baseline reset: cameraY=${newCameraBaseline}, targetY=${newTargetBaseline}`,
        );
    }

    startColorAnimation() {
        const animateToNextPreset = () => {
            if (this.isTransitioning) return;

            this.isTransitioning = true;
            const nextIndex =
                (this.currentPresetIndex + 1) % this.colorPresets.length;
            const nextPreset = this.colorPresets[nextIndex];

            this.setBackgroundColors(
                nextPreset.top,
                nextPreset.bottom,
                this.transitionDuration,
            );
            this.currentPresetIndex = nextIndex;

            setTimeout(() => {
                this.isTransitioning = false;
                setTimeout(animateToNextPreset, this.pauseDuration * 1000);
            }, this.transitionDuration * 1000);
        };

        setTimeout(animateToNextPreset, this.pauseDuration * 1000);
    }

    setBackgroundColors(topColor, bottomColor, duration = 1.0) {
        // Skip color updates if using skybox (we'll check a property we add in the constructor)
        if (this.usingSkybox) {
            // Still update stored colors so GUI controls stay in sync
            this.backgroundColors.top.set(topColor);
            this.backgroundColors.bottom.set(bottomColor);
            return;
        }

        // Continue with normal gradient color transition
        const oldTop = this.backgroundColors.top.clone();
        const oldBottom = this.backgroundColors.bottom.clone();
        const newTop = new THREE.Color(topColor);
        const newBottom = new THREE.Color(bottomColor);

        const startTime = performance.now();

        const updateColors = () => {
            const elapsed = (performance.now() - startTime) / 1000;
            const progress = Math.min(elapsed / duration, 1);

            this.backgroundColors.top.copy(oldTop).lerp(newTop, progress);
            this.backgroundColors.bottom
                .copy(oldBottom)
                .lerp(newBottom, progress);

            // Only update if we're not using the skybox
            if (!this.usingSkybox) {
                // Just redraw the gradient background, not the whole setupBackground which might recreate skybox
                this.updateGradientBackground();
            }

            if (progress < 1) {
                requestAnimationFrame(updateColors);
            }
        };

        updateColors();
    }

    // Helper method to update just the gradient background
    updateGradientBackground() {
        const canvas = document.createElement("canvas");
        canvas.width = 2;
        canvas.height = 512;
        const context = canvas.getContext("2d");
        const gradient = context.createLinearGradient(0, 0, 0, 512);
        gradient.addColorStop(0, this.backgroundColors.top.getStyle());
        gradient.addColorStop(1, this.backgroundColors.bottom.getStyle());
        context.fillStyle = gradient;
        context.fillRect(0, 0, 2, 512);

        const texture = new THREE.CanvasTexture(canvas);
        this.scene.background = texture;
    }

    setSpotlightIntensity(index, intensity, duration = 1.0) {
        if (index >= 0 && index < this.spotLights.length) {
            const spotlight = this.spotLights[index];
            const oldIntensity = spotlight.intensity;
            const startTime = performance.now();

            const updateIntensity = () => {
                const elapsed = (performance.now() - startTime) / 1000;
                const progress = Math.min(elapsed / duration, 1);

                spotlight.intensity =
                    oldIntensity + (intensity - oldIntensity) * progress;

                if (progress < 1) {
                    requestAnimationFrame(updateIntensity);
                }
            };

            updateIntensity();
        }
    }

    setupModelControls() {
        const modelSelector = document.getElementById("character-model");
        const switchButton = document.getElementById("switch-model");

        if (!modelSelector || !switchButton) {
            console.warn("Model selection controls not found in the DOM");
            return;
        }

        switchButton.addEventListener("click", async () => {
            const selectedModel = modelSelector.value;
            if (selectedModel) {
                await this.switchModel(selectedModel);
            } else {
                console.warn("No model selected");
            }
        });
    }

    async switchModel(modelId) {
        if (modelId === this.currentModelKey) {
            console.log("Model already active, no need to switch");
            return;
        }

        // Clean up particle system before switching models
        if (this.particleSystem) {
            this.particleSystem.dispose();
            this.particleSystem = null;
        }

        // Use the class instance of ModelManager to handle the model change
        // This will update the server, set localStorage, and reload the page
        await this.modelManager.setCurrentModel(modelId, true);
    }

    async initialize() {
        const loadingOverlay = document.getElementById("loading-overlay");
        const loadingText = document.getElementById("loading-text");
        const errorOverlay = document.getElementById("error-overlay");

        try {
            // Show loading indicator
            if (loadingOverlay) loadingOverlay.style.display = "flex";
            if (loadingText) loadingText.textContent = "Initializing...";
            if (errorOverlay) errorOverlay.classList.add("d-none");

            // Initialize 3D image panels for displaying images behind model
            this.imagePanel3D = new ImagePanel3D(this.scene, this.camera);

            // Initialize our new SimpleImagePanel implementation.
            // Task #403: the placement provider recomputes the
            // per-character head anchor on every show, so the artwork
            // hangs beside the head of whichever legend is loaded.
            this.imagePanel = new SimpleImagePanel(this.scene, this.camera, {
                getPlacementAnchor: () => this._computeArtworkAnchor(),
            });
            // Expose globally for chat image clicks (same 3D preview as bio viewer)
            window.simpleImagePanel = this.imagePanel;
            console.log("3D image panel initialized and exposed globally");

            console.log(
                "Starting ModelViewer initialization with Three.js 0.170.0",
            );

            // Make sure WebGL is supported in browser
            try {
                // Try creating a WebGL renderer to test support
                const testCanvas = document.createElement("canvas");
                const testRenderer = new THREE.WebGLRenderer({
                    canvas: testCanvas,
                    antialias: false,
                    alpha: false,
                });
                testRenderer.dispose();
                console.log("WebGL is supported in this browser");
            } catch (webglError) {
                console.warn(
                    "WebGL not available - app will run in limited mode:",
                    webglError,
                );
                // Don't throw error - let the app continue working in limited mode
                // The 3D features won't work but other features like quotes will still function
            }

            // Initialize our class instance of ModelManager
            // Wait for the ModelManager to initialize and check for model IDs
            await this.modelManager.initialize();

            // Task #84: Prefer the server-rendered inline JSON to skip a
            // full RTT to /api/models. Fall back to the fetch when the
            // inline payload isn't present (e.g. /lab visited without a
            // character slug, or any non-/lab page that reuses this code).
            let models;
            const inlineModelsEl = document.getElementById("available-models");
            if (inlineModelsEl && inlineModelsEl.textContent) {
                try {
                    models = JSON.parse(inlineModelsEl.textContent);
                    console.log(
                        "Available models (from inline JSON, 0 RTT):",
                        models,
                    );
                } catch (e) {
                    console.warn(
                        "Failed to parse inline available-models JSON; falling back to fetch",
                        e,
                    );
                    models = null;
                }
            }
            if (!models) {
                console.log("Fetching available models...");
                const response = await fetch("/api/models");
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch available models: ${response.status} ${response.statusText}`,
                    );
                }
                models = await response.json();
                console.log("Available models:", models);
            }

            // Get model ID from the central ModelManager.
            // Task #251 — forcedModelId (set by ResidentLegend on the /learn
            // stage) hard-pins the exact faculty UUID. It wins over the
            // ModelManager's current id, whose second-pass fallback can resolve
            // to the process-global CURRENT_MODEL (the last /lab pick).
            let modelToLoad =
                this.forcedModelId || this.modelManager.getCurrentModelId();
            // Bare-/lab guard: app.config["CURRENT_MODEL"] is process-global
            // and shared across ALL visitors, so a visitor on bare /lab could
            // otherwise inherit the last picker's choice via /api/models/current
            // and silently download its ~190MB model before they pick anything.
            // Task #545 (Hall-first): bare /lab ALWAYS opens the Hall now — for
            // returning visitors too — so the server-global fallback is only
            // honored for an EXPLICIT request (forcedModelId or a URL
            // deep-link/preselect). Everyone else falls through to the Hall
            // with nothing on stage; picking a card navigates to /lab/<slug>,
            // which arrives with PRESELECTED_MODEL_ID set.
            {
                const src = this.modelManager.modelIdSource;
                const bareHallEntry =
                    window.__LAB_EXPLICIT_ENTRY === false &&
                    !this.forcedModelId;
                if (
                    modelToLoad &&
                    bareHallEntry &&
                    (src === "server" || src === "localStorage")
                ) {
                    console.log(
                        "ModelViewer: bare /lab — ignoring remembered/shared model; Hall handles entry (no premature model download).",
                    );
                    modelToLoad = null;
                }
            }
            console.log(
                "Selected model to load from ModelManager:",
                modelToLoad || "none",
            );

            // Verify we have a model available
            if (!modelToLoad && (!models || models.length === 0)) {
                throw new Error("No models available for display");
            }

            // Update UI for character selection if we have a model
            const characterModel = document.getElementById("character-model");
            if (characterModel && modelToLoad) {
                // Check if the model exists in the characterData using the getCharacterById method
                const character = characterData.getCharacterById(modelToLoad);
                if (!character) {
                    console.error("Invalid model ID:", modelToLoad);
                    throw new Error(`Invalid model ID: ${modelToLoad}`);
                }
                characterModel.value = modelToLoad;
                // Dispatch event for character switch
                console.log(
                    "Dispatching characterSwitch event with character:",
                    character.name,
                );
                const event = new Event("characterSwitch");
                document.dispatchEvent(event);
            }

            // Handle panel visibility based on whether a character model is explicitly selected
            const panel = document.getElementById("character-panel");

            if (!modelToLoad) {
                // No character FBX selected - ensure panel is OPEN
                console.log(
                    "No character FBX selected - force panel to stay OPEN",
                );
                if (panel) {
                    panel.classList.add("panel-visible");
                    window.isPanelVisible = true;
                    localStorage.setItem("keepCharacterPanelOpen", "true");
                }
            }

            // Only load a model if one is explicitly selected
            if (modelToLoad) {
                console.log("Loading selected model:", modelToLoad);
                await this.loadSelectedModel(modelToLoad);

                // Notify the ModelManager that we've loaded the model
                this.modelManager.notifyModelLoaded(modelToLoad);

                // Ensure character panel stays visible after model loading only if forceKeepOpen is true
                if (localStorage.getItem("forceKeepOpen") === "true") {
                    if (panel) {
                        setTimeout(() => {
                            console.log(
                                "Final check to ensure character panel stays open after model load",
                            );
                            panel.classList.add("panel-visible");
                            window.isPanelVisible = true;
                        }, 500); // Short delay to ensure UI has updated
                    }
                }
            } else {
                console.log("No model selected, skipping model load");
            }

            // Start the animation loop
            console.log("Starting animation loop");
            this.animate();

            console.log("ModelViewer initialization completed successfully");
        } catch (error) {
            console.error("Error during ModelViewer initialization:", error);

            // Only display error overlay for critical errors, not WebGL-related issues
            if (error.message && error.message.includes("WebGL")) {
                console.warn(
                    "WebGL not available - continuing with limited functionality",
                );
            } else if (errorOverlay && !error.message?.includes("renderer")) {
                // Only show error overlay for non-WebGL/renderer errors
                errorOverlay.classList.remove("d-none");
                const alertElement = errorOverlay.querySelector(".alert");
                if (alertElement) {
                    alertElement.textContent = `Error: Failed to initialize 3D viewer: ${error.message}`;
                }
                throw new Error(
                    `Failed to initialize 3D viewer: ${error.message}`,
                );
            }
        } finally {
            // Hide loading overlay regardless of success/failure
            if (loadingOverlay) loadingOverlay.style.display = "none";
        }
    }

    async loadSelectedModel(modelId) {
        // Serialize character loads, but register this turn synchronously so
        // rapid B→C selections can supersede an in-flight A immediately. The
        // newest loader paints at once; only the latest queued turn is allowed
        // to mutate ModelViewer after the prior network/setup work settles.
        let previousSelectedLoad = null;
        let selectedLoadSlot = null;
        let releaseSelectedLoad = null;
        if (modelId) {
            previousSelectedLoad = this._selectedModelLoadTail;
            selectedLoadSlot = new Promise((resolve) => {
                releaseSelectedLoad = resolve;
            });
            this._selectedModelLoadTail = selectedLoadSlot;
        }

        // Character panel behavior depends on whether a model ID is provided
        const panel = document.getElementById("character-panel");

        if (!modelId) {
            // No character FBX - keep panel OPEN
            if (panel) {
                console.log(
                    "No character FBX loaded - keeping character panel OPEN",
                );
                panel.classList.add("panel-visible");
                window.isPanelVisible = true;
                localStorage.setItem("keepCharacterPanelOpen", "true");

                // Force the panel to be visible with a short delay to ensure DOM updates
                setTimeout(() => {
                    panel.classList.add("panel-visible");
                    window.isPanelVisible = true;
                    console.log("Enforcing panel visibility without FBX model");
                }, 100);
            }
        } else {
            // Character FBX is being loaded - keep panel CLOSED
            if (panel) {
                console.log(
                    "Character FBX loaded - keeping character panel CLOSED",
                );
                panel.classList.remove("panel-visible");
                window.isPanelVisible = false;
                localStorage.removeItem("keepCharacterPanelOpen");
                localStorage.removeItem("forceKeepOpen");
            }
        }

        const loadingOverlay = document.getElementById("loading-overlay");
        const loadingText = document.getElementById("loading-text");
        const progressBar = document.getElementById("loading-progress-bar");
        const loadingQuote = document.getElementById("loading-quote");
        const loadingQuoteAuthor = document.getElementById(
            "loading-quote-author",
        );
        const loadingQuoteContainer = document.getElementById(
            "loading-quote-container",
        );
        let materializeToken = null;

        if (loadingOverlay) loadingOverlay.style.display = "flex";
        if (loadingText) loadingText.textContent = "Checking model status...";
        if (progressBar) {
            progressBar.style.width = "5%";
        }

        // The visible loading experience is a non-blocking editorial title
        // card. Start it for a real character load (modelId present); the
        // legacy overlay above remains only as the boot-watchdog host.
        // start() returns a generation token so delayed callbacks from a
        // previous character cannot update or dismiss the current wait.
        // _pendingReveal arms the one-shot hard cut after the solid model
        // first paints (see applyPose commit).
        if (modelId) {
            this._pendingReveal = true;
            try {
                const selectedCharacter =
                    characterData?.getCharacterById?.(modelId) || null;
                const rosterEntry = Object.values(window.__LEGENDS_DATA__ || {}).find(
                    (entry) => entry && entry.modelId === modelId,
                ) || null;
                const selectedSlug = Object.keys(window.LEGEND_TO_MODEL_ID || {}).find(
                    (slug) => window.LEGEND_TO_MODEL_ID[slug] === modelId,
                );
                const mergedPortrait = rosterEntry ||
                    (selectedSlug && window.__LEGENDS_DATA__?.[selectedSlug]) ||
                    null;
                // The loader paints ONLY the loader-scoped image
                // (`loaderPortrait`, Task #586). It must never fall back to
                // the card `portrait` (uploaded still / bundled card art) or
                // to a local 3D render — no loader image ⇒ no image.
                const loaderImage = mergedPortrait?.loaderPortrait
                    ? {
                        url: mergedPortrait.loaderPortrait,
                        alt: mergedPortrait.portraitProvenance?.alt ||
                            `Historical reference image of ${selectedCharacter?.name || "the selected legend"}`,
                      }
                    : (modelId === window.PRESELECTED_MODEL_ID &&
                        window.PRESELECTED_MODEL_IMAGE
                        ? {
                            url: window.PRESELECTED_MODEL_IMAGE,
                            alt: window.PRESELECTED_MODEL_IMAGE_ALT || "",
                          }
                        : null);
                materializeToken = window.MaterializeLoader?.start({
                    name:
                        selectedCharacter?.name ||
                        window.PRESELECTED_MODEL_NAME ||
                        "",
                    phase: "preparing",
                    image: loaderImage,
                });
                this._materializeLoadToken = materializeToken;
                // A previous character's scene sparkles may still exist for
                // the brief connection phase before cleanupCurrentModel().
                // Hide them immediately; reveal() restores the new system.
                if (this.particleSystem?.particleSystem) {
                    this.particleSystem.particleSystem.visible = false;
                }
            } catch (e) {
                console.warn("[MaterializeLoader] start failed:", e);
            }
        }

        // Display rotating quotes for the character being loaded
        // Display rotating random quotes for the character being loaded
        if (loadingQuoteContainer && modelId) {
            console.log("Setting up quote rotation for character:", modelId);
            import("./characterData.js")
                .then((module) => {
                    const characterData = module.characterData;
                    const character = characterData.getCharacterById(modelId);

                    if (
                        !character ||
                        !character.quotes ||
                        character.quotes.length === 0
                    ) {
                        loadingQuoteContainer.style.display = "none";
                        console.log("No quotes found for character:", modelId);
                        return;
                    }

                    const quotes = character.quotes;
                    let currentQuoteIndex = Math.floor(
                        Math.random() * quotes.length,
                    ); // random start

                    // Helper: pick a new random quote (not the same as last one)
                    const getNextQuoteIndex = () => {
                        if (quotes.length < 2) return currentQuoteIndex;
                        let nextIndex;
                        do {
                            nextIndex = Math.floor(
                                Math.random() * quotes.length,
                            );
                        } while (nextIndex === currentQuoteIndex);
                        return nextIndex;
                    };

                    // Helper: fade and update quote
                    const displayQuote = (index) => {
                        const quote = quotes[index];
                        if (!loadingQuote || !loadingQuoteAuthor) return;

                        loadingQuoteContainer.style.transition =
                            "opacity 0.5s ease";
                        loadingQuoteContainer.style.opacity = "0";

                        setTimeout(() => {
                            loadingQuote.textContent = `"${quote}"`;
                            loadingQuoteAuthor.textContent = `— ${character.name}`;
                            loadingQuoteContainer.style.display = "block";
                            loadingQuoteContainer.style.opacity = "1";
                            try {
                                window.MaterializeLoader?.setQuote(
                                    quote,
                                    character.name,
                                    materializeToken,
                                );
                            } catch (_) {}
                            console.log(
                                `Displayed quote ${index + 1}/${quotes.length}: "${quote}"`,
                            );
                        }, 500);
                    };

                    // Clear previous timer if any
                    if (this._activeQuoteTimer) {
                        clearInterval(this._activeQuoteTimer);
                        this._activeQuoteTimer = null;
                    }

                    // Display the first random quote immediately
                    displayQuote(currentQuoteIndex);

                    // Start rotation every 10 seconds
                    this._activeQuoteTimer = setInterval(() => {
                        currentQuoteIndex = getNextQuoteIndex();
                        displayQuote(currentQuoteIndex);
                    }, 10000);

                    console.log(
                        `Quote rotation started for ${character.name} (every 10s)`,
                    );
                })
                .catch((error) => {
                    console.error("Error loading character quotes:", error);
                    if (loadingQuoteContainer)
                        loadingQuoteContainer.style.display = "none";
                });
        }

        // Hide microphone button during loading and show stop button
        if (window.chatController) {
            if (window.chatController.micButton) {
                window.chatController.micButton.classList.add("d-none");
                console.log("Microphone button hidden during model loading");
            }
            if (window.chatController.stopAudioButton) {
                window.chatController.stopAudioButton.classList.remove(
                    "d-none",
                );
                console.log("Stop button shown during model loading");
            }
        }

        try {
            if (previousSelectedLoad) {
                try {
                    await previousSelectedLoad;
                } catch (_) {
                    // The queue advances even if the prior character failed.
                }
                if (this._materializeLoadToken !== materializeToken) {
                    return;
                }
            }

            try {
                window.MaterializeLoader?.setPhase(
                    "connecting",
                    materializeToken,
                );
            } catch (_) {}

            // Ensure we have a model manager instance
            if (!this.modelManager) {
                this.modelManager = new ModelManager();
            }

            // Set the currentModelKey and currentModelId at the start of loading
            this.currentModelKey = modelId;
            this.currentModelId = modelId;
            console.log("Setting current model key and ID:", modelId);

            // Also set it in the material manager to handle hair colors
            if (this.materialManager) {
                this.materialManager.setCurrentModelId(modelId);
            } else {
                console.warn(
                    "MaterialManager not initialized yet, skipping setCurrentModelId",
                );
            }

            // setCurrentModel() drives the in-page state change; the server
            // pointer is synced fire-and-forget by ModelManager.initialize()
            // on page load, so we don't await an /api/models/current POST
            // before kicking off the model fetch.
            await this.modelManager.setCurrentModel(modelId, false);

            if (loadingText)
                loadingText.textContent = "Connecting to character database...";
            console.log("Loading model through server proxy for:", modelId);
            if (progressBar) progressBar.style.width = "25%";

            // 🔧 IMPORTANT: clean up BEFORE starting a new quote rotation
            this.cleanupCurrentModel();

            // 🔐 Token to ensure only the newest load controls the timer
            const loadToken = Symbol("quote-rotation");
            this._quoteLoadToken = loadToken;

            // ✅ Start quote rotation AFTER cleanup so it won't be cleared
            if (loadingQuoteContainer && modelId) {
                try {
                    const { characterData } = await import(
                        "./characterData.js"
                    );
                    const character = characterData.getCharacterById(modelId);

                    if (character?.quotes?.length > 0) {
                        const quotes = character.quotes;

                        // one canonical timer
                        if (this._activeQuoteTimer) {
                            clearInterval(this._activeQuoteTimer);
                            this._activeQuoteTimer = null;
                        }

                        // start from a random quote, avoid immediate repeat
                        let currentIndex = Math.floor(
                            Math.random() * quotes.length,
                        );

                        const nextIndex = () => {
                            if (quotes.length < 2) return currentIndex;
                            let i;
                            do {
                                i = Math.floor(Math.random() * quotes.length);
                            } while (i === currentIndex);
                            return i;
                        };

                        const displayQuote = (index) => {
                            if (this._quoteLoadToken !== loadToken) return; // a newer load started
                            if (!loadingQuote || !loadingQuoteAuthor) return;

                            loadingQuoteContainer.style.transition =
                                "opacity 0.3s ease";
                            loadingQuoteContainer.style.opacity = "0";

                            setTimeout(() => {
                                if (this._quoteLoadToken !== loadToken) return;
                                loadingQuote.textContent = `"${quotes[index]}"`;
                                loadingQuoteAuthor.textContent = `— ${character.name}`;
                                loadingQuoteContainer.style.display = "block";
                                loadingQuoteContainer.style.opacity = "1";
                                try {
                                    window.MaterializeLoader?.setQuote(
                                        quotes[index],
                                        character.name,
                                        materializeToken,
                                    );
                                } catch (_) {}
                                // console.debug('[quotes] shown index', index);
                            }, 300);
                        };

                        // show first quote immediately
                        displayQuote(currentIndex);

                        // rotate every 10s
                        this._activeQuoteTimer = setInterval(() => {
                            if (this._quoteLoadToken !== loadToken) {
                                clearInterval(this._activeQuoteTimer);
                                this._activeQuoteTimer = null;
                                return;
                            }
                            currentIndex = nextIndex();
                            displayQuote(currentIndex);
                        }, 10000);

                        console.log("Quote rotation started (10s interval)");
                    } else {
                        loadingQuoteContainer.style.display = "none";
                    }
                } catch (err) {
                    console.error("Error loading character quotes:", err);
                    loadingQuoteContainer.style.display = "none";
                }
            }

            // Determine model format (GLB or FBX) from characterData
            const modelFormat = characterData.getModelFormat
                ? characterData.getModelFormat(modelId)
                : "fbx";
            this.currentModelFormat = modelFormat;
            console.log(`Model format for ${modelId}: ${modelFormat}`);

            // proceed with model download after quotes are set up
            const modelUrl = `/api/stream_model/${modelId}`;
            console.log("Loading model through server proxy:", modelUrl);
            if (loadingText)
                loadingText.textContent = "Downloading character model...";
            if (progressBar) progressBar.style.width = "50%";
            try {
                window.MaterializeLoader?.setPhase(
                    "downloading",
                    materializeToken,
                );
                window.MaterializeLoader?.setProgress(0, materializeToken);
            } catch (_) {}

            const didLoad = await this.loadModel(modelUrl, materializeToken);
            if (
                !didLoad ||
                this._materializeLoadToken !== materializeToken
            ) {
                return;
            }
            console.log("Model loaded successfully, ID:", this.currentModelKey);
        } finally {
            // Don't clear the quote timer here - let it run during loading
            // It will be cleared by cleanupCurrentModel() when a new model is loaded

            if (loadingOverlay) loadingOverlay.style.display = "none";

            // If the CURRENT load finished/errored without the model ever
            // becoming visible (reveal never fired), tear its title card down
            // so it cannot linger forever. Never let an old load clear a newer
            // load's pending reveal.
            if (
                this._pendingReveal &&
                this._materializeLoadToken === materializeToken
            ) {
                this._pendingReveal = false;
                try {
                    window.MaterializeLoader?.hide(materializeToken);
                } catch (_) {}
                if (this.particleSystem?.particleSystem) {
                    this.particleSystem.particleSystem.visible = true;
                }
            }

            // Final check to maintain proper panel state after loading completes
            const panel = document.getElementById("character-panel");
            if (panel) {
                if (!modelId) {
                    // No character FBX - final check to ensure panel stays OPEN
                    console.log(
                        "Final check - no character FBX - panel stays OPEN",
                    );
                    panel.classList.add("panel-visible");
                    window.isPanelVisible = true;

                    // Add a stronger enforcement for panel visibility when no FBX is loaded
                    // using a delay to ensure all other operations have completed
                    setTimeout(() => {
                        if (!this.currentModelKey && !this.currentModelId) {
                            console.log(
                                "Extra enforcement - ensuring panel stays OPEN without FBX",
                            );
                            panel.classList.add("panel-visible");
                            window.isPanelVisible = true;
                            localStorage.setItem(
                                "keepCharacterPanelOpen",
                                "true",
                            );
                        }
                    }, 500);
                } else {
                    // Character FBX loaded - final check to ensure panel stays CLOSED
                    console.log(
                        "Final check - character FBX loaded - panel stays CLOSED",
                    );
                    panel.classList.remove("panel-visible");
                    window.isPanelVisible = false;
                }
            }

            if (releaseSelectedLoad) {
                releaseSelectedLoad();
                if (this._selectedModelLoadTail === selectedLoadSlot) {
                    this._selectedModelLoadTail = null;
                }
            }
        }
    }

    cleanupCurrentModel() {
        this.lessonReactionController?.cancel?.();
        // Clean up quote rotation timer if active
        if (this._activeQuoteTimer) {
            clearInterval(this._activeQuoteTimer);
            this._activeQuoteTimer = null;
        }

        if (this.mixer) {
            this.mixer.stopAllAction();
            this.mixer = null;
        }

        if (this.model) {
            this.model.traverse((child) => {
                if (child.isMesh) {
                    child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach((material) =>
                                material.dispose(),
                            );
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            });
            this.scene.remove(this.model);
            this.model = null;
        }

        this.animationController = null;
        this.lipSyncController = null;
        this.blinkController = null;
        this.eyeTrackingController = null;
        if (this.idleSmileController) {
            try {
                this.idleSmileController.dispose();
            } catch (_) {}
        }
        this.idleSmileController = null;
        this.blendShapeControls = {};

        // Cleanup particle system
        if (this.particleSystem) {
            this.particleSystem.dispose();
            this.particleSystem = null;
        }

        // Cleanup teeth material manager
        if (this.teethMaterialManager) {
            this.teethMaterialManager.dispose();
            this.teethMaterialManager = null;
        }

        // Cleanup mouth material manager
        if (this.mouthMaterialManager) {
            this.mouthMaterialManager.dispose();
            this.mouthMaterialManager = null;
        }
    }

    /**
     * Task #199: returns true while a character-picker overlay (the Hall or the
     * collapsed selector) is up. Used to hold the model reveal + the opening
     * greeting until the stage is clear, so a cold visitor never sees a
     * half-transparent character behind the cards or an audio prompt over them.
     */
    _isPickerOpen() {
        const b = document.body;
        return (
            !!b &&
            (b.classList.contains("hall-open") ||
                b.classList.contains("selector-open"))
        );
    }

    /**
     * Task #199: reveal the freshly-loaded model only once no picker overlay is
     * open. If a picker is up, keep the model invisible and watch the body
     * classes; the instant the picker closes (legend committed / Hall closed),
     * show the model fully solid and cut the editorial loader away. Reveals
     * immediately when the stage is already clear.
     */
    /**
     * Monotonic, token-guarded progress writes to the editorial loader.
     * All setProgress traffic funnels through here so the reveal's "100"
     * (fired the moment the character shows) can never be dragged
     * backwards by a later, slower milestone write from loadModel.
     */
    _materializeProgress(token, value) {
        if (token == null || this._materializeLoadToken !== token) return;
        if (
            this._mlProgressHigh &&
            this._mlProgressHigh.token === token &&
            value <= this._mlProgressHigh.value
        ) {
            return;
        }
        this._mlProgressHigh = { token, value };
        try {
            window.MaterializeLoader?.setProgress?.(value, token);
        } catch (_) {}
    }

    _revealModelWhenStageClear() {
        const materializeToken = this._materializeLoadToken;
        const doReveal = () => {
            if (
                !this.model ||
                this.model !== model ||
                this._materializeLoadToken !== materializeToken
            ) {
                return;
            }
            // Task #340 (founder ruling 2026-08-11): no character ever
            // starts at opacity 0. The old dissolve-in (zero every material,
            // tween 0→1) is structurally removed — the model shows fully
            // solid on the very frame it becomes visible; stamp + settle
            // enforce the authored state instead of trusting any effect.
            try {
                this._showModelSolid(model);
            } catch (e) {
                console.warn("[solid-start] settle failed:", e);
            }
            this.model.visible = true;
            // The dominant "100" lands on the same frame as the character:
            // download maps to 0–85 and finalize milestones fill 85–99, so
            // the meter never sits at 100 over an empty stage (Task #552
            // follow-on, founder: "100% should mean the character appears").
            this._materializeProgress?.(materializeToken, 100);
            // Honest ready signal: cut the editorial loader away
            // only after the mesh has actually PAINTED (two rAFs = at least
            // one full rendered frame with the model visible), never over a
            // legend that isn't on screen yet. The setTimeout fallback keeps
            // hidden-tab loads from wedging (rAF is throttled there).
            let fired = false;
            const fire = () => {
                if (fired) return;
                // A character can be switched during the two-frame paint
                // gate. In that case this delayed cleanup belongs to the old
                // load and must not restore the new scene's particles or
                // dismiss its loader.
                if (
                    this.model !== model ||
                    this._materializeLoadToken !== materializeToken
                ) {
                    return;
                }
                fired = true;
                try {
                    // Keep WebGL scene particles hidden for the entire wait,
                    // then restore them on the same hard-cut handoff.
                    if (this.particleSystem?.particleSystem) {
                        this.particleSystem.particleSystem.visible = true;
                    }
                    window.MaterializeLoader?.reveal(materializeToken);
                } catch (e) {
                    console.warn("[MaterializeLoader] reveal failed:", e);
                }
            };
            try {
                requestAnimationFrame(() => requestAnimationFrame(fire));
                setTimeout(fire, 1500);
            } catch (e) {
                fire();
            }
        };
        // Snapshot the model so a mid-wait character switch can't reveal a
        // stale figure.
        const model = this.model;

        if (!this._isPickerOpen()) {
            doReveal();
            return;
        }

        // Hold the model hidden behind the open picker. The editorial loader
        // is already CSS-suppressed while a picker is open, so leave it intact
        // and perform the normal hard cut after the picker closes.
        if (this.model) this.model.visible = false;

        const obs = new MutationObserver(() => {
            if (this._isPickerOpen()) return;
            obs.disconnect();
            doReveal();
        });
        try {
            obs.observe(document.body, {
                attributes: true,
                attributeFilter: ["class"],
            });
        } catch (e) {
            // If observation can't be set up for any reason, fail open and
            // reveal so the model is never permanently hidden.
            doReveal();
        }
    }

    /**
     * Best-effort GPU warm-up for a freshly-parsed (still hidden) model:
     * compile shader programs and upload textures BEFORE the reveal cut so
     * the character's first visible frame doesn't stall (Task #552
     * follow-on — the founder's post-100% "stage only" gap). Never throws;
     * aborts silently when the model is superseded mid-warm. Texture
     * uploads yield between pairs so the finalizing card stays fluid.
     */
    async _warmGpuForModel(model) {
        if (!model || !this.renderer || !this.scene || !this.camera) return;
        const t0 = typeof performance !== "undefined" ? performance.now() : 0;
        try {
            if (typeof this.renderer.compileAsync === "function") {
                await this.renderer.compileAsync(
                    model,
                    this.camera,
                    this.scene,
                );
            } else if (typeof this.renderer.compile === "function") {
                this.renderer.compile(model, this.camera, this.scene);
            }
        } catch (_) {
            /* warm-up is best-effort; first paint compiles as before */
        }
        if (this.model !== model) return;
        const textures = [];
        const seen = new Set();
        const MAP_SLOTS = [
            "map",
            "normalMap",
            "roughnessMap",
            "metalnessMap",
            "aoMap",
            "emissiveMap",
            "alphaMap",
            "specularColorMap",
            "specularIntensityMap",
            "clearcoatMap",
            "clearcoatNormalMap",
            "clearcoatRoughnessMap",
            "sheenColorMap",
            "sheenRoughnessMap",
            "transmissionMap",
            "thicknessMap",
        ];
        try {
            model.traverse((child) => {
                if (!child.isMesh) return;
                const mats = Array.isArray(child.material)
                    ? child.material
                    : [child.material];
                for (const m of mats) {
                    if (!m) continue;
                    for (const slot of MAP_SLOTS) {
                        const t = m[slot];
                        if (t && t.isTexture && !seen.has(t)) {
                            seen.add(t);
                            textures.push(t);
                        }
                    }
                }
            });
        } catch (_) {
            return;
        }
        for (let i = 0; i < textures.length; i++) {
            if (this.model !== model) return;
            try {
                this.renderer.initTexture(textures[i]);
            } catch (_) {}
            if ((i & 1) === 1) {
                await new Promise((r) => setTimeout(r, 0));
            }
        }
        if (t0) {
            console.log(
                `[GpuWarm] shaders + ${textures.length} textures warmed in ` +
                    `${Math.round(performance.now() - t0)}ms (pre-reveal)`,
            );
        }
    }

    /**
     * Authored-blend stamp (Task #331, retained by #340 as the permanent
     * safety net): record a material's AUTHORED blend state exactly once
     * (m.userData.__authoredBlend). Every later settle reads this stamp
     * instead of the live values, so no stray writer can ever get a zeroed
     * opacity accepted as the "original", and material clones (e.g. the
     * physical->standard shader downgrade copies materials, and
     * Material.copy deep-copies userData) inherit the stamp and stay
     * healable.
     *
     * CONTRACT: __authoredBlend is the source of truth for a material's
     * at-rest blend state. Any code that wants a PERSISTENT change to an
     * already-stamped material's opacity/transparent/depthWrite must also
     * update the stamp, or the next settle will lawfully revert it.
     */
    _stampAuthoredBlend(m) {
        if (!m) return null;
        if (!m.userData) m.userData = {};
        if (!m.userData.__authoredBlend) {
            m.userData.__authoredBlend = {
                opacity: typeof m.opacity === "number" ? m.opacity : 1,
                transparent: m.transparent === true,
                depthWrite: m.depthWrite !== false,
            };
        }
        return m.userData.__authoredBlend;
    }

    /** Force one material back to its authored blend state (no-op if it
     *  was never stamped). */
    _restoreAuthoredBlend(m) {
        const a = m && m.userData && m.userData.__authoredBlend;
        if (!a) return;
        if (
            m.opacity !== a.opacity ||
            m.transparent !== a.transparent ||
            m.depthWrite !== a.depthWrite
        ) {
            m.opacity = a.opacity;
            m.transparent = a.transparent;
            m.depthWrite = a.depthWrite;
            m.needsUpdate = true;
        }
    }

    /**
     * Invisible-mesh fix (Task #331, kept by #340): walk `model` and force
     * every stamped material back to its authored
     * opacity/transparent/depthWrite. Idempotent and cheap; safe to call
     * from any completion, interruption, or model-ready path. The authored
     * state is ENFORCED — never trusted to any effect having run.
     */
    _settleAuthoredMaterials(model) {
        if (!model) return;
        try {
            model.traverse((child) => {
                if (!child.isMesh && !child.isSkinnedMesh) return;
                const mats = Array.isArray(child.material)
                    ? child.material
                    : [child.material];
                mats.forEach((m) => this._restoreAuthoredBlend(m));
            });
        } catch (e) {
            console.warn("[solid-start] authored-material settle failed:", e);
        }
    }

    /**
     * Task #340: stamp the authored blend state of every material on
     * `model`. One-time per material and idempotent per model — safe to
     * call again whenever materials may have been created or swapped.
     * Called at load so the settle safety net always has its source of
     * truth; because NOTHING may zero character material opacity at load
     * anymore, what gets stamped is the fully-solid authored state
     * (including any transmission-glass approximation the physical→
     * standard downgrade path applied).
     */
    _stampModelAuthoredState(model) {
        if (!model) return;
        try {
            model.traverse((child) => {
                if (!child.isMesh && !child.isSkinnedMesh) return;
                const mats = Array.isArray(child.material)
                    ? child.material
                    : [child.material];
                mats.forEach((m) => m && this._stampAuthoredBlend(m));
            });
        } catch (e) {
            console.warn("[solid-start] authored-material stamp failed:", e);
        }
    }

    /**
     * Task #340 (founder ruling 2026-08-11, supersedes the Task #79
     * dissolve-in): the character appears FULLY SOLID on the very frame
     * the model is ready — a hard cut from the particle silhouette to the
     * real character. The old _dissolveInModel() (zero every material's
     * opacity, tween 0→1 over ~900ms) was the root of the entire
     * stuck-at-opacity-0 bug family — interrupted tweens, overlapping
     * fades, downgrade clones born invisible — and is structurally
     * removed. No code path may set character material opacity to 0 at
     * load again.
     *
     * Implementation: stamp (idempotent — records authored state for any
     * material not stamped at load yet) then settle (pure enforcement —
     * heals any stray transparency back to the authored stamp). Never
     * zeroes, never tweens, nothing to interrupt or trust.
     */
    _showModelSolid(model) {
        if (!model) return;
        this._stampModelAuthoredState(model);
        this._settleAuthoredMaterials(model);
    }

    loadCameraConfig() {
        const savedConfig = localStorage.getItem("cameraConfig");
        if (savedConfig) {
            try {
                this.cameraConfig = JSON.parse(savedConfig);
                console.log("Loaded camera config:", this.cameraConfig);
            } catch (e) {
                console.error("Error loading camera config:", e);
                this.cameraConfig = { ...this.defaultCameraConfig };
            }
        } else {
            this.cameraConfig = { ...this.defaultCameraConfig };
        }
    }

    saveCameraConfig() {
        const config = {
            position: {
                x: this.camera.position.x,
                y: this.camera.position.y,
                z: this.camera.position.z,
            },
            rotation: {
                x: this.camera.rotation.x,
                y: this.camera.rotation.y,
                z: this.camera.rotation.z,
            },
            target: {
                x: this.controls.target.x,
                y: this.controls.target.y,
                z: this.controls.target.z,
            },
        };
        localStorage.setItem("cameraConfig", JSON.stringify(config));
        this.cameraConfig = config;
        this.updateCameraInfo();
        console.log("Saved camera config:", config);
    }

    setupCameraControls() {
        const lockButton = document.getElementById("lock-camera");
        const resetButton = document.getElementById("reset-camera");
        const toggleOrbitButton = document.getElementById("toggle-orbit");
        const cameraInfo = document.getElementById("camera-info");

        // Initialize the camera animator
        this.cameraAnimator = new CameraAnimator(this.camera, this.controls);
        console.log("Camera animator initialized");

        if (!lockButton || !resetButton || !toggleOrbitButton) {
            console.warn("Camera control buttons not found in the DOM");
            // Still return because we've initialized the camera animator
        }

        if (lockButton) {
            lockButton.addEventListener("click", () => {
                this.saveCameraConfig();
                if (cameraInfo) {
                    cameraInfo.classList.remove("d-none");
                }
            });
        }

        if (resetButton) {
            resetButton.addEventListener("click", () => {
                this.resetCamera();
                if (cameraInfo) {
                    cameraInfo.classList.add("d-none");
                }
            });
        }

        if (toggleOrbitButton) {
            toggleOrbitButton.addEventListener("click", () => {
                this.controls.enabled = !this.controls.enabled;
                toggleOrbitButton.textContent = this.controls.enabled
                    ? "Disable Orbit"
                    : "Enable Orbit";
            });
        }
    }

    updateCameraInfo() {
        const positionEl = document.getElementById("camera-position");
        const rotationEl = document.getElementById("camera-rotation");
        const targetEl = document.getElementById("camera-target");

        if (positionEl) {
            positionEl.textContent = `(${this.camera.position.x.toFixed(2)}, ${this.camera.position.y.toFixed(2)}, ${this.camera.position.z.toFixed(2)})`;
        }
        if (rotationEl) {
            rotationEl.textContent = `(${this.camera.rotation.x.toFixed(2)}, ${this.camera.rotation.y.toFixed(2)}, ${this.camera.rotation.z.toFixed(2)})`;
        }
        if (targetEl && this.controls) {
            targetEl.textContent = `(${this.controls.target.x.toFixed(2)}, ${this.controls.target.y.toFixed(2)}, ${this.controls.target.z.toFixed(2)})`;
        }
    }

    resetCamera() {
        this.camera.position.set(
            this.defaultCameraConfig.position.x,
            this.defaultCameraConfig.position.y,
            this.defaultCameraConfig.position.z,
        );
        this.controls.target.set(
            this.defaultCameraConfig.target.x,
            this.defaultCameraConfig.target.y,
            this.defaultCameraConfig.target.z,
        );
        this.camera.rotation.set(
            this.defaultCameraConfig.rotation.x,
            this.defaultCameraConfig.rotation.y,
            this.defaultCameraConfig.rotation.z,
        );
        this.controls.update();
        localStorage.removeItem("cameraConfig");
    }

    init() {
        // Initialize panel visibility right at startup
        // This happens BEFORE any model loading
        const panel = document.getElementById("character-panel");
        const noModelSelected = !localStorage.getItem("selectedModelId");

        if (panel && noModelSelected) {
            console.log(
                "INIT: No character model selected - panel should stay OPEN",
            );
            panel.classList.add("panel-visible");
            window.isPanelVisible = true;
            localStorage.setItem("keepCharacterPanelOpen", "true");

            // Add a style tag to reinforce this behavior
            const styleTag = document.createElement("style");
            styleTag.id = "character-panel-force-style";
            styleTag.textContent = `
                /* Force character panel visible when no FBX loaded */
                #character-panel.panel-visible {
                    transform: translateX(0) !important;
                }
            `;
            document.head.appendChild(styleTag);
        }

        // Mobile performance: lighter renderer settings
        if (this.isMobile) {
            this.renderer.shadowMap.enabled = false; // huge win on mobile
            this.renderer.toneMapping = THREE.NoToneMapping;
            this.renderer.toneMappingExposure = 1.0;
            this.renderer.physicallyCorrectLights = false;
        } else {
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
            this.renderer.toneMappingExposure = 1.2; // Increased for more vibrancy
            this.renderer.physicallyCorrectLights = true;
        }

        // Capture the exact per-device "classic" renderer defaults so the
        // opt-in Studio lighting mode (setLightingMode) can restore them
        // bit-for-bit when switching back (desktop: ACES @ 1.2, mobile:
        // NoToneMapping @ 1.0).
        this._classicRenderDefaults = {
            toneMapping: this.renderer.toneMapping,
            toneMappingExposure: this.renderer.toneMappingExposure,
        };
        if (this._lightingMode === "studio") {
            // Defensive: if init() ever re-runs while Studio is active,
            // re-assert its renderer settings over the classic block above.
            this._applyStudioRenderSettings();
        }

        // Enhanced renderer configuration for vibrant realism
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;

        this.camera.position.set(
            this.cameraConfig.position.x,
            this.cameraConfig.position.y,
            this.cameraConfig.position.z,
        );
        this.camera.lookAt(
            this.cameraConfig.target.x,
            this.cameraConfig.target.y,
            this.cameraConfig.target.z,
        );

        // Enable camera to see both regular objects (layer 0) and sky dome (layer 31)
        this.camera.layers.enable(0);
        this.camera.layers.enable(31);

        this.controls = new OrbitControls(this.camera, this.canvas);
        this.controls.target.set(
            this.cameraConfig.target.x,
            this.cameraConfig.target.y,
            this.cameraConfig.target.z,
        );

        // Zoom bounds are derived from the standard face framing:
        // camera target is on the eye line (Y=180) and FOV is 50° so a
        // ~25-unit face fits the viewport vertically at D≈30. We pad
        // that to keep the entire face — including hair/hat — comfortably
        // inside the frame even at maximum zoom-in. Max distance is
        // generous so users can pull back to see the full character.
        if (this.isMobile) {
            this.controls.enableDamping = false; // avoids extra math each frame
            this.controls.dampingFactor = 0.05;
            this.controls.enablePan = false;
            this.controls.autoRotate = false;
            this.controls.minDistance = 70;
            this.controls.maxDistance = 240;
        } else {
            this.controls.enableDamping = true;
            this.controls.dampingFactor = 0.05;
            this.controls.enablePan = false;
            this.controls.autoRotate = false;
            this.controls.minDistance = 55;
            this.controls.maxDistance = 350;
        }

        // Face-centered zoom state and OrbitControls listener. Runs
        // on BOTH mobile and desktop so pinch-zoom on touch devices
        // gets the same smooth target-lerp toward the face anchor
        // and the same camera-position safety clamp.
        const baselineCameraY = this.camera.position.y;
        const baselineTargetY = this.controls.target.y;
        this._zoomState = {
            isExtremeZoom: false,
            baselineCameraY: baselineCameraY,
            baselineTargetY: baselineTargetY,
            isAnimating: false,
            // Zoom distance thresholds; rebased per-character in
            // _applyEyeBasedFraming() to match face-derived
            // distanceDefault / distanceClose so the lerp tracks
            // the actual zoom band for short vs tall characters.
            normalDistance: 95,
            closeDistance: 60,
            extremeDistance: 55,
            // Face-centered zoom targets. As the user zooms in,
            // the orbit target lerps from `framingTarget` (the
            // head-top-anchored talking-frame target from
            // EyeFraming) toward `faceTarget` (eye midpoint
            // shifted slightly down) so the face slides to the
            // visual center of the viewport. Both vectors are
            // refreshed every framing pass; if absent the lerp
            // is a no-op and the target stays put.
            framingTarget: new THREE.Vector3(),
            faceTarget: new THREE.Vector3(),
            hasTargets: false,
            currentLerpT: 0,
            targetLerpT: 0,
            lastLoggedLerpT: 0,
            lastClampWasActive: false,
            lastRadius: null,
        };

        this.controls.addEventListener("change", () => {
            // Lesson framing owns a fixed full-body camera. Portrait zoom
            // interpolation is for interactive /lab orbiting only; allowing
            // this listener to schedule it would restore the portrait target
            // on the next rAF after a lesson resize/refit.
            if (this.fullBodyFit?.policy === "lesson") return;
            // Camera-position safety guard. The clamp distance is
            // measured against the FACE anchor (faceTarget), not
            // the moving orbit target, so the camera physically
            // can't enter the head/chest mesh even when the orbit
            // target has lerped most of the way toward the face.
            // The push-back direction stays along the current
            // orbit ray (camera - controls.target) so the user's
            // viewing angle is preserved.
            const dxT = this.camera.position.x - this.controls.target.x;
            const dyT = this.camera.position.y - this.controls.target.y;
            const dzT = this.camera.position.z - this.controls.target.z;
            const radius = Math.sqrt(dxT * dxT + dyT * dyT + dzT * dzT);

            let clampActive = false;
            const minSafe = this._zoomState.closeDistance;
            if (this._zoomState.hasTargets && Number.isFinite(minSafe)) {
                const fx = this._zoomState.faceTarget.x;
                const fy = this._zoomState.faceTarget.y;
                const fz = this._zoomState.faceTarget.z;
                const dxF = this.camera.position.x - fx;
                const dyF = this.camera.position.y - fy;
                const dzF = this.camera.position.z - fz;
                const distFace = Math.sqrt(
                    dxF * dxF + dyF * dyF + dzF * dzF,
                );
                if (distFace > 1e-3 && distFace < minSafe) {
                    // Move outward along the current orbit ray, by
                    // the distance shortfall measured to the face.
                    const shortfall = minSafe - distFace;
                    const rLen = Math.max(radius, 1e-3);
                    const ux = dxT / rLen;
                    const uy = dyT / rLen;
                    const uz = dzT / rLen;
                    this.camera.position.set(
                        this.camera.position.x + ux * shortfall,
                        this.camera.position.y + uy * shortfall,
                        this.camera.position.z + uz * shortfall,
                    );
                    clampActive = true;
                }
            }

            // Recompute radius after clamp so the lerp factor and
            // log reflect the post-clamp state.
            const dx2 = this.camera.position.x - this.controls.target.x;
            const dy2 = this.camera.position.y - this.controls.target.y;
            const dz2 = this.camera.position.z - this.controls.target.z;
            const radiusPost = Math.sqrt(dx2 * dx2 + dy2 * dy2 + dz2 * dz2);

            if (this._zoomState.lastRadius === null) {
                this._zoomState.lastRadius = radiusPost;
            }
            const radiusDelta = Math.abs(
                radiusPost - this._zoomState.lastRadius,
            );
            this._zoomState.lastRadius = radiusPost;

            if (radiusDelta < 0.5 && !clampActive) return;

            const newTargetLerpT = this._calculateFaceLerp(radiusPost);
            this._zoomState.targetLerpT = newTargetLerpT;
            this._startZoomOffsetAnimation();

            const wasExtremeZoom = this._zoomState.isExtremeZoom;
            const isNowExtremeZoom =
                radiusPost <= this._zoomState.extremeDistance;

            if (wasExtremeZoom !== isNowExtremeZoom) {
                this._zoomState.isExtremeZoom = isNowExtremeZoom;
                this._adjustChatForExtremeZoom(isNowExtremeZoom);
            }

            const lerpDelta = Math.abs(
                newTargetLerpT - this._zoomState.lastLoggedLerpT,
            );
            if (
                lerpDelta > 0.05
                || clampActive !== this._zoomState.lastClampWasActive
            ) {
                this._zoomState.lastLoggedLerpT = newTargetLerpT;
                this._zoomState.lastClampWasActive = clampActive;
                console.log(
                    `[Zoom] dist=${radiusPost.toFixed(1)} ` +
                        `closeD=${this._zoomState.closeDistance.toFixed(1)} ` +
                        `normalD=${this._zoomState.normalDistance.toFixed(1)} ` +
                        `lerp=${newTargetLerpT.toFixed(2)} ` +
                        `clamp=${clampActive ? "Y" : "N"}`,
                );
            }
        });

        // Tight polar limits keep the face inside the viewport: ±27°
        // from horizontal eye-line. Anything beyond starts cropping the
        // forehead or chin at typical conversational distance.
        this.controls.maxPolarAngle = Math.PI * 0.65;
        this.controls.minPolarAngle = Math.PI * 0.35;

        this.controls.enableZoom = true;

        // Apply the saved click-drag "feel" (damping/inertia + strength).
        // Defaults to "crisp" until the user picks one via the in-menu
        // switcher. No-op on mobile damping (kept off for direct touch).
        let _savedFeel = "crisp";
        try {
            _savedFeel = localStorage.getItem("cameraFeel") || "crisp";
        } catch (e) {}
        this.setCameraFeel(_savedFeel, false);

        this.controls.update();
    }

    /**
     * Set the click-and-drag camera "feel" (desktop). Each preset tunes how
     * tightly the view tracks the cursor and how it settles on release:
     *   - crisp:     near-instant stop, minimal glide, slightly stronger turn.
     *   - cinematic: tracks tight, glides to a soft weighted stop.
     *   - flick:     low damping so a quick flick keeps spinning and settles.
     * Relies on the per-frame controls.update() in animate() to drain damping.
     * @param {string} name preset key (crisp|cinematic|flick)
     * @param {boolean} persist save to localStorage (default true)
     */
    setCameraFeel(name, persist = true) {
        if (!this.controls) return null;
        const FEELS = {
            crisp: { dampingFactor: 0.25, rotateSpeed: 1.05 },
            cinematic: { dampingFactor: 0.09, rotateSpeed: 1.0 },
            flick: { dampingFactor: 0.045, rotateSpeed: 0.95 },
        };
        const key = FEELS[name] ? name : "crisp";
        const feel = FEELS[key];

        // On desktop, damping is drained per-frame in animate(); on mobile we
        // keep damping OFF for direct 1:1 touch tracking, so only set strength.
        if (!this.isMobile) {
            this.controls.enableDamping = true;
            this.controls.dampingFactor = feel.dampingFactor;
        }
        this.controls.rotateSpeed = feel.rotateSpeed;
        this._cameraFeel = key;

        if (persist) {
            try {
                localStorage.setItem("cameraFeel", key);
            } catch (e) {}
        }
        try {
            window.dispatchEvent(
                new CustomEvent("camera-feel-changed", { detail: key }),
            );
        } catch (e) {}
        return key;
    }

    /**
     * Switch the character lighting between two looks (Task #552):
     *   - "classic": the analytic 6-light rig + per-device tone mapping —
     *     today's default look, byte-for-byte restored.
     *   - "studio":  the Tripo3D viewer look — pure HDRI image-based
     *     lighting (analytic rig hidden, zero contributing lights), ACES
     *     filmic tone mapping @ exposure 1.0.
     * The HDR environment is fetched lazily the first time Studio is chosen
     * and cached for the session. scene.background (the sky dome), the
     * camera, and model materials are never touched in either direction.
     * @param {string} name mode key ("classic" | "studio")
     * @param {boolean} persist save to localStorage (default true)
     */
    setLightingMode(name, persist = true) {
        const key = name === "studio" ? "studio" : "classic";
        this._lightingMode = key;
        if (persist) {
            try {
                localStorage.setItem("lightingMode", key);
            } catch (e) {}
        }
        if (key === "studio") {
            this._applyStudioLighting();
        } else {
            this._applyClassicLighting();
        }
        try {
            window.dispatchEvent(
                new CustomEvent("lighting-mode-changed", { detail: key }),
            );
        } catch (e) {}
        return key;
    }

    getLightingMode() {
        return this._lightingMode;
    }

    // Studio = IBL only. Loads the HDRI on first use (never before), then:
    // environment set, analytic rig hidden (visible=false — never removed),
    // ACES filmic @ exposure 1.0 per the Tripo spec.
    _applyStudioLighting() {
        if (!this.renderer || !this.scene) return; // limited / no-WebGL mode
        if (this._studioEnvTexture) {
            this._applyStudioEnvironment();
            return;
        }
        if (this._studioEnvLoading) return; // arrival handler re-checks mode
        this._studioEnvLoading = true;
        console.log("[LIGHTING] studio: loading HDRI", STUDIO_HDRI_URL);
        new HDRLoader().load(
            STUDIO_HDRI_URL,
            (texture) => {
                this._studioEnvLoading = false;
                texture.mapping = THREE.EquirectangularReflectionMapping;
                this._studioEnvTexture = texture;
                // Only apply if the user hasn't switched back mid-flight.
                if (this._lightingMode === "studio") {
                    this._applyStudioEnvironment();
                }
            },
            undefined,
            (err) => {
                this._studioEnvLoading = false; // allow retry on next click
                console.error("[LIGHTING] studio HDRI failed to load", err);
            },
        );
    }

    _applyStudioEnvironment() {
        const scene = this.scene;
        scene.environment = this._studioEnvTexture;
        scene.environmentIntensity = 1.0;
        scene.environmentRotation.set(
            0,
            THREE.MathUtils.degToRad(STUDIO_ENV_ROTATION_Y_DEG),
            0,
        );
        // scene.background is NOT touched: the sky dome stays the visible
        // backdrop and the HDRI lights the model without ever being seen.
        (this._analyticLights || []).forEach((light) => {
            light.visible = false;
        });
        this._applyStudioRenderSettings();
        console.log(
            "[LIGHTING] studio mode active (HDRI IBL, analytic rig hidden, " +
                `envRotY=${STUDIO_ENV_ROTATION_Y_DEG}deg, ACES @ 1.0)`,
        );
    }

    _applyStudioRenderSettings() {
        if (!this.renderer) return;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        // Already the app-wide default (set in init()), asserted here for
        // spec fidelity — Studio requires sRGB output.
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    }

    // Classic = restore EXACTLY what a user who never touched the toggle
    // has: environment cleared (constructor sets scene.environment = null),
    // whole rig visible, per-device tone mapping captured in init().
    _applyClassicLighting() {
        if (!this.renderer || !this.scene) return; // limited / no-WebGL mode
        this.scene.environment = null;
        this.scene.environmentIntensity = 1.0; // three.js default
        this.scene.environmentRotation.set(0, 0, 0);
        (this._analyticLights || []).forEach((light) => {
            light.visible = true;
        });
        const d = this._classicRenderDefaults;
        if (d) {
            this.renderer.toneMapping = d.toneMapping;
            this.renderer.toneMappingExposure = d.toneMappingExposure;
        }
        console.log("[LIGHTING] classic mode active (analytic rig restored)");
    }

    /**
     * Adjust chat window position for extreme zoom levels
     * When zooming in very close, moves the chat window down to avoid blocking the view
     * Stores the user's position before adjusting and restores it when zooming out
     * @param {boolean} isExtremeZoom - Whether we're in extreme zoom mode
     */
    _adjustChatForExtremeZoom(isExtremeZoom) {
        // Initialize state for tracking zoom-based chat adjustments
        if (!this._chatZoomState) {
            this._chatZoomState = {
                wasAdjustedForZoom: false,
                priorPosition: null,
            };
        }

        // Use the global chat drag controller if available.
        // The drawer-model controller no longer exposes getPosition/setPosition
        // (the chat is a fixed bottom drawer that doesn't move on zoom), so guard
        // against the old API to avoid a runtime crash on extreme zoom.
        if (
            window.chatDragController &&
            typeof window.chatDragController.getPosition === "function" &&
            typeof window.chatDragController.setPosition === "function"
        ) {
            if (isExtremeZoom) {
                // Move chat down when in extreme zoom
                const currentBottom = window.chatDragController.getPosition();

                // Only move down if not already at a low position and not already adjusted
                if (
                    currentBottom > 100 &&
                    !this._chatZoomState.wasAdjustedForZoom
                ) {
                    // Store the user's current position before adjusting
                    this._chatZoomState.priorPosition = currentBottom;
                    this._chatZoomState.wasAdjustedForZoom = true;

                    window.chatDragController.setPosition(80, true);
                    console.log(
                        "Chat window moved down for extreme zoom (was at:",
                        currentBottom,
                        ")",
                    );
                }
            } else {
                // Restore chat position when zooming out
                // Only restore if we previously moved it for zoom
                if (
                    this._chatZoomState.wasAdjustedForZoom &&
                    this._chatZoomState.priorPosition !== null
                ) {
                    window.chatDragController.setPosition(
                        this._chatZoomState.priorPosition,
                        true,
                    );
                    console.log(
                        "Chat window restored to prior position:",
                        this._chatZoomState.priorPosition,
                    );

                    // Reset tracking state
                    this._chatZoomState.wasAdjustedForZoom = false;
                    this._chatZoomState.priorPosition = null;
                }
            }
        }

        // Dispatch event for other systems to react
        window.dispatchEvent(
            new CustomEvent("zoomLevelChanged", {
                detail: {
                    isExtremeZoom: isExtremeZoom,
                    zoomState: this._zoomState,
                },
            }),
        );
    }

    /**
     * Calculate the face-target lerp factor for the current orbit
     * radius. 0 = orbit target sits at the talking-frame target
     * (head-top anchored, breathing room above). 1 = orbit target
     * sits at the face target (eye midpoint shifted slightly down,
     * face visually centered). The transition is a smoothstep over
     * the band [closeDistance .. normalDistance].
     *
     * @param {number} distance - camera-to-target radius
     * @returns {number} lerp factor in [0, 1]
     */
    _calculateFaceLerp(distance) {
        if (!this._zoomState) return 0;
        const { normalDistance, closeDistance } = this._zoomState;
        if (!Number.isFinite(normalDistance)
            || !Number.isFinite(closeDistance)
            || normalDistance <= closeDistance) {
            return 0;
        }
        if (distance >= normalDistance) return 0;
        if (distance <= closeDistance) return 1;
        const linear =
            (normalDistance - distance) / (normalDistance - closeDistance);
        // Smoothstep for a softer ease-in/out so the face glides
        // toward center rather than snapping mid-zoom.
        return linear * linear * (3 - 2 * linear);
    }

    /**
     * Start continuous animation loop to smoothly interpolate camera offset
     * Uses requestAnimationFrame to ensure smooth transitions even after user stops zooming
     */
    _startZoomOffsetAnimation() {
        if (this.fullBodyFit?.policy === "lesson") {
            if (this._zoomAnimationId != null) {
                cancelAnimationFrame(this._zoomAnimationId);
                this._zoomAnimationId = null;
            }
            if (this._zoomState) {
                this._zoomState.hasTargets = false;
                this._zoomState.isAnimating = false;
            }
            return;
        }
        if (!this._zoomState) return;
        if (!this._zoomState.hasTargets) return;

        // If animation is already running, it will pick up the new target
        if (this._zoomState.isAnimating) return;

        this._zoomState.isAnimating = true;

        const tmp = new THREE.Vector3();
        const animate = () => {
            if (!this._zoomState || !this._zoomState.hasTargets) {
                this._zoomState && (this._zoomState.isAnimating = false);
                this._zoomAnimationId = null;
                return;
            }

            const currentT = this._zoomState.currentLerpT;
            const targetT = this._zoomState.targetLerpT;
            const diff = targetT - currentT;

            const applyLerp = (t) => {
                tmp.lerpVectors(
                    this._zoomState.framingTarget,
                    this._zoomState.faceTarget,
                    Math.max(0, Math.min(1, t)),
                );
                this.controls.target.copy(tmp);
                this.controls.update();
            };

            if (Math.abs(diff) < 0.005) {
                this._zoomState.currentLerpT = targetT;
                applyLerp(targetT);
                this._zoomState.isAnimating = false;
                this._zoomAnimationId = null;
                return;
            }

            const smoothFactor = 0.15;
            const newT = currentT + diff * smoothFactor;
            this._zoomState.currentLerpT = newT;
            applyLerp(newT);

            this._zoomAnimationId = requestAnimationFrame(animate);
        };

        // Start the animation loop
        this._zoomAnimationId = requestAnimationFrame(animate);
    }

    // Make sure your setupEvents uses the same sizing logic:
    setupEvents() {
        window.addEventListener("resize", () => {
            const aw = (this.useCanvasSize && this.canvas)
                ? (this.canvas.clientWidth || 1) : window.innerWidth;
            const ah = (this.useCanvasSize && this.canvas)
                ? (this.canvas.clientHeight || 1) : window.innerHeight;
            this.camera.aspect = aw / ah;
            this.camera.updateProjectionMatrix();

            this.configureRendererSize();

            // Aspect ratio change can crop a face that previously fit;
            // re-run eye-based framing so portrait/landscape rotations
            // and window resizes always frame the head correctly.
            if (this.model && this.model.visible) {
                try {
                    this._applyEyeBasedFraming(this.currentModelKey, {
                        preserveCameraDirection: true,
                    });
                } catch (e) {
                    console.warn(
                        "[resize] eye-based framing failed:",
                        e,
                    );
                }
            }
        });
    }

    /**
     * Re-frame the camera, target, and zoom bounds against the actual
     * world positions of the character's eye meshes. Called from the
     * applyPose commit() callback (initial + every pose update) and
     * from the resize handler.
     *
     * @param {string|null} modelId
     * @param {object} [opts]
     * @param {boolean} [opts.preserveCameraDirection=false]
     *   If true, keep the user's current orbit angle and only adjust
     *   the target + radius (used by the resize handler so a viewer
     *   who has orbited around the head doesn't get snapped back to
     *   the front on a window resize).
     */
    _applyEyeBasedFraming(modelId, opts = null) {
        if (!this.model || !this.camera || !this.controls) return;
        const preserveDirection = !!(opts && opts.preserveCameraDirection);

        // Per-character opt-in: when `useEyeAnchor: true` is set on
        // the characterData entry, look for an artist-placed
        // `EyeAnchor` (or `Eye_Anchor`) node in the model (GLB or
        // FBX since Task #75) and use its
        // world position directly. This short-circuits all heuristics
        // (eye-mesh regex, IPD plausibility check, head-bone fallback,
        // upper-third bbox) for that character. If the flag is on but
        // the GLB has no such node, log a clear error and fall through
        // to the existing heuristics so the character isn't invisible.
        const charEntryEarly =
            (characterData && modelId && characterData[modelId]) || null;
        const useEyeAnchor = !!(charEntryEarly && charEntryEarly.useEyeAnchor);
        let explicitAnchors = null;
        if (useEyeAnchor) {
            explicitAnchors = detectExplicitEyeAnchor(this.model);
            if (!explicitAnchors) {
                console.error(
                    `[EyeFraming] ${modelId || "?"} useEyeAnchor=true ` +
                        "but no EyeAnchor node found in model; " +
                        "falling back to heuristics",
                );
            }
        }
        const primaryAnchors =
            explicitAnchors || detectEyeAnchors(this.model);
        const anchors = primaryAnchors || fallbackAnchorsFromHead(this.model);
        if (!anchors) {
            console.warn(
                "[EyeFraming] no eye anchors detected for " +
                    `${modelId || "?"}; keeping previous framing`,
            );
            return;
        }
        // Treat the explicit anchor as a primary, trusted source —
        // not a "fallback" — so eye-tracking stays enabled.
        const isFallback = !primaryAnchors;

        // Per-character rotation override: characters whose authored
        // forward axis is not -Z carry a `modelRotationY` (radians) on
        // their characterData entry. We feed this into the framing
        // math so the camera sits on the correct side of the face.
        const charEntry =
            (characterData && modelId && characterData[modelId]) || null;
        const modelRotationY =
            charEntry && Number.isFinite(charEntry.modelRotationY)
                ? charEntry.modelRotationY
                : 0;

        const aspect = this.camera.aspect || (window.innerWidth / window.innerHeight);

        // Per-character vertical framing knobs:
        //   framingYOffset      — cm; +up / -down nudge of the camera
        //                         target. Use to slide the framed
        //                         portrait higher or lower in the
        //                         viewport without changing zoom.
        //   framingHeadroomMin  — cm; minimum breathing room above
        //                         the head and below the chin. Larger
        //                         => looser frame (more empty space).
        const framingYOffset =
            charEntry && Number.isFinite(charEntry.framingYOffset)
                ? charEntry.framingYOffset
                : 0;
        const framingHeadroomMin =
            charEntry
            && Number.isFinite(charEntry.framingHeadroomMin)
            && charEntry.framingHeadroomMin >= 0
                ? charEntry.framingHeadroomMin
                : null; // null => use EyeFraming's DEFAULT_HEADROOM_MIN

        const framing = computeFramingFromAnchors(anchors, {
            fovDegrees: this.camera.fov,
            aspect,
            isMobile: this.isMobile,
            modelRotationY,
            framingYOffset,
            ...(framingHeadroomMin != null
                ? { framingHeadroomMin }
                : {}),
        });

        // Zoom bounds come straight from the face-size multipliers
        // (close / wide), so the user can never zoom past upper-body
        // or so close that the face crops.
        const minD = framing.distanceClose;
        const maxD = framing.distanceWide;
        this.controls.minDistance = minD;
        this.controls.maxDistance = maxD;

        // Tilt clamp: ±27° from horizontal eye-line. The face crops
        // beyond this. Constants match the previous SCENE_FRAMING era.
        this.controls.maxPolarAngle = Math.PI * 0.65;
        this.controls.minPolarAngle = Math.PI * 0.35;

        // Azimuth clamp: ±60° from the character's facing direction.
        // The "centered" azimuth is the one OrbitControls computes
        // when the camera sits on the model's local +Z back-side, i.e.
        // atan2(forward.x, forward.z) where `forward` was placed along
        // (sin(rotY), 0, cos(rotY)). Anything beyond this band rotates
        // past the cheek and starts losing the face.
        const centerAzimuth = Math.atan2(framing.forward.x, framing.forward.z);
        this.controls.minAzimuthAngle = centerAzimuth - Math.PI / 3;
        this.controls.maxAzimuthAngle = centerAzimuth + Math.PI / 3;

        if (preserveDirection) {
            // Snapshot the user's current orbit direction BEFORE we
            // mutate the orbit target — otherwise the offset reflects
            // the new target, not the old one, and the camera lurches.
            const oldOffset = this.camera.position
                .clone()
                .sub(this.controls.target);
            const len = oldOffset.length();
            this.controls.target.set(
                framing.target.x,
                framing.target.y,
                framing.target.z,
            );
            if (Number.isFinite(len) && len > 1e-3) {
                oldOffset.multiplyScalar(framing.distanceDefault / len);
            } else {
                oldOffset.set(0, 0, framing.distanceDefault);
            }
            this.camera.position.copy(this.controls.target).add(oldOffset);
        } else {
            this.controls.target.set(
                framing.target.x,
                framing.target.y,
                framing.target.z,
            );
            this.camera.position.set(
                framing.cameraPosition.x,
                framing.cameraPosition.y,
                framing.cameraPosition.z,
            );
        }
        this.controls.update();

        // Task #284 — /learn stage-only FULL-BODY framing. The eye-based
        // framing above anchors to the face (a portrait crop); the corner
        // teacher should read head-to-feet. Override the final camera with a
        // whole-body fit while keeping everything else this method set up
        // (eye-tracking eye level, clamps; zoom baselines below re-read the
        // camera we set here). Runs on resize too — this method IS the
        // resize re-fit path.
        if (this.fullBodyFraming) {
            this._applyFullBodyFraming(framing);
        }

        if (this._zoomState) {
            this._zoomState.baselineCameraY = this.camera.position.y;
            this._zoomState.baselineTargetY = this.controls.target.y;
            this._zoomState.lastRadius = null;
            // Rebase the face-centered zoom band against the new
            // face size so the lerp opens / closes at the right
            // zoom level for tall vs short characters. Lerp band:
            //   lerp = 0 at distanceDefault (talking-frame target)
            //   lerp = 1 at distanceClose   (face-centered target)
            const lessonPolicy = this.fullBodyFit?.policy === "lesson";
            if (lessonPolicy && this._zoomAnimationId != null) {
                cancelAnimationFrame(this._zoomAnimationId);
                this._zoomAnimationId = null;
            }
            this._zoomState.isAnimating = false;
            this._zoomState.normalDistance = framing.distanceDefault;
            this._zoomState.closeDistance = framing.distanceClose;
            this._zoomState.extremeDistance = framing.distanceClose;
            // Cache the two zoom targets (talking-frame and face-
            // locked) so the change handler can lerp the orbit
            // target between them as the user zooms in / out.
            this._zoomState.framingTarget.set(
                framing.target.x,
                framing.target.y,
                framing.target.z,
            );
            const ft = framing.faceTarget || framing.target;
            this._zoomState.faceTarget.set(ft.x, ft.y, ft.z);
            this._zoomState.hasTargets = !lessonPolicy;
            this._zoomState.currentLerpT = 0;
            this._zoomState.targetLerpT = 0;
            this._zoomState.lastLoggedLerpT = 0;
            this._zoomState.lastClampWasActive = false;
        }

        // Push the live eye world-Y into the eye-tracking controller so
        // gaze math uses the real eye level rather than a default.
        // When framing fell back (no LeftEye/RightEye nodes), the
        // anchors are guessed — disable gaze tracking entirely so the
        // pupils don't aim at a synthetic point that doesn't match
        // the actual eye geometry.
        if (this.eyeTrackingController) {
            try {
                if (isFallback) {
                    this.eyeTrackingController.enabled = false;
                    if (typeof this.eyeTrackingController.resetEyeRotation
                            === "function") {
                        this.eyeTrackingController.resetEyeRotation();
                    }
                    console.log(
                        `[EyeFraming] eye-tracking DISABLED for ${modelId || "?"} ` +
                            `— framing used fallback (${anchors.source})`,
                    );
                } else {
                    this.eyeTrackingController.enabled = true;
                    if (typeof this.eyeTrackingController.setEyeLevelFromWorldAnchors
                            === "function") {
                        this.eyeTrackingController.setEyeLevelFromWorldAnchors(
                            anchors,
                            this.model,
                        );
                    }
                }
            } catch (_e) {
                /* noop */
            }
        }

        const pathTag = isFallback ? "FALLBACK" : "PRIMARY";
        const leftName = (anchors.leftNodeName || anchors.leftName || "n/a");
        const rightName = (anchors.rightNodeName || anchors.rightName || "n/a");
        const headTopStr = Number.isFinite(framing.headTopY)
            ? `${framing.headTopY.toFixed(1)}(${framing.headTopSource || "?"})`
            : "n/a";
        const headroomStr =
            framing.headroomMin.toFixed(1)
            + (framing.headroomOverride ? "*" : "");
        const yOffsetStr =
            framing.yOffset.toFixed(1)
            + (framing.yOffsetOverride ? "*" : "");
        console.log(
            `[EyeFraming] ${modelId || "?"} path=${pathTag} ` +
                `source=${anchors.source} ` +
                `nodes=[L:${leftName} R:${rightName}] ` +
                `midpointWorld=(${anchors.midpoint.x.toFixed(1)},` +
                `${anchors.midpoint.y.toFixed(1)},` +
                `${anchors.midpoint.z.toFixed(1)}) ` +
                `eyeDist=${anchors.eyeDistance.toFixed(2)} ` +
                `face=${anchors.faceWidth.toFixed(1)}x${anchors.faceHeight.toFixed(1)} ` +
                `headTop=${headTopStr} ` +
                `effH=${framing.effectiveHeight.toFixed(1)} ` +
                `headroom=${headroomStr} ` +
                `yOff=${yOffsetStr} ` +
                `target=(${framing.target.x.toFixed(1)},` +
                `${framing.target.y.toFixed(1)},` +
                `${framing.target.z.toFixed(1)}) ` +
                `dist[close=${framing.distanceClose.toFixed(1)} ` +
                `def=${framing.distanceDefault.toFixed(1)} ` +
                `wide=${framing.distanceWide.toFixed(1)}] ` +
                `bounds=[${minD.toFixed(1)}..${maxD.toFixed(1)}] ` +
                `azimuth=${(centerAzimuth * 180 / Math.PI).toFixed(0)}°±60°`,
        );
    }

    /**
     * Task #284 — fit the WHOLE body in the viewport (the /learn corner
     * teacher). Opt-in via `viewer.fullBodyFraming = true`; only
     * ResidentLegend sets it, /lab never does. Computes the model's world
     * bounding box and places the camera straight-on along the character's
     * facing direction (framing.forward — respects modelRotationY) at the
     * distance that fits body height AND width with a small margin. Zoom is
     * locked at that distance (the corner canvas is pointer-events:none, so
     * there is no user orbit/zoom to preserve).
     *
     * Task #394 — optional `viewer.fullBodyFit` knob, passed through
     * ResidentLegend opts by the /learn lesson player ONLY:
     *   { heightMargin, widthMargin, anchor: "feet" }
     * tightens the breathing room and pins the FEET to the canvas bottom so
     * the figure fills the tall stage box (all leftover space goes above the
     * head when the width fit wins, instead of floating him mid-box). Every
     * other surface (tutor onboarding, course problem lesson, admin tools)
     * leaves the knob unset and gets the original Task #284 math unchanged.
     */
    _applyFullBodyFraming(framing) {
        try {
            const fit = this.fullBodyFit || {};
            if (fit.policy === "lesson") {
                const result = applyLessonFraming(this, framing, fit);
                if (result) {
                    this.lessonFramingEvidence = result;
                    console.log(
                        `[LessonFraming] vertices=${result.sampledVertices} ` +
                            `distance=${result.distance.toFixed(1)} ` +
                            `eyeY=${result.eyeY.toFixed(1)} ` +
                            `pitch=${result.pitchDegrees.toFixed(1)}° ` +
                            `occupancy=${(result.occupancy.width * 100).toFixed(1)}%x` +
                            `${(result.occupancy.height * 100).toFixed(1)}%`,
                    );
                    return;
                }
                console.warn(
                    "[LessonFraming] posed silhouette unavailable; using classic full-body fit",
                );
            }
            const box = new THREE.Box3().setFromObject(this.model);
            if (box.isEmpty()) return;
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            if (!(size.y > 0)) return;
            const heightMargin = Number.isFinite(fit.heightMargin)
                ? fit.heightMargin
                : 1.08; // ~8% head/foot breathing room (classic)
            const widthMargin = Number.isFinite(fit.widthMargin)
                ? fit.widthMargin
                : 1.12;
            const fovRad = (this.camera.fov * Math.PI) / 180;
            const aspect = this.camera.aspect || 1;
            const halfH = (size.y / 2) * heightMargin;
            const halfW = (Math.max(size.x, size.z) / 2) * widthMargin;
            const distV = halfH / Math.tan(fovRad / 2);
            const distH = halfW / (Math.tan(fovRad / 2) * aspect);
            const dist = Math.max(distV, distH);
            // Vertical anchor. Default: body-centered (leftover space splits
            // evenly above/below — the classic fit). anchor:"feet" plants the
            // feet just above the canvas bottom edge; when the height fit
            // binds the two anchors agree exactly (footPad = the bottom half
            // of the breathing margin), so this only moves the figure when a
            // wide pose/prop makes the width fit win.
            let anchorY = center.y;
            if (fit.anchor === "feet") {
                const visHalfH = dist * Math.tan(fovRad / 2);
                const footPad = (size.y / 2) * Math.max(0, heightMargin - 1);
                anchorY = Math.max(center.y, box.min.y - footPad + visHalfH);
            }
            const fwd = (framing && framing.forward) || { x: 0, z: 1 };
            const norm = Math.hypot(fwd.x, fwd.z) || 1;
            this.controls.target.set(center.x, anchorY, center.z);
            // Camera at anchor height, looking straight on — no tilt, so
            // the figure isn't foreshortened in the tall corner box.
            this.camera.position.set(
                center.x + (fwd.x / norm) * dist,
                anchorY,
                center.z + (fwd.z / norm) * dist,
            );
            this.controls.minDistance = dist;
            this.controls.maxDistance = dist;
            this.controls.update();
            console.log(
                `[FullBodyFraming] h=${size.y.toFixed(1)} ` +
                    `w=${Math.max(size.x, size.z).toFixed(1)} ` +
                    `dist=${dist.toFixed(1)} aspect=${aspect.toFixed(2)} ` +
                    `anchor=${fit.anchor === "feet" ? "feet" : "center"} ` +
                    `y=${anchorY.toFixed(1)} ` +
                    `center=(${center.x.toFixed(1)},${center.y.toFixed(1)},` +
                    `${center.z.toFixed(1)})`,
            );
        } catch (e) {
            console.warn("[FullBodyFraming] failed — keeping eye framing:", e);
        }
    }

    /**
     * Create and setup the gold sparkle particle system
     */
    setupParticleSystem() {
        // Skip particle system on mobile devices to reduce GPU load
        if (this.isMobile) {
            console.log(
                "Particle system disabled on mobile device for performance",
            );
            return;
        }

        // Clean up existing particle system if it exists
        if (this.particleSystem) {
            this.particleSystem.dispose();
            this.particleSystem = null;
        }

        // Create a new blue sparkle particle system
        const particleOptions = {
            count: 600, // Increased particle count for more density
            size: { min: 0.3, max: 1.5 },
            area: {
                width: 500, // Wider distribution area
                height: 200, // Higher distribution area
                depth: 800, // Deeper distribution area
            },
            color: 0x1e90ff, // Primary blue color (dodger blue)
            secondaryColor: 0x87cefa, // Light sky blue
            speed: { min: 0.1, max: 0.8 }, // Slower movement for a gentle falling effect
            lifetime: { min: 3, max: 8 }, // Longer lifetime for particles
            opacity: { min: 0.4, max: 0.8 },
        };

        this.particleSystem = new ParticleSystem(this.scene, particleOptions);
        if (this.particleSystem?.particleSystem) {
            this.particleSystem.particleSystem.visible =
                !window.MaterializeLoader?.isActive;
        }
        console.log("Blue sparkle particle system initialized");
    }

    async loadModel(url, materializeToken = null) {
        if (this.isLoading) {
            console.warn("Model loading already in progress");
            return false;
        }

        const isCurrentLoad = () =>
            materializeToken == null ||
            this._materializeLoadToken === materializeToken;

        const updateMaterialize = (method, ...args) => {
            if (
                materializeToken == null ||
                this._materializeLoadToken !== materializeToken
            ) {
                return;
            }
            // Progress is monotonic per load: the reveal path can push 100
            // (character on screen) before loadModel's own later milestone
            // writes land, and the meter must never run backwards.
            if (method === "setProgress") {
                this._materializeProgress(materializeToken, args[0]);
                return;
            }
            try {
                window.MaterializeLoader?.[method]?.(
                    ...args,
                    materializeToken,
                );
            } catch (_) {}
        };

        const loadingOverlay = document.getElementById("loading-overlay");
        const loadingText = document.getElementById("loading-text");
        const progressBar = document.getElementById("loading-progress-bar");
        const errorOverlay = document.getElementById("error-overlay");

        if (!loadingOverlay || !loadingText || !errorOverlay) {
            console.warn("UI elements missing, but continuing with model load");
        }

        this.isLoading = true;
        // Reset per-load auto-fit floor offset (legacy field name kept).
        // Since Task #75 the unified auto-fit block populates this for
        // both GLB and FBX so it never leaks between characters.
        this._glbFloorOffsetY = 0;

        // Task #274: every character starts at FULL quality — reset the
        // adaptive-resolution governor so a heavy previous character's
        // downscale never leaks onto the next one, and load-time frame
        // stalls don't count as "slow rendering".
        if (this.adaptiveResGovernor) {
            this.adaptiveResGovernor.reset();
            this.configureRendererSize();
        }

        try {
            if (loadingOverlay) loadingOverlay.style.display = "flex";
            if (loadingText)
                loadingText.textContent = "Preparing 3D character...";
            if (progressBar) progressBar.style.width = "75%";
            if (errorOverlay) errorOverlay.classList.add("d-none");
            updateMaterialize("setPhase", "downloading");
            updateMaterialize("setProgress", 0);

            // Task #552 follow-on (post-100% gap): overlap the shared
            // platform fetch and the animation control-plane lookup with
            // the model download, so neither spends a round trip after the
            // meter is already high. Both are best-effort; the consumers
            // keep their own fallbacks.
            try {
                this._loadSharedPlatform().catch(() => {});
            } catch (_) {}
            if (this.currentModelKey) {
                const prefetchKey = this.currentModelKey;
                this._animConfigPrefetch = {
                    key: prefetchKey,
                    promise: (async () => {
                        try {
                            const opts =
                                typeof AbortSignal !== "undefined" &&
                                AbortSignal.timeout
                                    ? { signal: AbortSignal.timeout(2500) }
                                    : {};
                            const resp = await fetch(
                                `/api/animations/${encodeURIComponent(prefetchKey)}`,
                                opts,
                            );
                            return resp.ok ? await resp.json() : null;
                        } catch (_) {
                            return null;
                        }
                    })(),
                };
            }

            console.log("Starting model load from URL:", url);

            // Create a new instance of LoadingManager with proper callbacks
            const manager = new THREE.LoadingManager();

            manager.onProgress = (url, itemsLoaded, itemsTotal) => {
                const progress = (itemsLoaded / itemsTotal) * 100;
                console.log(`Loading progress: ${progress.toFixed(0)}%`);
                if (loadingText) {
                    loadingText.textContent = `Loading character: ${progress.toFixed(0)}%`;
                }
                if (progressBar) {
                    const finalProgress = Math.max(75, 75 + progress * 0.25);
                    progressBar.style.width = `${finalProgress}%`;
                }
                // Download owns 0–85 of the meter; parse/materials/setup
                // milestones own 85–99; 100 fires with the reveal itself.
                updateMaterialize("setProgress", Math.min(85, progress * 0.85));
                if (progress >= 100) {
                    updateMaterialize("setPhase", "finalizing");
                }
                if (progress === 100) {
                    console.log(
                        "Model loading complete. Model ID:",
                        this.currentModelKey,
                    );
                }
            };

            manager.onError = (url) => {
                console.warn("Resource load warning:", url);
            };

            // Determine loader based on model format and URL type
            const useRangeLoader = url.includes("/api/stream_model/");
            const isGLB = this.currentModelFormat === "glb";
            let loader;

            if (isGLB) {
                loader = useRangeLoader
                    ? new RangeBasedGLTFLoader(manager)
                    : new GLTFLoader(manager);
            } else {
                loader = useRangeLoader
                    ? new RangeBasedFBXLoader(manager)
                    : new FBXLoader(manager);
            }

            const loaderName = isGLB
                ? useRangeLoader
                    ? "RangeBasedGLTFLoader"
                    : "GLTFLoader"
                : useRangeLoader
                  ? "RangeBasedFBXLoader"
                  : "FBXLoader";
            console.log(`Using ${loaderName} for URL:`, url);

            const loadedModel = await new Promise((resolve, reject) => {
                try {
                    loader.load(
                        url,
                        (result) => {
                            updateMaterialize("setProgress", 88);
                            updateMaterialize("setPhase", "finalizing");
                            let model = result;
                            if (
                                isGLB &&
                                !useRangeLoader &&
                                result &&
                                result.scene
                            ) {
                                model = result.scene;
                                model.animations = result.animations || [];
                            }
                            if (!model) {
                                console.warn(
                                    "Model failed to load - null model returned, but continuing...",
                                );
                            }
                            console.log(
                                "Model loaded successfully from URL:",
                                url,
                            );
                            resolve(model);
                        },
                        (progress) => {
                            if (progress.lengthComputable) {
                                const percentComplete =
                                    (progress.loaded / progress.total) * 100;
                                console.log(
                                    `Download progress: ${percentComplete.toFixed(0)}%`,
                                );
                                if (loadingText) {
                                    loadingText.textContent = `Downloading character: ${percentComplete.toFixed(0)}%`;
                                }
                                if (progressBar) {
                                    const finalProgress = Math.max(
                                        75,
                                        75 + percentComplete * 0.25,
                                    );
                                    progressBar.style.width = `${finalProgress}%`;
                                }
                                updateMaterialize(
                                    "setProgress",
                                    Math.min(85, percentComplete * 0.85),
                                );
                                if (percentComplete >= 100) {
                                    updateMaterialize(
                                        "setPhase",
                                        "finalizing",
                                    );
                                }
                            }
                        },
                        (error) => {
                            console.error(`${loaderName} load error:`, error);

                            if (
                                !isGLB &&
                                error.message &&
                                (error.message.includes(
                                    "Cannot find the version number",
                                ) ||
                                    error.message.includes(
                                        "FBX format version",
                                    ))
                            ) {
                                console.warn(
                                    "FBXLoader version warning:",
                                    error.message,
                                );
                                if (loadingText)
                                    loadingText.textContent =
                                        "Loading model (legacy format)...";
                                resolve(new THREE.Group());
                            } else {
                                reject(
                                    new Error(
                                        `Failed to load model: ${error.message || "Unknown error"}`,
                                    ),
                                );
                            }
                        },
                    );
                } catch (initError) {
                    console.error("Error initializing model load:", initError);
                    reject(initError);
                }
            });

            // A newer character can be selected while this request is in
            // flight. Do not assign or set up the stale result; dispose it
            // through the normal cleanup path before the queued load proceeds.
            if (!isCurrentLoad()) {
                this.model = loadedModel;
                this.cleanupCurrentModel();
                return false;
            }
            this.model = loadedModel;

            console.log(
                "Model loaded successfully, analyzing skeleton structure...",
            );
            updateMaterialize("setProgress", 88);
            updateMaterialize("setPhase", "finalizing");

            // Analyze and correct bone structure if needed
            // this.analyzeBoneStructure();

            // ---------------------------------------------------------------
            // FBX load diagnostic dump (Task #91, FBX only).
            //
            // One-time, compact dump of the loaded rig BEFORE the strip
            // pass / auto-fit so we can see exactly where junk
            // translations live (which bone, which clip, which track,
            // or the model root itself). Designed to be pasted back
            // verbatim by the user when triaging a broken FBX export.
            // Gated behind FBX_DIAG_ENABLED — default true. Single
            // emit per loadModel.
            // ---------------------------------------------------------------
            const FBX_DIAG_ENABLED = true;
            if (
                FBX_DIAG_ENABLED &&
                this.currentModelFormat !== "glb" &&
                this.model
            ) {
                try {
                    const fmt = (n) =>
                        typeof n === "number" && Number.isFinite(n)
                            ? n.toFixed(1)
                            : String(n);
                    const fmtVec = (v) =>
                        v
                            ? `(${fmt(v.x)},${fmt(v.y)},${fmt(v.z)})`
                            : "(n/a)";

                    // Model root info.
                    const root = this.model;
                    const rootLocal = root.position
                        ? { x: root.position.x, y: root.position.y, z: root.position.z }
                        : null;
                    const worldPos = new THREE.Vector3();
                    if (typeof root.getWorldPosition === "function") {
                        root.updateMatrixWorld(true);
                        root.getWorldPosition(worldPos);
                    }
                    // UnitScaleFactor: FBXLoader stores raw FBX tree
                    // on `parser` privately, but the model's
                    // `.userData` sometimes carries it. Try a few
                    // plausible locations; OK if missing.
                    let unitScale = "unknown";
                    const ud = root.userData || {};
                    if (ud.UnitScaleFactor != null) unitScale = ud.UnitScaleFactor;
                    else if (ud.fbxTree && ud.fbxTree.GlobalSettings &&
                             ud.fbxTree.GlobalSettings.UnitScaleFactor != null) {
                        unitScale = ud.fbxTree.GlobalSettings.UnitScaleFactor;
                    }

                    const clips = Array.isArray(root.animations)
                        ? root.animations
                        : [];

                    console.log(
                        `[FBX-DIAG] model="${root.name || "(unnamed)"}" ` +
                            `local=${fmtVec(rootLocal)} ` +
                            `world=(${fmt(worldPos.x)},${fmt(worldPos.y)},${fmt(worldPos.z)}) ` +
                            `scale=(${fmt(root.scale.x)},${fmt(root.scale.y)},${fmt(root.scale.z)}) ` +
                            `unitScaleFactor=${unitScale} ` +
                            `animations=${clips.length}`,
                    );

                    // Walk every Object3D once; collect bones AND any
                    // non-bone node with a non-trivial .position (so
                    // we catch junk that lives on a Group, Empty,
                    // SkinnedMesh, etc., not just on Bones).
                    const allNodes = [];
                    root.traverse((n) => {
                        if (!n || n === root) return;
                        allNodes.push(n);
                    });
                    const bones = allNodes.filter((n) => n && n.isBone);
                    const nodeByName = new Map();
                    for (const n of allNodes) {
                        if (n.name) nodeByName.set(n.name, n);
                    }

                    // Bone rest pose list — only print ones with
                    // |any axis| > 1 cm to keep output small; always
                    // print the first 8 bones for hierarchy context.
                    const interestingBones = [];
                    const REST_PRINT_THRESHOLD = 1; // cm
                    for (let i = 0; i < bones.length; i++) {
                        const b = bones[i];
                        const p = b.position;
                        const abs = Math.max(
                            Math.abs(p.x),
                            Math.abs(p.y),
                            Math.abs(p.z),
                        );
                        if (i < 8 || abs > REST_PRINT_THRESHOLD) {
                            interestingBones.push({ b, abs });
                        }
                    }
                    console.log(
                        `[FBX-DIAG] bones total=${bones.length} ` +
                            `printed=${interestingBones.length} ` +
                            `(first 8 + any with rest.|axis|>${REST_PRINT_THRESHOLD}cm):`,
                    );
                    for (const { b } of interestingBones) {
                        const parentName =
                            b.parent && b.parent.name
                                ? b.parent.name +
                                  (b.parent.isBone ? "" : "(non-bone)")
                                : "(none)";
                        console.log(
                            `[FBX-DIAG]   bone "${b.name}" parent="${parentName}" ` +
                                `rest=${fmtVec(b.position)}`,
                        );
                    }

                    // Non-bone Object3Ds with significant local
                    // translation (Groups, Empties, SkinnedMeshes,
                    // helper nodes). These are the prime suspects for
                    // junk translations that the previous fix missed.
                    const NONBONE_THRESHOLD = 100; // cm
                    const nonBoneOffenders = [];
                    for (const n of allNodes) {
                        if (n.isBone) continue;
                        if (!n.position) continue;
                        const abs = Math.max(
                            Math.abs(n.position.x),
                            Math.abs(n.position.y),
                            Math.abs(n.position.z),
                        );
                        if (abs > NONBONE_THRESHOLD) nonBoneOffenders.push(n);
                    }
                    console.log(
                        `[FBX-DIAG] non-bone nodes with |any axis|>${NONBONE_THRESHOLD}cm: ` +
                            `${nonBoneOffenders.length}`,
                    );
                    for (const n of nonBoneOffenders.slice(0, 20)) {
                        const parentName =
                            n.parent && n.parent.name ? n.parent.name : "(none)";
                        console.log(
                            `[FBX-DIAG]   node type="${n.type}" name="${n.name}" ` +
                                `parent="${parentName}" pos=${fmtVec(n.position)}`,
                        );
                    }

                    // Every .position track across all clips, with
                    // per-axis min/max keyframe values. Only print
                    // tracks where |any axis| > 1 cm so a clean rig
                    // produces near-zero output, and a junk rig
                    // makes the offender obvious.
                    const TRACK_PRINT_THRESHOLD = 1; // cm
                    let totalPosTracks = 0;
                    const printedTracks = [];
                    for (const clip of clips) {
                        if (!clip || !Array.isArray(clip.tracks)) continue;
                        for (const track of clip.tracks) {
                            if (
                                !track ||
                                typeof track.name !== "string" ||
                                !track.name.endsWith(".position")
                            ) {
                                continue;
                            }
                            totalPosTracks += 1;
                            const values = track.values || [];
                            const samples = values.length / 3;
                            const mins = [Infinity, Infinity, Infinity];
                            const maxs = [-Infinity, -Infinity, -Infinity];
                            for (let i = 0; i < values.length; i += 3) {
                                if (values[i] < mins[0]) mins[0] = values[i];
                                if (values[i] > maxs[0]) maxs[0] = values[i];
                                if (values[i + 1] < mins[1]) mins[1] = values[i + 1];
                                if (values[i + 1] > maxs[1]) maxs[1] = values[i + 1];
                                if (values[i + 2] < mins[2]) mins[2] = values[i + 2];
                                if (values[i + 2] > maxs[2]) maxs[2] = values[i + 2];
                            }
                            const maxAbs = Math.max(
                                Math.abs(mins[0]),
                                Math.abs(maxs[0]),
                                Math.abs(mins[1]),
                                Math.abs(maxs[1]),
                                Math.abs(mins[2]),
                                Math.abs(maxs[2]),
                            );
                            if (maxAbs <= TRACK_PRINT_THRESHOLD) continue;
                            const boneName = track.name.slice(
                                0,
                                track.name.length - ".position".length,
                            );
                            const target = nodeByName.get(boneName);
                            const targetKind = target
                                ? target.isBone
                                    ? "bone"
                                    : target.type
                                : "MISSING";
                            printedTracks.push({
                                clipName: clip.name || "(unnamed)",
                                boneName,
                                targetKind,
                                samples,
                                mins,
                                maxs,
                                maxAbs,
                            });
                        }
                    }
                    console.log(
                        `[FBX-DIAG] position tracks total=${totalPosTracks} ` +
                            `printed=${printedTracks.length} ` +
                            `(only tracks with |any axis|>${TRACK_PRINT_THRESHOLD}cm):`,
                    );
                    printedTracks.sort((a, b) => b.maxAbs - a.maxAbs);
                    for (const t of printedTracks.slice(0, 40)) {
                        console.log(
                            `[FBX-DIAG]   clip="${t.clipName}" "${t.boneName}" ` +
                                `target=${t.targetKind} samples=${t.samples} ` +
                                `x:[${fmt(t.mins[0])}..${fmt(t.maxs[0])}] ` +
                                `y:[${fmt(t.mins[1])}..${fmt(t.maxs[1])}] ` +
                                `z:[${fmt(t.mins[2])}..${fmt(t.maxs[2])}]`,
                        );
                    }

                    // Bone rest .scale — anything where |axis - 1| > 0.1
                    // is suspect (default rig scale is 1,1,1). Also
                    // captures any node with non-trivial scale, not
                    // just bones.
                    const SCALE_DEV_THRESHOLD = 0.1;
                    const scaleOffenders = [];
                    for (const n of allNodes) {
                        if (!n.scale) continue;
                        const dx = Math.abs(n.scale.x - 1);
                        const dy = Math.abs(n.scale.y - 1);
                        const dz = Math.abs(n.scale.z - 1);
                        if (
                            dx > SCALE_DEV_THRESHOLD ||
                            dy > SCALE_DEV_THRESHOLD ||
                            dz > SCALE_DEV_THRESHOLD
                        ) {
                            scaleOffenders.push(n);
                        }
                    }
                    scaleOffenders.sort((a, b) => {
                        const maxA = Math.max(
                            Math.abs(a.scale.x),
                            Math.abs(a.scale.y),
                            Math.abs(a.scale.z),
                        );
                        const maxB = Math.max(
                            Math.abs(b.scale.x),
                            Math.abs(b.scale.y),
                            Math.abs(b.scale.z),
                        );
                        return maxB - maxA;
                    });
                    console.log(
                        `[FBX-DIAG] nodes with rest .scale deviating from (1,1,1) by ` +
                            `>${SCALE_DEV_THRESHOLD}: ${scaleOffenders.length}`,
                    );
                    for (const n of scaleOffenders.slice(0, 30)) {
                        const parentName =
                            n.parent && n.parent.name ? n.parent.name : "(none)";
                        const kind = n.isBone ? "bone" : n.type;
                        console.log(
                            `[FBX-DIAG]   ${kind} "${n.name}" parent="${parentName}" ` +
                                `scale=${fmtVec(n.scale)}`,
                        );
                    }

                    // Every NON-.position track across all clips —
                    // .scale and .quaternion. Print scale tracks where
                    // any value deviates from 1 by more than 0.1, and
                    // quaternion tracks where any |component| > 1
                    // (quaternions are normalized, so |w|,|x|,|y|,|z|
                    // <= 1 always — anything bigger is junk).
                    let totalScaleTracks = 0;
                    let totalQuatTracks = 0;
                    const scaleTracksOut = [];
                    const quatTracksOut = [];
                    for (const clip of clips) {
                        if (!clip || !Array.isArray(clip.tracks)) continue;
                        for (const track of clip.tracks) {
                            if (!track || typeof track.name !== "string") continue;
                            if (track.name.endsWith(".scale")) {
                                totalScaleTracks += 1;
                                const values = track.values || [];
                                const samples = values.length / 3;
                                const mins = [Infinity, Infinity, Infinity];
                                const maxs = [-Infinity, -Infinity, -Infinity];
                                for (let i = 0; i < values.length; i += 3) {
                                    if (values[i] < mins[0]) mins[0] = values[i];
                                    if (values[i] > maxs[0]) maxs[0] = values[i];
                                    if (values[i + 1] < mins[1])
                                        mins[1] = values[i + 1];
                                    if (values[i + 1] > maxs[1])
                                        maxs[1] = values[i + 1];
                                    if (values[i + 2] < mins[2])
                                        mins[2] = values[i + 2];
                                    if (values[i + 2] > maxs[2])
                                        maxs[2] = values[i + 2];
                                }
                                const maxDev = Math.max(
                                    Math.abs(mins[0] - 1),
                                    Math.abs(maxs[0] - 1),
                                    Math.abs(mins[1] - 1),
                                    Math.abs(maxs[1] - 1),
                                    Math.abs(mins[2] - 1),
                                    Math.abs(maxs[2] - 1),
                                );
                                if (maxDev <= 0.1) continue;
                                const boneName = track.name.slice(
                                    0,
                                    track.name.length - ".scale".length,
                                );
                                const target = nodeByName.get(boneName);
                                scaleTracksOut.push({
                                    clipName: clip.name || "(unnamed)",
                                    boneName,
                                    targetKind: target
                                        ? target.isBone
                                            ? "bone"
                                            : target.type
                                        : "MISSING",
                                    samples,
                                    mins,
                                    maxs,
                                    maxDev,
                                });
                            } else if (track.name.endsWith(".quaternion")) {
                                totalQuatTracks += 1;
                                const values = track.values || [];
                                const samples = values.length / 4;
                                let maxAbs = 0;
                                for (let i = 0; i < values.length; i++) {
                                    const v = Math.abs(values[i]);
                                    if (v > maxAbs) maxAbs = v;
                                }
                                if (maxAbs <= 1.01) continue;
                                const boneName = track.name.slice(
                                    0,
                                    track.name.length - ".quaternion".length,
                                );
                                quatTracksOut.push({
                                    clipName: clip.name || "(unnamed)",
                                    boneName,
                                    samples,
                                    maxAbs,
                                });
                            }
                        }
                    }
                    scaleTracksOut.sort((a, b) => b.maxDev - a.maxDev);
                    console.log(
                        `[FBX-DIAG] scale tracks total=${totalScaleTracks} ` +
                            `printed=${scaleTracksOut.length} ` +
                            `(only tracks where any value deviates from 1 by >0.1):`,
                    );
                    for (const t of scaleTracksOut.slice(0, 30)) {
                        console.log(
                            `[FBX-DIAG]   clip="${t.clipName}" "${t.boneName}" ` +
                                `target=${t.targetKind} samples=${t.samples} ` +
                                `x:[${fmt(t.mins[0])}..${fmt(t.maxs[0])}] ` +
                                `y:[${fmt(t.mins[1])}..${fmt(t.maxs[1])}] ` +
                                `z:[${fmt(t.mins[2])}..${fmt(t.maxs[2])}]`,
                        );
                    }
                    quatTracksOut.sort((a, b) => b.maxAbs - a.maxAbs);
                    console.log(
                        `[FBX-DIAG] quaternion tracks total=${totalQuatTracks} ` +
                            `printed=${quatTracksOut.length} (only tracks where any |component|>1.01):`,
                    );
                    for (const t of quatTracksOut.slice(0, 10)) {
                        console.log(
                            `[FBX-DIAG]   clip="${t.clipName}" "${t.boneName}" ` +
                                `samples=${t.samples} maxAbs=${fmt(t.maxAbs)}`,
                        );
                    }

                    // Effective world position of each Root_M-named
                    // bone, since the dump showed 5 bones share that
                    // name (FBX rig duplication). World pos exposes
                    // any accumulated scale/parent mischief.
                    const rootMNodes = allNodes.filter(
                        (n) => n.isBone && n.name === "Root_M",
                    );
                    if (rootMNodes.length > 0) {
                        root.updateMatrixWorld(true);
                        console.log(
                            `[FBX-DIAG] bones named "Root_M" count=${rootMNodes.length} (world positions at rest):`,
                        );
                        const tmp = new THREE.Vector3();
                        for (let i = 0; i < rootMNodes.length; i++) {
                            const b = rootMNodes[i];
                            b.getWorldPosition(tmp);
                            const parentName =
                                b.parent && b.parent.name
                                    ? b.parent.name
                                    : "(none)";
                            console.log(
                                `[FBX-DIAG]   [${i}] parent="${parentName}" ` +
                                    `local=${fmtVec(b.position)} ` +
                                    `worldRest=(${fmt(tmp.x)},${fmt(tmp.y)},${fmt(tmp.z)})`,
                            );
                        }
                    }

                    // EyeAnchor world position at rest. The end-of-
                    // chain target Three.js uses for the eye-anchor
                    // framing path. If this is sane at rest but
                    // becomes absurd after mixer.update(), the
                    // offender is in animation tracks; if it's
                    // already absurd here, the offender is in the
                    // rig itself.
                    const eyeAnchorNode = allNodes.find((n) => {
                        if (!n || !n.name) return false;
                        const lower = n.name.toLowerCase();
                        return (
                            lower === "eyeanchor" || lower === "eye_anchor"
                        );
                    });
                    if (eyeAnchorNode) {
                        root.updateMatrixWorld(true);
                        const tmp = new THREE.Vector3();
                        eyeAnchorNode.getWorldPosition(tmp);
                        const tmpScale = new THREE.Vector3();
                        const tmpQuat = new THREE.Quaternion();
                        const tmpPos = new THREE.Vector3();
                        eyeAnchorNode.matrixWorld.decompose(
                            tmpPos,
                            tmpQuat,
                            tmpScale,
                        );
                        console.log(
                            `[FBX-DIAG] EyeAnchor type=${eyeAnchorNode.type} ` +
                                `local=${fmtVec(eyeAnchorNode.position)} ` +
                                `worldRest=(${fmt(tmp.x)},${fmt(tmp.y)},${fmt(tmp.z)}) ` +
                                `worldScaleAtRest=${fmtVec(tmpScale)}`,
                        );
                    } else {
                        console.log(`[FBX-DIAG] EyeAnchor node not found in rig.`);
                    }

                    // Visible-mesh world bounding box (same path used
                    // by the auto-fit body-height pass + EyeFraming's
                    // detectHeadTopY). If this is at Y≈-69000 at rest,
                    // the offset lives in geometry vertices or in the
                    // SkinnedMesh bind matrix / skeleton.boneInverses,
                    // NOT in any .position we already checked.
                    root.updateMatrixWorld(true);
                    const visBox = new THREE.Box3();
                    const tmpBox = new THREE.Box3();
                    let visMeshCount = 0;
                    const skinnedDump = [];
                    root.traverseVisible((child) => {
                        if (!child) return;
                        if (!(child.isMesh || child.isSkinnedMesh)) return;
                        const geom = child.geometry;
                        if (!geom) return;
                        if (!geom.boundingBox) {
                            try { geom.computeBoundingBox(); } catch (_e) { return; }
                        }
                        if (!geom.boundingBox) return;
                        tmpBox.copy(geom.boundingBox);
                        tmpBox.applyMatrix4(child.matrixWorld);
                        if (!Number.isFinite(tmpBox.min.x) || !Number.isFinite(tmpBox.max.x)) return;
                        visBox.union(tmpBox);
                        visMeshCount += 1;
                        if (child.isSkinnedMesh && skinnedDump.length < 8) {
                            // Decompose matrixWorld + bindMatrix to
                            // see where this SkinnedMesh actually
                            // renders vs where it was bound.
                            const mwT = new THREE.Vector3();
                            const mwQ = new THREE.Quaternion();
                            const mwS = new THREE.Vector3();
                            child.matrixWorld.decompose(mwT, mwQ, mwS);
                            const bmT = new THREE.Vector3();
                            const bmQ = new THREE.Quaternion();
                            const bmS = new THREE.Vector3();
                            if (child.bindMatrix) {
                                child.bindMatrix.decompose(bmT, bmQ, bmS);
                            }
                            const gbb = geom.boundingBox;
                            skinnedDump.push({
                                name: child.name || "(unnamed)",
                                pos: child.position.clone(),
                                mwT: mwT.clone(),
                                mwS: mwS.clone(),
                                bmT: bmT.clone(),
                                bmS: bmS.clone(),
                                geomBoxLocal: { min: gbb.min.clone(), max: gbb.max.clone() },
                                worldBoxY: { min: tmpBox.min.y, max: tmpBox.max.y },
                                vertexCount: geom.attributes && geom.attributes.position
                                    ? geom.attributes.position.count
                                    : -1,
                            });
                        }
                    });
                    console.log(
                        `[FBX-DIAG] visibleMeshBox meshes=${visMeshCount} ` +
                            `min=${fmtVec(visBox.min)} max=${fmtVec(visBox.max)} ` +
                            `sizeY=${fmt(visBox.max.y - visBox.min.y)}`,
                    );
                    for (const s of skinnedDump) {
                        console.log(
                            `[FBX-DIAG]   SkinnedMesh "${s.name}" verts=${s.vertexCount} ` +
                                `localPos=${fmtVec(s.pos)} ` +
                                `matrixWorldT=${fmtVec(s.mwT)} mwScale=${fmtVec(s.mwS)} ` +
                                `bindMatrixT=${fmtVec(s.bmT)} bmScale=${fmtVec(s.bmS)} ` +
                                `geomLocalY=[${fmt(s.geomBoxLocal.min.y)}..${fmt(s.geomBoxLocal.max.y)}] ` +
                                `worldY=[${fmt(s.worldBoxY.min)}..${fmt(s.worldBoxY.max)}]`,
                        );
                    }

                    // boneInverses sanity: dump the translation
                    // component of the first 4 inverse bind matrices
                    // for the largest skeleton in the rig. If these
                    // have absurd translations the skinned vertices
                    // get pushed to absurd world positions even
                    // though every bone .position looks fine.
                    let primarySkel = null;
                    let primarySkelBoneCount = -1;
                    root.traverse((o) => {
                        if (o && o.isSkinnedMesh && o.skeleton && o.skeleton.bones) {
                            const c = o.skeleton.bones.length;
                            if (c > primarySkelBoneCount) {
                                primarySkelBoneCount = c;
                                primarySkel = o.skeleton;
                            }
                        }
                    });
                    if (primarySkel && Array.isArray(primarySkel.boneInverses)) {
                        const tBI = new THREE.Vector3();
                        const qBI = new THREE.Quaternion();
                        const sBI = new THREE.Vector3();
                        let absurdBICount = 0;
                        let maxBIabs = 0;
                        let maxBIname = "";
                        for (let i = 0; i < primarySkel.boneInverses.length; i++) {
                            const inv = primarySkel.boneInverses[i];
                            if (!inv) continue;
                            inv.decompose(tBI, qBI, sBI);
                            const mag = Math.max(
                                Math.abs(tBI.x), Math.abs(tBI.y), Math.abs(tBI.z),
                            );
                            if (mag > 1000) absurdBICount += 1;
                            if (mag > maxBIabs) {
                                maxBIabs = mag;
                                maxBIname = primarySkel.bones[i]?.name || `bone[${i}]`;
                            }
                        }
                        console.log(
                            `[FBX-DIAG] skeleton bones=${primarySkel.bones.length} ` +
                                `boneInverses with |T|>1000: ${absurdBICount} ` +
                                `maxBI=|${fmt(maxBIabs)}|@"${maxBIname}"`,
                        );
                        // Print first 4 boneInverses verbosely so we
                        // can compare against the rest-pose bone Ts.
                        for (let i = 0; i < Math.min(4, primarySkel.boneInverses.length); i++) {
                            primarySkel.boneInverses[i].decompose(tBI, qBI, sBI);
                            console.log(
                                `[FBX-DIAG]   boneInverses[${i}] bone="${primarySkel.bones[i]?.name || "?"}" ` +
                                    `T=${fmtVec(tBI)} S=${fmtVec(sBI)}`,
                            );
                        }
                    }

                    // detectEyeHeight (what auto-fit will see).
                    try {
                        const eh = detectEyeHeight(this.model, { skeletonOnly: true });
                        console.log(`[FBX-DIAG] detectEyeHeight(skeletonOnly=true)=${eh}`);
                        const ehFb = detectEyeHeight(this.model);
                        console.log(`[FBX-DIAG] detectEyeHeight(withBBoxFallback)=${ehFb}`);
                    } catch (eheErr) {
                        console.log(`[FBX-DIAG] detectEyeHeight failed: ${eheErr}`);
                    }

                    console.log(`[FBX-DIAG] end`);
                } catch (diagErr) {
                    console.warn("[FBX-DIAG] dump failed:", diagErr);
                }
            }

            // ---------------------------------------------------------------
            // Absurd-translation strip pass (FBX only, Task #91).
            //
            // Some FBX exports ship animation clips and/or rest poses
            // whose `.position` values are absurdly large (e.g. Karl
            // Marx's `KarlBestV1ALL.fbx` historically dragged
            // `Root_M` to Y ≈ −69,000 cm). When the mixer plays such
            // a clip the skeleton — and with it every visible mesh —
            // teleports far outside the sky sphere (radius 1000), the
            // eye-framing pipeline follows the model out of frame,
            // and the user sees a flat gray viewport with no apparent
            // character and a "broken" sky toggle.
            //
            // The earlier Task #88 fix gated this scrub by a root-
            // bone *name* regex (Root_M / Hips / Armature / …) plus
            // "topmost-bone" detection. The [FBX-DIAG] dump captured
            // the rig in detail and confirmed that:
            //   * the absurd value is a pure-magnitude phenomenon
            //     (no working FBX in our roster has any rest or
            //     animation `.position` value above ~89 cm on any
            //     node — root, child, or non-bone);
            //   * a name regex adds no signal and risks missing the
            //     same kind of junk if it ever lands on a different
            //     bone or on a non-bone helper.
            //
            // The general rule (this pass): walk every Object3D in
            // the loaded model and every `.position` track in every
            // animation clip; if any rest-pose `.position` or any
            // keyframe value has |axis| > POSITION_THRESHOLD cm,
            // snap the rest pose to (0,0,0) and/or drop the track.
            // No name regex, no bone-vs-non-bone gate. Rotation and
            // scale tracks are never touched. GLB is skipped.
            //
            // Threshold = 1000 cm: sits an order of magnitude above
            // any legitimate translation we have ever observed in
            // working FBX rigs, two orders of magnitude below the
            // known Marx junk value, and well below the sky-sphere
            // radius so anything caught here was already going to
            // teleport the character out of view.
            // ---------------------------------------------------------------
            if (this.currentModelFormat !== "glb" && this.model) {
                try {
                    const POSITION_THRESHOLD = 1000; // cm

                    // 1) Snap absurd rest-pose `.position` values on
                    //    ANY Object3D (bone, group, mesh, helper)
                    //    back to (0,0,0). The model root is excluded
                    //    because pose code legitimately writes a
                    //    translation onto it (setupCharacter /
                    //    applyPose set model.position.y from
                    //    pose.modelY + this._glbFloorOffsetY). That
                    //    leaves a narrow blind spot if a future FBX
                    //    ever embeds an absurd translation directly
                    //    on the loaded root — if `[FBX-DIAG]` ever
                    //    surfaces that, add a root-specific clamp
                    //    here rather than dropping the exclusion.
                    let resetNodeCount = 0;
                    const resetDetails = [];
                    this.model.traverse((n) => {
                        if (!n || n === this.model || !n.position) return;
                        const p = n.position;
                        if (
                            Math.abs(p.x) > POSITION_THRESHOLD ||
                            Math.abs(p.y) > POSITION_THRESHOLD ||
                            Math.abs(p.z) > POSITION_THRESHOLD
                        ) {
                            const kind = n.isBone ? "bone" : n.type;
                            resetDetails.push(
                                `${kind}:${n.name || "?"}@(${p.x.toFixed(0)},${p.y.toFixed(0)},${p.z.toFixed(0)})`,
                            );
                            p.set(0, 0, 0);
                            n.updateMatrix();
                            resetNodeCount += 1;
                        }
                    });
                    if (resetNodeCount > 0) {
                        this.model.updateMatrixWorld(true);
                    }

                    // 2) Scrub any `.position` track whose max
                    //    keyframe |axis| exceeds the threshold,
                    //    regardless of which node it targets.
                    let scrubbedTrackCount = 0;
                    let scannedClipCount = 0;
                    let scannedTrackCount = 0;
                    let scrubbedMaxAbs = 0;
                    const clips = this.model.animations || [];
                    for (const clip of clips) {
                        if (!clip || !Array.isArray(clip.tracks)) continue;
                        scannedClipCount += 1;
                        const kept = [];
                        for (const track of clip.tracks) {
                            if (
                                !track ||
                                typeof track.name !== "string" ||
                                !track.name.endsWith(".position")
                            ) {
                                kept.push(track);
                                continue;
                            }
                            scannedTrackCount += 1;
                            const values = track.values;
                            let maxAbs = 0;
                            if (values && values.length) {
                                for (let i = 0; i < values.length; i++) {
                                    const v = Math.abs(values[i]);
                                    if (v > maxAbs) maxAbs = v;
                                }
                            }
                            if (maxAbs > POSITION_THRESHOLD) {
                                if (maxAbs > scrubbedMaxAbs) {
                                    scrubbedMaxAbs = maxAbs;
                                }
                                scrubbedTrackCount += 1;
                                continue; // drop this track
                            }
                            kept.push(track);
                        }
                        clip.tracks = kept;
                    }

                    // Single summary line per FBX load (incl. no-op case).
                    const summaryParts = [
                        `[FBX] Absurd-translation strip pass:`,
                        `clips=${scannedClipCount}`,
                        `posTracks=${scannedTrackCount}`,
                        `scrubbed=${scrubbedTrackCount}`,
                        `restPosesReset=${resetNodeCount}`,
                        `threshold=${POSITION_THRESHOLD}`,
                    ];
                    if (scrubbedTrackCount > 0) {
                        summaryParts.push(
                            `maxAbs=${scrubbedMaxAbs.toFixed(0)}`,
                        );
                    }
                    if (resetDetails.length > 0) {
                        summaryParts.push(
                            `resets=[${resetDetails.slice(0, 8).join(",")}]`,
                        );
                    }
                    console.log(summaryParts.join(" "));
                } catch (stripErr) {
                    console.warn(
                        "[FBX] Absurd-translation strip pass failed (continuing without it):",
                        stripErr,
                    );
                }
            }

            {
                // Standardized model sizing — runs for BOTH GLB and FBX
                // (Task #75). Was originally GLB-only, which left FBX
                // rigs at whatever native scale the FBXLoader applied
                // from the file's UnitScaleFactor (1, 100, 2.54, …),
                // forcing every new FBX character to be hand-tuned via
                // characterData.modelScale just to land in frame.
                //
                // Goal: every model ends up with its EYES at the same
                // world-Y as the camera target, regardless of native
                // units (meters vs cm), source format, how the model
                // was authored (head pivot, feet pivot, off-center), or
                // whether it ships with extra bounding-box-inflating
                // geometry (hats, robes, props).
                //
                // The pose system later positions the model with
                //     modelY = effectiveAnchorY - eyeHeight + faceTrim
                // where eyeHeight defaults to DEFAULT_EYE_HEIGHT. So if
                // we scale every model so its detected eye height
                // equals DEFAULT_EYE_HEIGHT, the eyes always land in
                // the camera frame even with no per-character config.
                //
                // Because FBX models often arrive with a non-unit
                // scale already baked in by FBXLoader, we explicitly
                // reset model.scale to 1 BEFORE measuring, so every
                // measurement and every applied scale is absolute (in
                // the same world units) regardless of source format.
                //
                // Resolution order:
                //   1. characterData.modelScale  (manual escape hatch)
                //   2. eye-height auto-fit       (preferred, anatomy-aware)
                //   3. body-height auto-fit      (no eye nodes available)
                //   4. largest-axis fallback     (degenerate Y)
                //   5. legacy 100x fallback      (everything failed)
                const formatTag =
                    (this.currentModelFormat || "model").toUpperCase();
                // Neutralize any pre-existing scale (FBXLoader's
                // UnitScaleFactor, baked authoring scale, etc.) so the
                // measure → apply pipeline works in absolute world
                // units identically for GLB and FBX.
                this.model.scale.set(1, 1, 1);
                this.model.updateMatrixWorld(true);
                const REFERENCE_EYE_HEIGHT = DEFAULT_EYE_HEIGHT; // 165
                // Body-height target derived from anthropometric ratio so
                // path #3 produces an equivalent on-screen size to path #2.
                const REFERENCE_BODY_HEIGHT =
                    REFERENCE_EYE_HEIGHT / ANTHROPOMETRIC_EYE_RATIO;
                // Sanity bounds for the auto-derived scale: anything
                // outside this range almost certainly means the
                // measurement was wrong, and applying it would shoot the
                // model out of the camera frustum.
                const SCALE_MIN = 0.001;
                const SCALE_MAX = 100000;

                const characterEntry =
                    (characterData && characterData[this.currentModelId]) ||
                    null;
                const manualScale =
                    characterEntry &&
                    typeof characterEntry.modelScale === "number" &&
                    characterEntry.modelScale > 0
                        ? characterEntry.modelScale
                        : null;

                const HELPER_NAME_RE =
                    /(^|[_\-.])(helper|locator|empty|null|target|aim|ref|guide)([_\-.]|$)/i;

                // Helper: union per-mesh world-space bboxes from VISIBLE
                // mesh/skinnedMesh nodes only (skipping helper-name nodes).
                // Returns { box, count } where box is a THREE.Box3.
                const measureVisibleMeshBox = () => {
                    this.model.updateMatrixWorld(true);
                    const out = new THREE.Box3();
                    const tmp = new THREE.Box3();
                    let n = 0;
                    this.model.traverseVisible((child) => {
                        if (!child) return;
                        if (!(child.isMesh || child.isSkinnedMesh)) return;
                        if (child.name && HELPER_NAME_RE.test(child.name)) {
                            return;
                        }
                        const geom = child.geometry;
                        if (!geom) return;
                        if (!geom.boundingBox) {
                            try {
                                geom.computeBoundingBox();
                            } catch (e) {
                                return;
                            }
                        }
                        if (!geom.boundingBox) return;
                        tmp.copy(geom.boundingBox);
                        tmp.applyMatrix4(child.matrixWorld);
                        if (
                            !Number.isFinite(tmp.min.x) ||
                            !Number.isFinite(tmp.max.x)
                        ) {
                            return;
                        }
                        out.union(tmp);
                        n += 1;
                    });
                    return { box: out, count: n };
                };

                // Helper: re-measure post-scale and store the floor offset
                // (distance from current bottom to y=0) on this so the pose
                // system can add it to pose.modelY. Runs for ALL scale
                // paths (manual override, auto-fit, fallback).
                const recordFloorOffset = (label) => {
                    const { box: scaledBox, count } = measureVisibleMeshBox();
                    if (
                        !scaledBox.isEmpty() &&
                        Number.isFinite(scaledBox.min.y) &&
                        count > 0
                    ) {
                        this._glbFloorOffsetY = -scaledBox.min.y;
                        console.log(
                            `[${formatTag}] ${label}: floor offset y+=${this._glbFloorOffsetY.toFixed(2)} (post-scale bottom=${scaledBox.min.y.toFixed(2)})`,
                        );
                    } else {
                        this._glbFloorOffsetY = 0;
                    }
                };

                // Apply a scale, clamp it for safety, log the decision and
                // record the post-scale floor offset so the pose system can
                // pin the feet at y=0 regardless of native model pivot.
                const applyScale = (scale, label, extraInfo) => {
                    const clamped = Math.min(
                        Math.max(scale, SCALE_MIN),
                        SCALE_MAX,
                    );
                    if (clamped !== scale) {
                        console.warn(
                            `[${formatTag}] ${label}: clamped scale ${scale} → ${clamped} (range ${SCALE_MIN}..${SCALE_MAX})`,
                        );
                    }
                    this.model.scale.set(clamped, clamped, clamped);
                    console.log(
                        `[${formatTag}] ${label}: scale=${clamped.toFixed(4)}x` +
                            (extraInfo ? ` ${extraInfo}` : ""),
                    );
                    recordFloorOffset(label);
                };

                if (manualScale !== null) {
                    applyScale(
                        manualScale,
                        "Manual scale",
                        `(from characterData.modelScale)`,
                    );
                } else {
                    // Path #2: eye-height auto-fit. Refresh world matrices
                    // first so skinned bones report real world positions
                    // on the very first frame.
                    let nativeEyeHeight = null;
                    try {
                        this.model.updateMatrixWorld(true);
                        // Measure eye height in the model's own local
                        // space at native scale. detectEyeHeight() walks
                        // the skeleton (lefteye/righteye/eye/head) and
                        // returns worldY-baseY; with the model unparented
                        // and at scale 1, that equals the local Y of the
                        // eye node above the model's origin.
                        // skeletonOnly:true so we do NOT silently fall
                        // back to a bounding-box estimate here — that
                        // would mask hat/hair/prop bbox inflation, which
                        // is exactly the case path #3 (body-height
                        // auto-fit on visible meshes only) handles
                        // properly.
                        const detected = detectEyeHeight(this.model, {
                            skeletonOnly: true,
                        });
                        if (Number.isFinite(detected) && detected > 0) {
                            nativeEyeHeight = detected;
                        }
                    } catch (e) {
                        console.warn(
                            `[${formatTag}] Auto-fit: eye-height probe failed:`,
                            e,
                        );
                    }

                    if (nativeEyeHeight !== null) {
                        const autoScale =
                            REFERENCE_EYE_HEIGHT / nativeEyeHeight;
                        if (Number.isFinite(autoScale) && autoScale > 0) {
                            applyScale(
                                autoScale,
                                "Auto-fit (eye-height)",
                                `nativeEye=${nativeEyeHeight.toFixed(3)} → target=${REFERENCE_EYE_HEIGHT}`,
                            );
                        } else {
                            nativeEyeHeight = null;
                        }
                    }

                    if (nativeEyeHeight === null) {
                        // Path #3: body-height auto-fit. Measure ONLY
                        // visible meshes (skip helpers/empties/lights/
                        // cameras that some GLBs ship with — those would
                        // otherwise inflate the box and make the
                        // character come out miniature).
                        const measured = measureVisibleMeshBox();
                        let box = measured.box;
                        const measuredMeshCount = measured.count;
                        if (measuredMeshCount === 0 || box.isEmpty()) {
                            box = new THREE.Box3().setFromObject(this.model);
                            console.warn(
                                `[${formatTag}] Auto-fit: no visible meshes found, falling back to full-object Box3`,
                            );
                        }

                        const sizeX = box.max.x - box.min.x;
                        const sizeY = box.max.y - box.min.y;
                        const sizeZ = box.max.z - box.min.z;

                        const yOk =
                            Number.isFinite(sizeY) &&
                            sizeY > 0.001 &&
                            sizeY < 100000;

                        let autoScale = null;
                        let scaleAxis = "Y";
                        if (yOk) {
                            autoScale = REFERENCE_BODY_HEIGHT / sizeY;
                        } else {
                            // Path #4: largest-axis fallback for
                            // degenerate Y (lying-down rigs, T-pose with
                            // extreme arm spread, etc).
                            const largest = Math.max(
                                Number.isFinite(sizeX) ? sizeX : 0,
                                Number.isFinite(sizeY) ? sizeY : 0,
                                Number.isFinite(sizeZ) ? sizeZ : 0,
                            );
                            if (largest > 0.001 && largest < 100000) {
                                autoScale = REFERENCE_BODY_HEIGHT / largest;
                                scaleAxis = "MAX";
                                console.warn(
                                    `[${formatTag}] Auto-fit: degenerate Y (${sizeY}); using largest axis instead`,
                                );
                            }
                        }

                        if (
                            autoScale === null ||
                            !Number.isFinite(autoScale) ||
                            autoScale <= 0
                        ) {
                            // Path #5: hard fallback. Match the legacy
                            // "scale by 100" behaviour so a broken
                            // measurement still produces a visible
                            // character roughly in frame.
                            applyScale(
                                100,
                                "Legacy fallback scale",
                                `(degenerate bbox x=${sizeX} y=${sizeY} z=${sizeZ})`,
                            );
                        } else {
                            applyScale(
                                autoScale,
                                "Auto-fit (body-height)",
                                `meshes=${measuredMeshCount} size x=${sizeX.toFixed(3)} y=${sizeY.toFixed(3)} z=${sizeZ.toFixed(3)} axis=${scaleAxis} target=${REFERENCE_BODY_HEIGHT.toFixed(2)}`,
                            );
                        }
                    }
                }
            }

            // FBX-DIAG2 — post-auto-fit snapshot (Task #91). Shows
            // what the auto-fit block produced: final model.scale,
            // EyeAnchor world position, visible-mesh bbox, floor
            // offset. If EyeAnchor world Y is sane here but absurd
            // at framing time, the offender is downstream (mixer,
            // applyPose). If absurd here, the auto-fit scale is the
            // culprit.
            if (
                FBX_DIAG_ENABLED &&
                this.currentModelFormat !== "glb" &&
                this.model
            ) {
                try {
                    const fmt = (n) =>
                        typeof n === "number" && Number.isFinite(n)
                            ? n.toFixed(1)
                            : String(n);
                    const fmtVec = (v) =>
                        v ? `(${fmt(v.x)},${fmt(v.y)},${fmt(v.z)})` : "(n/a)";
                    this.model.updateMatrixWorld(true);
                    const visBox2 = new THREE.Box3();
                    const tmpBox2 = new THREE.Box3();
                    let visMeshCount2 = 0;
                    this.model.traverseVisible((child) => {
                        if (!child) return;
                        if (!(child.isMesh || child.isSkinnedMesh)) return;
                        const geom = child.geometry;
                        if (!geom || !geom.boundingBox) return;
                        tmpBox2.copy(geom.boundingBox);
                        tmpBox2.applyMatrix4(child.matrixWorld);
                        if (!Number.isFinite(tmpBox2.min.x)) return;
                        visBox2.union(tmpBox2);
                        visMeshCount2 += 1;
                    });
                    const eaNode = (() => {
                        let found = null;
                        this.model.traverse((n) => {
                            if (!n || !n.name || found) return;
                            const lc = n.name.toLowerCase();
                            if (lc === "eyeanchor" || lc === "eye_anchor")
                                found = n;
                        });
                        return found;
                    })();
                    const eaWorld = new THREE.Vector3();
                    if (eaNode) eaNode.getWorldPosition(eaWorld);
                    console.log(
                        `[FBX-DIAG2] post-auto-fit model.scale=${fmtVec(this.model.scale)} ` +
                            `model.position=${fmtVec(this.model.position)} ` +
                            `_glbFloorOffsetY=${fmt(this._glbFloorOffsetY)} ` +
                            `EyeAnchor.world=${eaNode ? fmtVec(eaWorld) : "(absent)"} ` +
                            `visBox(meshes=${visMeshCount2}) min=${fmtVec(visBox2.min)} max=${fmtVec(visBox2.max)} ` +
                            `sizeY=${fmt(visBox2.max.y - visBox2.min.y)}`,
                    );
                } catch (d2err) {
                    console.warn("[FBX-DIAG2] post-auto-fit dump failed:", d2err);
                }
            }

            // ---------------------------------------------------------------
            // Skeleton consolidation pass (beard-deformation fix).
            //
            // Symptom: on some FBX rigs (Leonardo da Vinci's LeoFBXFALL.fbx
            // is the canonical case), accessory meshes like the beard
            // deform during body animation, but the deformation disappears
            // while the character is speaking (A2F driving blendshapes)
            // and reappears once speech ends. The same FBX renders cleanly
            // in Maya / other FBX viewers.
            //
            // Root cause: the accessory mesh (beard) is a separate
            // SkinnedMesh that arrived with its OWN Skeleton instance
            // referencing its own private clones of bones with the same
            // names as the head's bones (Head, Jaw, Neck, …). The body
            // animation clip's tracks resolve to the body skeleton's
            // bones, so the body bones animate but the beard's private
            // bones never receive any animation track — they stay in bind
            // pose while the head bone moves, and the beard tears away
            // from the face.
            //
            // Fix: detect every SkinnedMesh whose skeleton's bones overlap
            // by NAME with a "primary" skeleton (the one with the most
            // bones, i.e. the body rig), then rebuild its bones array
            // using the primary skeleton's actual bone references, and
            // rebind. After this, the same animation tracks that move the
            // head bone also move the beard's view of the head bone, so
            // the beard follows the face correctly.
            //
            // This is purely additive — meshes whose bones do not overlap
            // with the primary skeleton are left untouched, so non-rigged
            // accessories or differently-rigged sub-models are not
            // affected.
            // ---------------------------------------------------------------
            try {
                const skinnedMeshes = [];
                this.model.traverse((o) => {
                    if (o && o.isSkinnedMesh && o.skeleton) {
                        skinnedMeshes.push(o);
                    }
                });

                if (skinnedMeshes.length >= 2) {
                    // Group by Skeleton instance identity so meshes that
                    // already share a skeleton (the common case) are
                    // counted as a single rig.
                    const skeletonGroups = new Map();
                    for (const m of skinnedMeshes) {
                        if (!skeletonGroups.has(m.skeleton)) {
                            skeletonGroups.set(m.skeleton, []);
                        }
                        skeletonGroups.get(m.skeleton).push(m);
                    }

                    if (skeletonGroups.size >= 2) {
                        // Primary = skeleton with the most bones. For
                        // humanoid rigs this is the body skeleton; the
                        // beard skeleton typically has a small subset
                        // (Head / Jaw / Neck plus a few beard bones).
                        let primarySkeleton = null;
                        let primaryMembers = null;
                        let primaryBoneCount = -1;
                        for (const [skel, members] of skeletonGroups) {
                            const count = skel.bones ? skel.bones.length : 0;
                            if (count > primaryBoneCount) {
                                primaryBoneCount = count;
                                primarySkeleton = skel;
                                primaryMembers = members;
                            }
                        }

                        // Build a name → bone lookup from the primary
                        // skeleton so we can rebuild the secondary bone
                        // arrays using primary-skeleton bone references.
                        const primaryBonesByName = new Map();
                        if (primarySkeleton && primarySkeleton.bones) {
                            for (const b of primarySkeleton.bones) {
                                if (b && b.name && !primaryBonesByName.has(b.name)) {
                                    primaryBonesByName.set(b.name, b);
                                }
                            }
                        }

                        const formatTag2 =
                            (this.currentModelFormat || "model").toUpperCase();
                        let rebindCount = 0;

                        for (const [skel, members] of skeletonGroups) {
                            if (skel === primarySkeleton) continue;
                            if (!skel.bones || skel.bones.length === 0) continue;

                            // Count how many of this secondary skeleton's
                            // bones have a same-named counterpart in the
                            // primary skeleton. A high overlap means this
                            // is the "same rig, cloned for one mesh" case
                            // that causes the beard-deformation bug.
                            let matched = 0;
                            const newBones = [];
                            for (const b of skel.bones) {
                                if (b && b.name && primaryBonesByName.has(b.name)) {
                                    newBones.push(primaryBonesByName.get(b.name));
                                    matched += 1;
                                } else {
                                    // Keep the original bone for any
                                    // unique-to-secondary bones (e.g.
                                    // beard tip bones with no primary
                                    // analog) so skinning stays valid.
                                    newBones.push(b);
                                }
                            }

                            const overlapRatio = matched / skel.bones.length;

                            // Require at least 50% bone-name overlap before
                            // rebinding — anything less probably isn't the
                            // same rig and we'd risk breaking a legitimate
                            // standalone accessory.
                            if (overlapRatio >= 0.5 && matched >= 2) {
                                // Build a fresh Skeleton out of the
                                // resolved bones (preserving inverse bind
                                // matrices from the secondary skeleton so
                                // each mesh's authored bind pose is kept).
                                const newSkeleton = new THREE.Skeleton(
                                    newBones,
                                    skel.boneInverses
                                        ? skel.boneInverses.slice()
                                        : undefined,
                                );

                                for (const mesh of members) {
                                    // Re-bind preserving the mesh's
                                    // current bind matrix so its authored
                                    // rest pose stays correct.
                                    mesh.bind(newSkeleton, mesh.bindMatrix);
                                    rebindCount += 1;
                                    console.log(
                                        `[${formatTag2}] Skeleton consolidation: rebound "${mesh.name || "(unnamed)"}" ` +
                                            `to primary skeleton (overlap ${matched}/${skel.bones.length} = ${(overlapRatio * 100).toFixed(0)}%)`,
                                    );
                                }
                            } else {
                                console.log(
                                    `[${formatTag2}] Skeleton consolidation: skipping group with low overlap ` +
                                        `(${matched}/${skel.bones.length} = ${(overlapRatio * 100).toFixed(0)}%) — not the same rig`,
                                );
                            }
                        }

                        if (rebindCount > 0) {
                            console.log(
                                `[${formatTag2}] Skeleton consolidation: rebound ${rebindCount} mesh(es) to primary skeleton ` +
                                    `(primary bones=${primaryBoneCount}, primary meshes=${primaryMembers ? primaryMembers.length : 0})`,
                            );
                            this.model.updateMatrixWorld(true);
                        }
                    }
                }
            } catch (consolidateErr) {
                console.warn(
                    "Skeleton consolidation pass failed (continuing without it):",
                    consolidateErr,
                );
            }

            // ---------------------------------------------------------------
            // Embedded-backdrop strip pass (FBX only).
            //
            // Some FBX assets (e.g. Karl Marx's `KarlBestV1ALL.fbx` —
            // the "ALL" suffix is the tell) ship the character together
            // with non-character scenery baked into the same scene:
            // backdrops, cyc walls, studio planes, environment domes,
            // etc. When we add the model to the scene these meshes sit
            // close to the camera and occlude our dedicated sky sphere
            // (radius 1000, depthWrite:false, renderOrder:-1), making
            // the Light/Dark sky toggle look dead because the sphere
            // really is recolouring, the user just can't see it past
            // the baked backdrop.
            //
            // Detection is intentionally conservative — we never want
            // to delete a piece of the character. A mesh is stripped
            // only if it is a plain Mesh (NOT a SkinnedMesh, so it has
            // no skeleton binding to the body rig) AND it has no morph
            // targets AND either (a) its name matches a strong backdrop
            // token, (b) its scaled-world bbox is larger in any axis
            // than ~3× the character's body height, or (c) its name
            // matches a weak/ambiguous backdrop token AND it is also
            // size-flagged.
            //
            // Runs AFTER the skeleton consolidation pass so the body
            // rig is fully resolved before we measure body height from
            // its SkinnedMesh union (per task #78 sequencing).
            //
            // GLB is skipped entirely — the symptom has only ever been
            // observed on FBX, and GLB scenery embeddings are vanishing-
            // ly rare in this codebase.
            // ---------------------------------------------------------------
            if (this.currentModelFormat !== "glb") {
                try {
                    // High-confidence tokens: stripped on name alone.
                    // These names are essentially never used for a piece
                    // of a character (no legitimate character mesh is
                    // called "Backdrop" or "CycWall").
                    const STRONG_BACKDROP_NAME_RE =
                        /(^|[_\-.\s])(backdrop|background|backwall|cyc|cyclorama|skydome|sky_dome|backplate|studio_bg|studiobg)([_\-.\s]|$)/i;
                    // Weak tokens: common but ambiguous. A mesh whose
                    // name matches one of these is only stripped if its
                    // bbox ALSO exceeds the size threshold, so a small
                    // prop called e.g. "stage_prop" survives.
                    const WEAK_BACKDROP_NAME_RE =
                        /(^|[_\-.\s])(environment|env|studio|wall|stage|plane|floor_mesh|floorplane|ground|ground_mesh)([_\-.\s]|$)/i;

                    this.model.updateMatrixWorld(true);

                    // 1) Character body height: union of all
                    //    SkinnedMesh world-space bboxes. SkinnedMesh
                    //    nodes are by definition rigged to the body
                    //    skeleton and therefore part of the character.
                    const bodyBox = new THREE.Box3();
                    const _tmp = new THREE.Box3();
                    let bodyMeshCount = 0;
                    this.model.traverse((child) => {
                        if (!child || !child.isSkinnedMesh) return;
                        const geom = child.geometry;
                        if (!geom) return;
                        if (!geom.boundingBox) {
                            try {
                                geom.computeBoundingBox();
                            } catch (e) {
                                return;
                            }
                        }
                        if (!geom.boundingBox) return;
                        _tmp.copy(geom.boundingBox);
                        _tmp.applyMatrix4(child.matrixWorld);
                        if (
                            !Number.isFinite(_tmp.min.y) ||
                            !Number.isFinite(_tmp.max.y)
                        ) {
                            return;
                        }
                        bodyBox.union(_tmp);
                        bodyMeshCount += 1;
                    });

                    const bodyHeight =
                        bodyMeshCount > 0 && !bodyBox.isEmpty()
                            ? bodyBox.max.y - bodyBox.min.y
                            : 0;
                    const SIZE_THRESHOLD = bodyHeight > 0
                        ? bodyHeight * 3
                        : Infinity;

                    // 2) Walk every mesh and decide whether to strip it.
                    //    Collect first, then strip — never mutate the
                    //    scene graph mid-traverse.
                    const toStrip = [];
                    this.model.traverse((child) => {
                        if (!child) return;
                        // SkinnedMesh = character geometry, never touch.
                        if (child.isSkinnedMesh) return;
                        if (!child.isMesh) return;
                        // Anything with morph targets is a facial mesh
                        // or similar — leave it alone.
                        if (
                            child.morphTargetDictionary &&
                            Object.keys(child.morphTargetDictionary).length > 0
                        ) {
                            return;
                        }
                        if (
                            Array.isArray(child.morphTargetInfluences) &&
                            child.morphTargetInfluences.length > 0
                        ) {
                            return;
                        }

                        const name = child.name || "";
                        const strongName = STRONG_BACKDROP_NAME_RE.test(name);
                        const weakName = !strongName &&
                            WEAK_BACKDROP_NAME_RE.test(name);

                        let sizeMatch = false;
                        let sizeInfo = "";
                        const geom = child.geometry;
                        if (geom) {
                            if (!geom.boundingBox) {
                                try {
                                    geom.computeBoundingBox();
                                } catch (e) {
                                    /* ignore */
                                }
                            }
                            if (geom.boundingBox) {
                                _tmp.copy(geom.boundingBox);
                                _tmp.applyMatrix4(child.matrixWorld);
                                const sx = _tmp.max.x - _tmp.min.x;
                                const sy = _tmp.max.y - _tmp.min.y;
                                const sz = _tmp.max.z - _tmp.min.z;
                                if (
                                    Number.isFinite(sx) &&
                                    Number.isFinite(sy) &&
                                    Number.isFinite(sz)
                                ) {
                                    const largest = Math.max(sx, sy, sz);
                                    if (largest > SIZE_THRESHOLD) {
                                        sizeMatch = true;
                                    }
                                    sizeInfo = `bbox ${sx.toFixed(0)}x${sy.toFixed(0)}x${sz.toFixed(0)}`;
                                }
                            }
                        }

                        // Strip on: strong-name match (unconditional),
                        // OR size match alone, OR weak-name match
                        // confirmed by size. Weak name without big
                        // size = leave alone.
                        const shouldStrip =
                            strongName || sizeMatch || (weakName && sizeMatch);
                        if (shouldStrip) {
                            const reason = strongName
                                ? "name-strong"
                                : weakName && sizeMatch
                                  ? "name-weak+size"
                                  : "size";
                            toStrip.push({
                                node: child,
                                name,
                                reason,
                                sizeInfo,
                            });
                        }
                    });

                    for (const entry of toStrip) {
                        const { node, name, reason, sizeInfo } = entry;
                        if (node.parent) {
                            node.parent.remove(node);
                        }
                        try {
                            if (node.geometry && node.geometry.dispose) {
                                node.geometry.dispose();
                            }
                            const mat = node.material;
                            if (Array.isArray(mat)) {
                                for (const m of mat) {
                                    if (m && m.dispose) m.dispose();
                                }
                            } else if (mat && mat.dispose) {
                                mat.dispose();
                            }
                        } catch (e) {
                            /* dispose errors are non-fatal */
                        }
                        console.log(
                            `[FBX] Stripped backdrop mesh "${name || "(unnamed)"}" (reason=${reason}, ${sizeInfo || "no bbox"}, bodyHeight=${bodyHeight.toFixed(2)})`,
                        );
                    }

                    if (toStrip.length === 0) {
                        console.log(
                            `[FBX] Backdrop strip pass: no scenery meshes detected (bodyHeight=${bodyHeight.toFixed(2)}, threshold=${
                                Number.isFinite(SIZE_THRESHOLD)
                                    ? SIZE_THRESHOLD.toFixed(2)
                                    : "n/a"
                            })`,
                        );
                    }
                } catch (e) {
                    console.warn(
                        "[FBX] Backdrop strip pass failed (continuing without it):",
                        e,
                    );
                }
            }

            console.log("Model loaded successfully, processing materials...");

            if (loadingText) loadingText.textContent = "Processing model...";

            let meshCount = 0;
            const meshPromises = [];
            const isGLBFormat = this.currentModelFormat === "glb";
            const useNativeMaterials =
                isGLBFormat || shouldUseNativeMaterials(this.currentModelId);

            if (isGLBFormat) {
                console.log(
                    `[GLB] Preserving native GLB PBR materials for model ${this.currentModelId}`,
                );
            } else if (useNativeMaterials) {
                console.log(
                    `[NativeMaterial] Bypassing PBRMaterialManager for model ${this.currentModelId}`,
                );
            }

            this.model.traverse((child) => {
                if (child.isMesh) {
                    meshCount++;
                    console.log("Processing mesh:", child.name);

                    const meshName = child.name.toLowerCase();
                    const shouldCastShadow =
                        meshName.includes("head") || meshName.includes("teeth");

                    child.castShadow = shouldCastShadow;
                    child.receiveShadow = shouldCastShadow;

                    console.log(
                        `Mesh "${child.name}" - castShadow: ${shouldCastShadow}, receiveShadow: true`,
                    );

                    if (useNativeMaterials) {
                        if (isGLBFormat) {
                            // Task #331: physical-material shader permutations
                            // are engine-dependent (meshes vanish on some
                            // Chrome/ANGLE + software-GL backends). Preemptive
                            // downgrade only where physical is known-unusable
                            // (software GL / previously-failed device).
                            // Phase 2: desktop Chromium runs a one-time
                            // preflight compile test and keeps the premium
                            // physical shader when it passes. Anyone who keeps
                            // physical is guarded by the health watch in
                            // animate() (self-heal + remember). Safari/iPad:
                            // untouched full quality.
                            const materialRisk =
                                physicalMaterialsRisky() ||
                                (isDesktopChromiumUA() &&
                                physicalPreflightFailed(this.renderer)
                                    ? "chromium-preflight"
                                    : null);
                            if (materialRisk) {
                                downgradePhysicalMaterials(child);
                            } else {
                                const childMats = Array.isArray(child.material)
                                    ? child.material
                                    : [child.material];
                                if (
                                    childMats.some(
                                        (m) => m && m.isMeshPhysicalMaterial,
                                    )
                                ) {
                                    this._physicalHealthWatch = 0; // arm the watch
                                }
                            }
                        }
                        logNativeMaterialInfo(child);
                    } else {
                        meshPromises.push(
                            this.materialManager
                                .setupMaterialForMesh(child, this.scene)
                                .catch((error) => {
                                    console.warn(
                                        "Error setting up material for mesh:",
                                        child.name,
                                        error,
                                    );
                                }),
                        );
                    }

                    if (child.morphTargetDictionary) {
                        Object.entries(child.morphTargetDictionary).forEach(
                            ([name, index]) => {
                                const entry = {
                                    mesh: child,
                                    index: index,
                                    value: 0,
                                };
                                if (!this.blendShapeControls[name]) {
                                    this.blendShapeControls[name] = entry;
                                } else if (
                                    Array.isArray(this.blendShapeControls[name])
                                ) {
                                    this.blendShapeControls[name].push(entry);
                                } else {
                                    this.blendShapeControls[name] = [
                                        this.blendShapeControls[name],
                                        entry,
                                    ];
                                }
                            },
                        );
                    }
                }
            });

            if (meshCount === 0) {
                console.warn("Model contains no meshes, but continuing...");
            }

            await Promise.all(meshPromises);
            if (!isCurrentLoad()) {
                this.cleanupCurrentModel();
                return false;
            }
            console.log("Materials processed successfully");
            updateMaterialize("setProgress", 93);

            // Task #340: stamp every material's authored blend state at
            // load (after material setup / preemptive downgrade), so the
            // settle safety net has its source of truth on every later
            // model-ready path. Nothing may zero these opacities at load —
            // the character's first painted frame is its solid, final look.
            this._stampModelAuthoredState(this.model);

            if (loadingText)
                loadingText.textContent = "Setting up character animations...";
            if (progressBar) progressBar.style.width = "95%";

            // CRITICAL: Register greeting event listener BEFORE setupCharacter() to prevent race condition
            // setupCharacter() calls setupAnimations() which triggers spawn animation
            // The spawn animation dispatches 'spawnAnimationComplete' when finished
            // If we register the listener after setupCharacter(), the event may fire before we're listening (especially in Safari)
            let greetingTriggered = false;
            let handleSpawnComplete = null;

            // Doc-born characters exist only in window.__docCharacters
            // (synthesized by characterData.getCharacterById), never as
            // hand-wired keys of the characterData map — a direct map lookup
            // here silently skipped the ENTIRE greeting trigger for them:
            // total silence on /lab even with voice + persona + expertise all
            // audit-green. Resolve through the doc-born-aware helper and keep
            // the record for the legacy fallback below.
            const greetingCharacterRecord =
                this.currentModelId &&
                (characterData[this.currentModelId] ||
                    (typeof characterData.getCharacterById === "function"
                        ? characterData.getCharacterById(this.currentModelId)
                        : null));

            if (this.currentModelId && greetingCharacterRecord) {
                // Helper function to wait for ChatController to be ready
                const waitForChatController = (
                    maxWaitMs = 10000,
                    checkIntervalMs = 100,
                ) => {
                    return new Promise((resolve) => {
                        const startTime = Date.now();

                        const checkController = () => {
                            if (
                                window.chatController &&
                                typeof window.chatController
                                    .sendDynamicGreeting === "function"
                            ) {
                                console.log(
                                    "🎉 [DynamicGreeting] ChatController is ready",
                                );
                                resolve(true);
                                return;
                            }

                            if (Date.now() - startTime >= maxWaitMs) {
                                console.warn(
                                    "🎉 [DynamicGreeting] Timeout waiting for ChatController after",
                                    maxWaitMs,
                                    "ms",
                                );
                                resolve(false);
                                return;
                            }

                            setTimeout(checkController, checkIntervalMs);
                        };

                        checkController();
                    });
                };

                // Task #199: don't start the greeting (and, on Safari, its
                // "Tap to hear ..." permission card) while a character-picker
                // overlay is still up — it must be the very last beat, after
                // the model has rendered and the Hall/selector has settled.
                const waitForStageClear = () =>
                    new Promise((resolve) => {
                        if (!this._isPickerOpen()) {
                            resolve();
                            return;
                        }
                        // belt-and-braces — alongside the
                        // MutationObserver, poll the body classes every 500ms
                        // so a missed/foreign mutation can never leave the
                        // greeting waiting forever (a silent no-greeting path).
                        // The greeting still (correctly) waits as long as a
                        // picker is genuinely open.
                        let poll = null;
                        const finish = () => {
                            if (poll !== null) {
                                clearInterval(poll);
                                poll = null;
                            }
                            obs.disconnect();
                            resolve();
                        };
                        const obs = new MutationObserver(() => {
                            if (this._isPickerOpen()) return;
                            finish();
                        });
                        try {
                            obs.observe(document.body, {
                                attributes: true,
                                attributeFilter: ["class"],
                            });
                            poll = setInterval(() => {
                                if (!this._isPickerOpen()) finish();
                            }, 500);
                        } catch (e) {
                            resolve();
                        }
                    });

                // Helper function to trigger the dynamic greeting
                const triggerDynamicGreeting = async (modelId) => {
                    await waitForStageClear();
                    console.log(
                        "🎉 [DynamicGreeting] Waiting for ChatController...",
                    );
                    const chatControllerReady = await waitForChatController();

                    if (chatControllerReady) {
                        console.log(
                            "🎉 [DynamicGreeting] Using dynamic greeting system via ChatController",
                        );
                        await window.chatController.sendDynamicGreeting();
                    } else {
                        // Fallback to old greeting service if ChatController not available after timeout
                        console.log(
                            "🎉 [DynamicGreeting] ChatController not available, falling back to static greeting",
                        );
                        await this.greetingService.playGreeting(
                            characterData[modelId] || greetingCharacterRecord,
                        );
                    }
                };

                handleSpawnComplete = async (event) => {
                    // Make sure this is for our current model and greeting hasn't been triggered yet
                    if (
                        event.detail.modelId !== this.currentModelId ||
                        greetingTriggered
                    ) {
                        return;
                    }

                    console.log(
                        "Spawn animation complete, triggering dynamic greeting for model:",
                        this.currentModelId,
                    );

                    // Mark greeting as triggered to prevent double execution
                    greetingTriggered = true;

                    try {
                        // Remove the event listener after it fires once
                        document.removeEventListener(
                            "spawnAnimationComplete",
                            handleSpawnComplete,
                        );

                        // Use the new dynamic greeting system via ChatController with waiting
                        await triggerDynamicGreeting(this.currentModelId);

                        // NOTE: No need to manually return to idle animation here
                        // AnimationController.onTTSEnd() already handles this
                    } catch (error) {
                        console.warn("Error playing greeting:", error);
                        // Continue even if greeting fails
                    }
                };

                // Add the event listener BEFORE setupCharacter() to catch the spawn complete event
                document.addEventListener(
                    "spawnAnimationComplete",
                    handleSpawnComplete,
                );
                console.log(
                    "Registered spawn completion listener for dynamic greeting playback (BEFORE setupCharacter)",
                );

                // Safari fallback: If no spawn event after 3 seconds, trigger greeting anyway
                setTimeout(async () => {
                    // Check if we haven't already triggered or played the greeting
                    if (
                        !greetingTriggered &&
                        !this.greetingService.isPlaying &&
                        !this.greetingService.greetingPlayed
                    ) {
                        console.log(
                            "Safari fallback: Triggering dynamic greeting after timeout",
                        );
                        greetingTriggered = true; // Mark as triggered to prevent double execution

                        // Remove the spawn event listener since we're triggering via timeout
                        if (handleSpawnComplete) {
                            document.removeEventListener(
                                "spawnAnimationComplete",
                                handleSpawnComplete,
                            );
                        }

                        // Use dynamic greeting system with waiting
                        try {
                            await triggerDynamicGreeting(this.currentModelId);
                        } catch (err) {
                            console.warn(
                                "Safari fallback dynamic greeting failed:",
                                err,
                            );
                        }
                    }
                }, 3000);
            }

            // Now call setupCharacter() - the spawn animation will trigger the event we're already listening for
            await this.setupCharacter();
            if (!isCurrentLoad()) {
                this.cleanupCurrentModel();
                return false;
            }
            console.log("Character setup complete");

            if (loadingText) loadingText.textContent = "Character ready!";
            if (progressBar) progressBar.style.width = "100%";
            updateMaterialize("setProgress", 97);
            updateMaterialize("setPhase", "complete");

            // Initialize the blue sparkle particle system for the model
            this.setupParticleSystem();
            console.log("Blue particle effect initialized");

            // Notify the ModelManager that the model has been fully loaded
            if (this.modelManager) {
                console.log(
                    "Notifying ModelManager that model has loaded:",
                    this.currentModelId,
                );
                this.modelManager.notifyModelLoaded(this.currentModelId);
            }

            const modelLoadingComplete = new Event("modelLoadingComplete");
            document.dispatchEvent(modelLoadingComplete);
            return true;
        } catch (error) {
            console.error("Error in model loading:", error);
            // A superseded request may fail after a newer character has
            // already started. Its error is diagnostic only; it must not
            // surface the global error toast over the current loader.
            if (!isCurrentLoad()) {
                console.warn("Ignoring stale model-load failure");
                return false;
            }
            if (errorOverlay) {
                errorOverlay.classList.remove("d-none");
                errorOverlay.querySelector(".alert").textContent =
                    `Warning: Model may have loading issues: ${error.message || "Unknown error occurred"}`;
            }
            // Don't throw error, just log it and continue
            console.warn("Continuing despite model loading error:", error);
            return false;
        } finally {
            if (loadingOverlay) loadingOverlay.style.display = "none";
            this.isLoading = false;
        }
    }

    async setupCharacter() {
        console.log("Starting character setup");
        if (!this.model) {
            console.error("No model loaded");
            throw new Error("No model loaded");
        }

        // Verify model ID is available
        if (!this.currentModelKey) {
            console.error("Model ID missing during character setup");
            throw new Error("Model ID is required for character setup");
        }
        console.log("Setting up character for model ID:", this.currentModelKey);

        const expectedModelId = this.currentModelKey;

        // Pre-frame the camera with the static SCENE_FRAMING defaults
        // so we don't ship a one-frame flash of the previous character's
        // framing. The real per-character framing is applied inside
        // applyPose's commit() callback once the model is at its final
        // world position (see _applyEyeBasedFraming).
        if (this.controls) {
            this.camera.position.set(
                SCENE_FRAMING.cameraPosition.x,
                SCENE_FRAMING.cameraPosition.y,
                SCENE_FRAMING.cameraPosition.z,
            );
            this.controls.target.set(
                SCENE_FRAMING.cameraTarget.x,
                SCENE_FRAMING.cameraTarget.y,
                SCENE_FRAMING.cameraTarget.z,
            );
            this.controls.update();
            if (this._zoomState) {
                this._zoomState.baselineCameraY = this.camera.position.y;
                this._zoomState.baselineTargetY = this.controls.target.y;
                this._zoomState.currentLerpT = 0;
                this._zoomState.targetLerpT = 0;
                this._zoomState.lastRadius = null;
                this._zoomState.hasTargets = false;
            }
        }

        // Place model at the initial pose Y (cache or config) and add it
        // to the scene HIDDEN so the skeleton's worldMatrix is valid for
        // detection but the user never sees the un-corrected pose.
        const initialPose = computePose(expectedModelId);
        const initialFloorOffset = Number.isFinite(this._glbFloorOffsetY)
            ? this._glbFloorOffsetY
            : 0;
        this.model.position.set(0, initialPose.modelY + initialFloorOffset, 0);
        this.model.visible = false;
        this.scene.add(this.model);

        // Cache-first detection: skip if manually locked or we already
        // have a cached detected eye height for this character.
        const cachedCfg = getPoseConfig(expectedModelId);
        const haveCached =
            cachedCfg.manualLock ||
            (Number.isFinite(cachedCfg.detectedEyeHeight) &&
                cachedCfg.detectedEyeHeight > 0);

        let runtimeEyeHeight = null;
        if (!haveCached) {
            try {
                const detected = detectEyeHeight(this.model);
                if (
                    Number.isFinite(detected) &&
                    detected > 0 &&
                    expectedModelId &&
                    CHARACTER_POSES[expectedModelId] &&
                    !isManuallyLocked(expectedModelId)
                ) {
                    runtimeEyeHeight = detected;
                    saveOverride(expectedModelId, {
                        detectedEyeHeight: detected,
                    });
                    console.log(
                        `[setupCharacter] cached detected eye height ` +
                        `for ${expectedModelId}: ${detected.toFixed(2)}`,
                    );
                }
            } catch (e) {
                console.warn("[setupCharacter] eye-height detection failed:", e);
            }
        }

        // Atomic platform + model commit. On first detection we pass the
        // freshly-detected value so the source tag reads "runtime"; on
        // subsequent loads it reads "cache".
        this.applyPose(
            expectedModelId,
            runtimeEyeHeight ? { runtimeEyeHeight } : null,
        );

        // Task #552 follow-on (post-100% gap): pre-warm the GPU while the
        // model is still hidden behind the title card. The all-specular
        // physical materials otherwise compile their shader programs on
        // the first VISIBLE frame — a main-thread stall landing exactly on
        // the reveal hard cut. compile() traverses the passed subtree
        // without checking visibility, and KHR_parallel_shader_compile
        // lets the driver link in the background while blend shapes and
        // animations finish setting up. Fire-and-forget, strictly
        // fail-open, stale-guarded; never touches visibility, materials,
        // or the reveal contract.
        this._warmGpuForModel(this.model);

        await this.initializeBlendShapes();
        console.log(
            "Blend shapes initialized:",
            Object.keys(this.blendShapeControls),
        );

        const hasAnimations =
            this.model.animations && this.model.animations.length > 0;

        if (hasAnimations) {
            console.log(`Found ${this.model.animations.length} animations`);
            console.log(
                "Available animations:",
                this.model.animations.map((a) => a.name),
            );

            this.mixer = new THREE.AnimationMixer(this.model);

            try {
                console.log(
                    "Initializing animation controller with model ID:",
                    this.currentModelKey,
                );

                // Task #300 follow-on — server animation control plane.
                // Fetch the effective config for this model; when an OVERRIDE
                // exists (set via /admin/animations or legendctl), inject it
                // so frame ranges/fps/policy apply WITHOUT a rebuild. Strictly
                // fail-open: any error/timeout means bundled config, as before.
                let __animPolicy = null;
                try {
                    // Prefetched in loadModel (parallel with the download);
                    // fall back to a direct fetch when the prefetch is
                    // missing or belongs to another character.
                    const prefetch = this._animConfigPrefetch;
                    let eff = null;
                    if (
                        prefetch &&
                        prefetch.key === this.currentModelKey &&
                        prefetch.promise
                    ) {
                        eff = await prefetch.promise;
                    } else {
                        const opts =
                            typeof AbortSignal !== "undefined" &&
                            AbortSignal.timeout
                                ? { signal: AbortSignal.timeout(2500) }
                                : {};
                        const resp = await fetch(
                            `/api/animations/${encodeURIComponent(this.currentModelKey)}`,
                            opts,
                        );
                        eff = resp.ok ? await resp.json() : null;
                    }
                    {
                        if (eff && eff.source === "override") {
                            setRuntimeAnimationOverride(
                                this.currentModelKey,
                                eff,
                            );
                            __animPolicy = eff.policy || null;
                            console.log(
                                "[AnimOverride] server override active:",
                                JSON.stringify({
                                    ranges: eff.ranges,
                                    fps: eff.fps,
                                    policy: eff.policy,
                                }),
                            );
                        }
                    }
                } catch (_) {
                    /* fail-open: bundled config */
                }

                this.animationController = new AnimationController(
                    this.mixer,
                    this.model,
                );
                if (__animPolicy) {
                    this.animationController.setPlaybackPolicy(__animPolicy);
                }

                await this.animationController.setupAnimations(
                    this.model.animations,
                    this.currentModelKey,
                );

                window.Avatar = {
                    play: (name) => this.animationController?.play(name),
                    idle: () => this.animationController?.play("idle"),
                    listen: () => this.animationController?.play("listen"),
                    speak: () => this.animationController?.play("speak"),
                    speak2: () => this.animationController?.play("speak2"),
                    spawn: () => this.animationController?.play("spawn"),
                    stop: () => this.animationController?.stopAnimation(),

                    ttsStart: () => this.animationController?.onTTSStart(),
                    ttsAudioStart: () =>
                        this.animationController?.onAudioPlaybackStart(),
                    ttsEnd: () => this.animationController?.onTTSEnd(),

                    state: () => this.animationController?.animationState,
                    list: () => this.animationController?.getAvailable(),
                };

                console.log(
                    "[Avatar] Global animation API ready. Try Avatar.play('listen')",
                );

                if (
                    this.greetingService &&
                    this.greetingService.setAnimationController
                ) {
                    this.greetingService.setAnimationController(
                        this.animationController,
                    );
                    console.log(
                        "Connected Animation controller to greeting service",
                    );
                }

                // Task #331: removed a duplicate setupAnimations() call here —
                // animations are fully set up by the call above.
                console.log("Animation controller setup complete");
            } catch (error) {
                console.error("Failed to setup animation controller:", error);
                throw new Error(
                    `Animation controller setup failed: ${error.message}`,
                );
            }
        } else {
            console.warn(
                "No animations found in model — skipping animation setup",
            );
            this.mixer = new THREE.AnimationMixer(this.model);
            window.Avatar = {
                play: () => {},
                idle: () => {},
                listen: () => {},
                speak: () => {},
                speak2: () => {},
                spawn: () => {},
                stop: () => {},
                ttsStart: () => {},
                ttsAudioStart: () => {},
                ttsEnd: () => {},
                state: () => null,
                list: () => [],
            };
        }

        try {
            if (Object.keys(this.blendShapeControls).length > 0) {
                console.log("Initializing LipSync controller...");
                this.lipSyncController = new LipSyncController(
                    this.blendShapeControls,
                );
                // Connect LipSync controller to greeting service
                if (
                    this.greetingService &&
                    this.greetingService.setLipSyncController
                ) {
                    this.greetingService.setLipSyncController(
                        this.lipSyncController,
                    );
                    console.log(
                        "Connected LipSync controller to greeting service",
                    );
                }

                console.log("LipSync controller initialized successfully");

                // Make sure the GreetingService has a reference to the ModelViewer for model ID access
                if (
                    this.greetingService &&
                    this.greetingService.setModelViewer
                ) {
                    this.greetingService.setModelViewer(this);
                    console.log(
                        "Connected ModelViewer to greeting service for model ID access",
                    );
                }

                console.log("Initializing Blink controller...");
                this.blinkController = new BlinkController(
                    this.blendShapeControls,
                );
                console.log("Blink controller initialized successfully");

                console.log("Initializing Eye Tracking controller...");
                // Pass the real eye world-Y so the look-at math uses the
                // actual eye position (not the legacy 1.65 meter default
                // that lands at the feet in this centimeter-scaled scene).
                let eyeLevelForTracking = null;
                try {
                    const detected = detectEyeHeight(this.model);
                    if (Number.isFinite(detected) && detected > 0) {
                        eyeLevelForTracking = detected;
                    }
                } catch (_e) {
                    /* fall through to default */
                }
                this.eyeTrackingController = new EyeTrackingController(
                    this.blendShapeControls,
                    this.camera,
                    this.model,
                    eyeLevelForTracking !== null
                        ? { eyeLevel: eyeLevelForTracking }
                        : {},
                );
                console.log("Eye Tracking controller initialized successfully");

                console.log("Initializing Idle Smile controller...");
                try {
                    if (this.idleSmileController) {
                        try {
                            this.idleSmileController.dispose();
                        } catch (_) {}
                    }
                    this.idleSmileController = new IdleSmileController({
                        blendShapeControls: this.blendShapeControls,
                        characterId: this.currentModelKey,
                    });
                    console.log(
                        "Idle Smile controller initialized successfully",
                    );
                } catch (e) {
                    console.warn(
                        "[IdleSmile] Failed to initialize:",
                        e,
                    );
                }

                // Emotion controller deleted in Task #38 — facial emotion is
                // now driven exclusively by the A2F renderer (see
                // A2FEmotionRenderer.js), which is created inside the TTS
                // session via RealtimeLLMToElevenLabs.enableLipSync().

                // Initialize teeth material management system
                console.log("Initializing Teeth Material Manager...");
                this.teethMaterialManager = new TeethMaterialManager();
                this.teethMaterialManager.initialize(this.model);
                console.log("Teeth Material Manager initialized successfully");

                // Make teeth material controls available globally for easy scripting
                window.teethMaterialManager = this.teethMaterialManager;
                window.setTeethMetallicness = (value) =>
                    this.teethMaterialManager.setMetallicness(value);
                window.setTeethRoughness = (value) =>
                    this.teethMaterialManager.setRoughness(value);
                window.setTeethColor = (color) =>
                    this.teethMaterialManager.setColor(color);
                window.applyTeethPreset = (preset) =>
                    this.teethMaterialManager.applyPreset(preset);
                window.getTeethStatus = () =>
                    this.teethMaterialManager.getStatus();
                console.log(
                    "Teeth material controls made available globally for scripting",
                );

                // Initialize mouth material management system
                console.log("Initializing Mouth Material Manager...");
                this.mouthMaterialManager = new MouthMaterialManager();
                this.mouthMaterialManager.initialize(this.model);
                console.log("Mouth Material Manager initialized successfully");

                // These exporter-named ducts are not broad head/teeth
                // surfaces. Run the narrow black guarantee after both
                // managers, so it is the final material-color writer during
                // model setup without altering any other material property.
                const blackTeethDucts = enforceTeethDuctBlack(this.model);
                if (blackTeethDucts) {
                    console.log(
                        `Preserved authored black on ${blackTeethDucts} teeth duct material(s)`,
                    );
                }

                // Make mouth material controls available globally for easy scripting
                window.mouthMaterialManager = this.mouthMaterialManager;
                window.setMouthMetallicness = (value) =>
                    this.mouthMaterialManager.setMetallicness(value);
                window.setMouthRoughness = (value) =>
                    this.mouthMaterialManager.setRoughness(value);
                window.setMouthColor = (color) =>
                    this.mouthMaterialManager.setColor(color);
                window.applyMouthPreset = (preset) =>
                    this.mouthMaterialManager.applyPreset(preset);
                window.getMouthStatus = () =>
                    this.mouthMaterialManager.getStatus();
                console.log(
                    "Mouth material controls made available globally for scripting",
                );

            } else {
                console.warn(
                    "No blend shapes found - LipSync, Blink, and Eye Movement features will be disabled",
                );
            }
        } catch (error) {
            console.warn("Failed to initialize controllers:", error);
            console.log("Continuing without facial animation features");
        }

        // Add event listeners for chat interactions
        const chatInput = document.querySelector(".chat-input");
        if (chatInput) {
            chatInput.addEventListener("focus", () => {
                // Play listen animation for character face
                if (this.animationController) {
                    this.animationController.playListenAnimation();
                }

                // Trigger the cinematic camera movement to the left
                if (this.cameraAnimator) {
                    console.log(
                        "Triggering cinematic camera movement for chat focus",
                    );
                    this.cameraAnimator.chatCameraAnimation(true, {
                        leftShift: 35, // Amount to shift left
                        duration: 1500, // 1.5 seconds for smooth cinematic movement
                    });
                }
            });

            chatInput.addEventListener("blur", () => {
                // Return to idle animation for character face
                if (this.animationController) {
                    this.animationController.playIdleAnimation();
                }

                // Return camera to original position when chat is blurred
                if (this.cameraAnimator) {
                    console.log("Returning camera from cinematic position");
                    this.cameraAnimator.chatCameraAnimation(false);
                }
            });
        }

        // Initial-emotion-on-load + idle loop removed in Task #38: facial
        // emotion is now driven exclusively by the A2F renderer during
        // speech, with a slow decay back to neutral after silence.
    }

    async initializeBlendShapes() {
        return new Promise((resolve) => {
            this.blendShapeControls = {};
            const morphMeshes = new Set();
            let multiTargetCount = 0;
            this.model.traverse((child) => {
                if (child.isMesh && child.morphTargetDictionary) {
                    console.log(
                        `%c[BlendShapes] Mesh: "${child.name}" — ${Object.keys(child.morphTargetDictionary).length} morphs, materials: ${Array.isArray(child.material) ? child.material.length : 1}`,
                        "color:#00ccff;font-weight:bold",
                    );
                    morphMeshes.add(child);
                    Object.entries(child.morphTargetDictionary).forEach(
                        ([name, index]) => {
                            const entry = {
                                mesh: child,
                                index: index,
                                value: 0,
                            };
                            if (!this.blendShapeControls[name]) {
                                this.blendShapeControls[name] = entry;
                            } else if (
                                Array.isArray(this.blendShapeControls[name])
                            ) {
                                this.blendShapeControls[name].push(entry);
                            } else {
                                this.blendShapeControls[name] = [
                                    this.blendShapeControls[name],
                                    entry,
                                ];
                                multiTargetCount++;
                            }
                        },
                    );
                }
            });

            for (const mesh of morphMeshes) {
                if (typeof mesh.updateMorphTargets === "function") {
                    mesh.updateMorphTargets();
                    console.log(
                        `[BlendShapes] updateMorphTargets() called on: ${mesh.name}`,
                    );
                }
            }

            const totalKeys = Object.keys(this.blendShapeControls).length;
            const group1 = Object.keys(this.blendShapeControls).filter((k) =>
                k.startsWith("blendShape1."),
            ).length;
            const group2 = Object.keys(this.blendShapeControls).filter((k) =>
                k.startsWith("blendShape2."),
            ).length;
            const group3 = Object.keys(this.blendShapeControls).filter((k) =>
                k.startsWith("blendShape3."),
            ).length;
            const ungrouped = totalKeys - group1 - group2 - group3;
            const arrayKeys = Object.keys(this.blendShapeControls).filter((k) =>
                Array.isArray(this.blendShapeControls[k]),
            ).length;
            console.log(
                `%c[BlendShapes] SUMMARY: ${totalKeys} total controls — group1=${group1}, group2=${group2}, group3=${group3}, ungrouped=${ungrouped}, multiTarget=${multiTargetCount}, arrayKeys=${arrayKeys}`,
                "color:#00ff00;font-weight:bold;font-size:13px",
            );

            const eyeBlinkKeys = Object.keys(this.blendShapeControls).filter(
                (k) => {
                    const low = k.toLowerCase();
                    return low.includes("eye") || low.includes("blink");
                },
            );
            if (eyeBlinkKeys.length > 0) {
                const keyDetails = {};
                eyeBlinkKeys.forEach((k) => {
                    const ctl = this.blendShapeControls[k];
                    if (Array.isArray(ctl)) {
                        keyDetails[k] = ctl.map((c) => ({
                            mesh: c.mesh?.name || "?",
                            index: c.index,
                        }));
                    } else {
                        keyDetails[k] = [
                            { mesh: ctl?.mesh?.name || "?", index: ctl?.index },
                        ];
                    }
                });
                console.log(
                    `%c[BlendShapes] Eye/Blink keys (${eyeBlinkKeys.length}) — ALL targets per key:`,
                    "color:#ff69b4;font-weight:bold;font-size:13px",
                );
                // 1) Dump the keys list clearly
                console.log("[BlendShapes] Eye/Blink key list:", eyeBlinkKeys);

                // 2) Dump a compact table (hard to miss / hard to collapse)
                const rows = [];
                for (const k of eyeBlinkKeys) {
                    const ctl = this.blendShapeControls[k];
                    const arr = Array.isArray(ctl) ? ctl : [ctl];
                    for (let i = 0; i < arr.length; i++) {
                        const c = arr[i];
                        rows.push({
                            key: k,
                            targetIndex: i,
                            mesh: c?.mesh?.name,
                            morphIndex: c?.index,
                            influencesLen:
                                c?.mesh?.morphTargetInfluences?.length,
                            dictHasKey: c?.mesh?.morphTargetDictionary
                                ? c.mesh.morphTargetDictionary[k] !== undefined
                                : false,
                        });
                    }
                }
                console.table(rows);
                for (const [k, targets] of Object.entries(keyDetails)) {
                    console.log(
                        `  ${k}: [${targets.map((t) => t.mesh).join(", ")}]`,
                        targets,
                    );
                }
            } else {
                console.warn(
                    "[BlendShapes] No eye/blink keys found in blendShapeControls!",
                );
            }

            if (group2 > 0) {
                const sample = this.blendShapeControls["blendShape2.jawOpen"];
                const firstSample = Array.isArray(sample) ? sample[0] : sample;
                console.log(
                    `%c[BlendShapes] blendShape2.jawOpen →`,
                    "color:#ffaa00;font-weight:bold",
                    firstSample
                        ? {
                              meshName: firstSample.mesh?.name,
                              index: firstSample.index,
                              dictIndex:
                                  firstSample.mesh?.morphTargetDictionary?.[
                                      "blendShape2.jawOpen"
                                  ],
                              influencesLen:
                                  firstSample.mesh?.morphTargetInfluences
                                      ?.length,
                              isArray: Array.isArray(sample),
                              targetCount: Array.isArray(sample)
                                  ? sample.length
                                  : 1,
                          }
                        : "NOT FOUND",
                );
            }

            resolve();
        });
    }

    getLipSyncController() {
        return new Promise((resolve, reject) => {
            if (!this.lipSyncController) {
                console.warn("LipSync controller not available");
                resolve(null);
                return;
            }
            resolve(this.lipSyncController);
        });
    }

    /**
     * Get the camera animator
     *
     * @returns {CameraAnimator} The camera animator instance
     */
    getCameraAnimator() {
        return this.cameraAnimator;
    }

    /**
     * Task #403 — per-character head anchor for the floating museum
     * artwork. Recomputed on EVERY show (character swaps reposition
     * automatically). Returns null when no model is committed yet —
     * SimpleImagePanel then falls back to its legacy fixed position.
     */
    _computeArtworkAnchor() {
        try {
            if (!this.model) return null;
            const entry =
                (this.currentModelId && characterData[this.currentModelId]) ||
                null;
            return computeArtworkAnchor(this.model, entry);
        } catch (e) {
            console.warn("[ArtworkPanel] anchor computation failed", e);
            return null;
        }
    }

    /**
     * Task #403 — optional reveal glance: the legend briefly turns the
     * head toward the artwork (screen-left). Head-BONE only via the
     * speech-safe direction path; the face always belongs to A2F while
     * speaking (see .agents/memory/head-reaction-into-speech.md).
     * Best-effort polish: any failure is swallowed.
     */
    _glanceAtArtworkReveal() {
        try {
            if (!this.model) return;
            if (!this.lessonReactionController) {
                this.lessonReactionController = new LessonReactionController(
                    this,
                );
            }
            const c = this.lessonReactionController;
            if (typeof c.glanceToward === "function") c.glanceToward(-1);
        } catch (_e) {
            /* optional polish — never block the reveal */
        }
    }

    /**
     * Show image in the 3D scene behind the model
     * @param {string} imageUrl - URL of the image to display
     * @param {string} description - Description of the image
     * @returns {Promise<boolean>} - Whether the image was successfully displayed
     */
    async showImage3D(imageUrl, description, opts) {
        console.log(`Showing image in 3D scene: ${imageUrl}`);

        // Initialize the image panel if needed
        if (!this.imagePanel) {
            console.log("Initializing SimpleImagePanel");
            this.imagePanel = new SimpleImagePanel(this.scene, this.camera, {
                getPlacementAnchor: () => this._computeArtworkAnchor(),
            });
            // Expose globally for chat image clicks
            window.simpleImagePanel = this.imagePanel;
        }

        try {
            // Use our SimpleImagePanel implementation which is more reliable
            const success = await this.imagePanel.showImage(imageUrl, {
                caption: description || "",
                ...(opts || {}),
            });
            if (success) this._glanceAtArtworkReveal();
            return success;
        } catch (error) {
            console.error("Error showing image with SimpleImagePanel:", error);

            // Fall back to older implementation if available
            if (this.imagePanel3D) {
                try {
                    console.log("Falling back to legacy image panel");
                    return await this.imagePanel3D.showImage(
                        imageUrl,
                        description,
                    );
                } catch (fallbackError) {
                    console.error(
                        "Fallback image display also failed:",
                        fallbackError,
                    );
                    return false;
                }
            }
            return false;
        }
    }

    /**
     * Hide the 3D image panel
     */
    hideImage3D() {
        console.log("Hiding 3D image panels");

        // Hide the simple image panel if it exists and is visible
        if (this.imagePanel && this.imagePanel.isVisible) {
            this.imagePanel.hideImage();
        }

        // Also hide the legacy image panel if it exists
        if (this.imagePanel3D) {
            this.imagePanel3D.hidePanel();
        }
    }

    /* Legacy method removed to avoid duplication */

    /**
     * Play the bio camera sequence animation
     *
     * @param {Object} options - Animation options
     * @returns {Promise} Resolves when animation completes
     */
    playBioCameraSequence(options = {}) {
        if (!this.cameraAnimator) {
            console.warn("Camera animator not initialized");
            return Promise.resolve(); // Return an immediately resolved promise
        }

        console.log("Playing bio camera sequence animation");
        return this.cameraAnimator.bioCameraSequence(options);
    }

    setupSceneControls() {
        const gui = new GUI();

        const backgroundFolder = gui.addFolder("Background");
        backgroundFolder
            .addColor(this.backgroundColors, "top")
            .name("Top Color")
            .onChange(() => {
                this.setBackgroundColors(
                    this.backgroundColors.top,
                    this.backgroundColors.bottom,
                    0.5,
                );
            });
        backgroundFolder
            .addColor(this.backgroundColors, "bottom")
            .name("Bottom Color")
            .onChange(() => {
                this.setBackgroundColors(
                    this.backgroundColors.top,
                    this.backgroundColors.bottom,
                    0.5,
                );
            });
        backgroundFolder.open();

        gui.domElement.style.position = "absolute";
        gui.domElement.style.top = "60px";
        gui.domElement.style.right = "10px";
    }
    // Platform controls for adjusting position and scale
    setupPlatformControls() {
        // Get UI elements
        const platformControls = document.getElementById("platform-controls");
        const togglePlatformUI = document.getElementById("toggle-platform-ui");
        const platformPosX = document.getElementById("platform-pos-x");
        const platformPosY = document.getElementById("platform-pos-y");
        const platformPosZ = document.getElementById("platform-pos-z");
        const platformScale = document.getElementById("platform-scale");
        const resetPlatform = document.getElementById("reset-platform");

        // Value display elements
        const platformPosXValue = document.getElementById(
            "platform-pos-x-value",
        );
        const platformPosYValue = document.getElementById(
            "platform-pos-y-value",
        );
        const platformPosZValue = document.getElementById(
            "platform-pos-z-value",
        );
        const platformScaleValue = document.getElementById(
            "platform-scale-value",
        );

        // Default values
        const defaultValues = {
            posX: 0,
            posY: 30, // Lowered platform Y position
            posZ: -27, // Updated platform Z position
            scale: 2.5, // Updated platform scale
        };

        // Add platform control panel to the menu button dropdown
        const menuItems = document.querySelector(
            "#menuButton + .dropdown-menu",
        );
        if (menuItems) {
            const platformMenuItem = document.createElement("a");
            platformMenuItem.className = "dropdown-item";
            platformMenuItem.href = "#";
            platformMenuItem.innerHTML =
                '<i class="fas fa-cube"></i> Platform Controls';
            platformMenuItem.addEventListener("click", (e) => {
                e.preventDefault();
                platformControls.classList.remove("d-none");
            });
            menuItems.appendChild(platformMenuItem);
        }

        // Set initial values on sliders
        if (platformPosX) platformPosX.value = defaultValues.posX;
        if (platformPosY) platformPosY.value = defaultValues.posY;
        if (platformPosZ) platformPosZ.value = defaultValues.posZ;
        if (platformScale) platformScale.value = defaultValues.scale;

        // Set initial values in display
        if (platformPosXValue)
            platformPosXValue.textContent = defaultValues.posX;
        if (platformPosYValue)
            platformPosYValue.textContent = defaultValues.posY;
        if (platformPosZValue)
            platformPosZValue.textContent = defaultValues.posZ;
        if (platformScaleValue)
            platformScaleValue.textContent = defaultValues.scale;

        // Update platform when controls change
        if (platformPosX) {
            platformPosX.addEventListener("input", (e) => {
                const value = parseFloat(e.target.value);
                if (this.platform) this.platform.position.x = value;
                if (platformPosXValue) platformPosXValue.textContent = value;
            });
        }

        if (platformPosY) {
            platformPosY.addEventListener("input", (e) => {
                const value = parseFloat(e.target.value);
                if (this.platform) this.platform.position.y = value;
                if (platformPosYValue) platformPosYValue.textContent = value;
            });
        }

        if (platformPosZ) {
            platformPosZ.addEventListener("input", (e) => {
                const value = parseFloat(e.target.value);
                if (this.platform) this.platform.position.z = value;
                if (platformPosZValue) platformPosZValue.textContent = value;
            });
        }

        if (platformScale) {
            platformScale.addEventListener("input", (e) => {
                const value = parseFloat(e.target.value);
                if (this.platform) {
                    // Handle both mesh and group objects
                    if (this.platform.isGroup) {
                        this.platform.scale.set(value, value, value);
                    } else {
                        this.platform.scale.set(value, value, value);
                    }
                }
                if (platformScaleValue) platformScaleValue.textContent = value;
            });
        }

        // Reset platform to default values
        if (resetPlatform) {
            resetPlatform.addEventListener("click", () => {
                if (platformPosX) platformPosX.value = defaultValues.posX;
                if (platformPosY) platformPosY.value = defaultValues.posY;
                if (platformPosZ) platformPosZ.value = defaultValues.posZ;
                if (platformScale) platformScale.value = defaultValues.scale;

                if (platformPosXValue)
                    platformPosXValue.textContent = defaultValues.posX;
                if (platformPosYValue)
                    platformPosYValue.textContent = defaultValues.posY;
                if (platformPosZValue)
                    platformPosZValue.textContent = defaultValues.posZ;
                if (platformScaleValue)
                    platformScaleValue.textContent = defaultValues.scale;

                if (this.platform) {
                    this.platform.position.set(
                        defaultValues.posX,
                        defaultValues.posY,
                        defaultValues.posZ,
                    );
                    this.platform.scale.set(
                        defaultValues.scale,
                        defaultValues.scale,
                        defaultValues.scale,
                    );
                }
            });
        }

        // Hide platform controls
        if (togglePlatformUI) {
            togglePlatformUI.addEventListener("click", () => {
                platformControls.classList.add("d-none");
            });
        }

        // Initialize the Platform Height Controller for model-specific positioning
        try {
            this.platformHeightController = new PlatformHeightController(this);
            console.log(
                "Platform height controller initialized for model-specific positioning",
            );
        } catch (error) {
            console.warn(
                "Failed to initialize platform height controller:",
                error,
            );
        }

        // Initialize the Platform Test Interface for comprehensive testing
        try {
            this.platformTestInterface = new PlatformTestInterface(this);
            console.log(
                "Platform test interface initialized for model positioning validation",
            );
        } catch (error) {
            console.warn(
                "Failed to initialize platform test interface:",
                error,
            );
        }
    }
}
